import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import '../workbench/crossTextEdition/CrossTextEdition.css'
import { ReaderAnalyticalWorkPackage } from './ReaderAnalyticalWorkPackage'
import {
  buildLedgerSafeTraditionReaderModel,
  type LedgerSafeTraditionPeerUnit,
  type LedgerSafeTraditionUnit,
} from '../../lib/ledgerSafeTraditionReader'
import type { Witness } from '../../store/booksStore'
import type { AnchoredRow } from '../../types/anchoredRowTypes'
import type { WitnessUnitTextRecord } from '../../lib/ledgerTextPreprocessing'
import {
  characterOccurrenceRefersTo,
  getCharacterOccurrenceReferentIds,
  isJointCharacterOccurrence,
  type CharacterCoverageSummary,
} from '../../lib/characterLayerHelpers'
import type {
  ChapterCharacterLayer,
  CharacterLayerNote,
  CharacterOccurrence,
} from '../../types/characterLayerTypes'
import type {
  CharacterIndexEntry,
  CoreferenceChainV2FigureKind as CharacterIndexFigureKind,
  CharacterIndexSourceAnchor,
} from '@variorum/character-index'
import {
  buildLectioRoster,
  buildTextualBeingCards,
  lectioEvidenceRefersTo,
  type LectioEvidenceKind,
  type LectioEvidenceRecord,
  type LectioProjection,
} from '../../lib/readerCharacterIndexLectio'
import {
  LECTIO_FOCALIZATION_CLASSES,
  LECTIO_MANUAL_CHARACTERIZATION_ACTION_DETAILS,
  LECTIO_MANUAL_CHARACTERIZATION_DIRECT_FORMS,
  LECTIO_MANUAL_CHARACTERIZATION_INDIRECT_CHANNELS,
  LECTIO_MANUAL_CHARACTERIZATION_MODIFIERS,
  LECTIO_MANUAL_CHARACTERIZATION_NARRATORIAL_FORMS,
  LECTIO_MANUAL_CHARACTERIZATION_REACTION_DETAILS,
  LECTIO_SPEAKER_ADDRESS_MODES,
  LECTIO_SPEAKER_DISCOURSE_FORMS,
  LECTIO_SPEAKER_DISCOURSE_FUNCTIONS,
  LECTIO_SPEAKER_ORIENTATIONS,
  LECTIO_SPEAKER_STANCES,
  LECTIO_VOICES,
  beginLectioAdjudicationSession,
  getLectioLayerStanding,
  getLectioOccurrenceStanding,
  getLectioSpeakerAnalysisValidationError,
  readLectioManualCharacterization,
  readLectioSpeakerAnalysis,
  type LectioAdjudicationCommand,
  type LectioAdjudicationResizeCommand,
  type LectioAdjudicationSession,
  type LectioLayerClassification,
  type LectioManualCharacterization,
  type LectioManualCharacterizationChannel,
  type LectioManualCharacterizationDirectSource,
  type LectioManualCharacterizationForm,
  type LectioManualCharacterizationIndirectChannel,
  type LectioManualCharacterizationIndirectDetail,
  type LectioManualCharacterizationModifier,
  type LectioSpeakerAddressMode,
  type LectioSpeakerAnalysis,
  type LectioSpeakerDiscourseForm,
  type LectioSpeakerDiscourseFunction,
  type LectioSpeakerOrientation,
  type LectioSpeakerRetrospectiveStatus,
  type LectioSpeakerRetrospectiveTarget,
  type LectioSpeakerStance,
  type LectioVoice,
} from '../../lib/readerCharacterIndexAdjudication'
import type {
  TextSection,
  TextSectionBoundary,
  TextSectionType,
  TextStructureCommand,
  TextStructureLayer,
} from '../../types/readerTextStructureTypes'
import {
  ANNOTATION_RESEARCH_NOTE_STATES,
  createAnnotationResearchNote,
  getAnnotationResearchNotesForTarget,
  renderAnnotationResearchNoteMarkdown,
  updateAnnotationResearchNote,
} from '../../lib/annotationResearchNotes'
import type {
  AnnotationResearchNote,
  AnnotationResearchNotePassageSnapshot,
  AnnotationResearchNoteState,
  AnnotationResearchNoteTarget,
} from '../../types/annotationResearchNoteTypes'
import type {
  AnalyticalCheckpointScope,
  AnalyticalTaskSet,
  AnalyticalTaskState,
} from '../../types/analyticalTaskTypes'
import type { CharacterIndexProgressEvidenceKind } from '../../lib/characterIndexProgress'

export interface LedgerSafeTraditionReaderViewProps {
  rows: AnchoredRow[]
  witnesses: Witness[]
  sourceWitnessId: string
  textLookupByWitnessId?: Map<string, Map<string, WitnessUnitTextRecord>>
  contextLabel?: string
  characterIndexEnabled?: boolean
  characterIndexEntries?: readonly CharacterIndexEntry[]
  characterOccurrences?: readonly CharacterOccurrence[]
  characterLayerNotes?: readonly CharacterLayerNote[]
  characterLayer?: ChapterCharacterLayer
  characterChipMode?: CharacterChipMode
  characterCoverageById?: Map<string, CharacterCoverageSummary>
  annotationResearchNotes?: readonly AnnotationResearchNote[]
  selectedCharacterId?: string
  characterIndexControlPanel?: React.ReactNode
  initialResumeRowId?: string
  initialResumeEvidenceId?: string
  initialResumeEvidenceKind?: CharacterIndexProgressEvidenceKind
  onCreateCharacter?: (displayName: string, kind?: CharacterIndexFigureKind) => string | Promise<string>
  onRenameCharacter?: (
    characterId: string,
    displayName: string,
    kind?: CharacterIndexFigureKind,
  ) => void | Promise<void>
  onDeleteCharacter?: (characterId: string) => void | Promise<void>
  onAdjudicateCharacterEvidence?: (command: LectioAdjudicationCommand) => void | Promise<void>
  textStructureLayer?: TextStructureLayer
  onEditTextStructure?: (command: TextStructureCommand) => void | Promise<void>
  onSaveAnnotationResearchNote?: (note: AnnotationResearchNote) => void | Promise<void>
  onArchiveAnnotationResearchNote?: (noteId: string) => void | Promise<void>
  onArchiveAnnotationResearchNotesForTarget?: (
    target: AnnotationResearchNoteTarget,
  ) => void | Promise<void>
  analyticalTaskSet?: AnalyticalTaskSet
  onCreateAnalyticalCheckpoint?: (request: {
    title?: string
    workstreamIds: string[]
    scope: AnalyticalCheckpointScope
  }) => void | Promise<void>
  onSetAnalyticalWorkstreamState?: (
    workstreamId: string,
    state: AnalyticalTaskState,
  ) => void | Promise<void>
  onSetAnalyticalCheckpointState?: (
    checkpointId: string,
    state: AnalyticalTaskState,
  ) => void | Promise<void>
  onSelectCharacter?: (characterId: string) => void
  onClose: () => void
}

type ReaderSection = 'read' | 'character-index' | 'compare'
type CharacterMentionType = 'name' | 'coref'
type CharacterChipMode = 'derived' | 'ordinal'
type ReaderLayerKind = 'speaker' | 'focalization' | 'characterization'
type LectioClassifiedLayerKind = ReaderLayerKind
type LectioEvidenceSubmenu = LectioClassifiedLayerKind | 'claim' | 'share'
type LectioSpeakerBranch = 'address' | 'addressees' | 'functions' | 'forms' | 'orientations' | 'stances' | 'retrospective'
type LectioCharacterizationBranch = 'direct' | 'indirect' | 'modifiers'
type LectioCharacterizationDetail = (
  LectioManualCharacterizationDirectSource
  | 'action'
  | 'reaction'
  | 'other-character-picker'
  | 'reaction-character-picker'
)
type LectioIsolationKind = 'chain' | ReaderLayerKind
type LectioRapidTool = 'coref' | ReaderLayerKind | 'create-character' | 'section'

const CHARACTER_FIGURE_KIND_OPTIONS: Array<{ value: CharacterIndexFigureKind; label: string }> = [
  { value: 'person', label: 'Person' },
  { value: 'animal', label: 'Animal' },
  { value: 'being', label: 'Being' },
  { value: 'group', label: 'Group' },
]
type LectioRapidSectionPhase = 'opening' | 'classify' | 'closing' | 'editing'
type LectioEvidenceStanding = 'admitted' | 'received'
type VariationTier = 't1' | 't2' | 't3'
type UnitVariationTier = VariationTier | 'silent-run'

const createManualCharacterizationDraft = (
  value?: LectioManualCharacterization | null,
): LectioManualCharacterizationDraft => ({
  mode: value?.mode || null,
  directSource: value?.directSource,
  channel: value?.channel,
  sourceCharacterId: value?.sourceCharacterId,
  forms: value?.directSource === 'narratorial'
    ? [...(value?.forms || [])]
    : [...(value?.forms || [])].filter(form => form !== 'reputation'),
  indirectChannel: value?.indirectChannel,
  indirectDetail: value?.indirectDetail,
  relatedCharacterId: value?.relatedCharacterId,
  modifiers: [...(value?.modifiers || [])],
})

const getManualCharacterizationValidationError = (
  draft: LectioManualCharacterizationDraft,
): string | null => {
  if (!draft.mode) {
    return 'Choose direct or indirect characterization'
  }
  if (draft.mode === 'direct') {
    if (!draft.directSource) {
      return 'Choose who supplies the direct characterization'
    }
    if (draft.directSource !== 'narratorial' && !draft.channel) {
      return 'Choose spoken or interior discourse'
    }
    if (draft.directSource === 'other' && !draft.sourceCharacterId) {
      return 'Choose the other character'
    }
    if (draft.forms.length === 0) {
      return 'Choose at least one direct characterization form'
    }
    if (draft.directSource !== 'narratorial' && draft.forms.includes('reputation')) {
      return 'Reputation belongs under narratorial characterization'
    }
    return null
  }
  if (!draft.indirectChannel) {
    return 'Choose how the characterization is conveyed'
  }
  if (
    draft.indirectChannel === 'action_conduct'
    && !LECTIO_MANUAL_CHARACTERIZATION_ACTION_DETAILS.some(detail => detail.id === draft.indirectDetail)
  ) {
    return 'Choose the kind of action or conduct'
  }
  if (draft.indirectChannel === 'others_reaction') {
    if (!draft.relatedCharacterId) {
      return 'Choose the reacting character'
    }
    if (!LECTIO_MANUAL_CHARACTERIZATION_REACTION_DETAILS.some(detail => detail.id === draft.indirectDetail)) {
      return 'Choose the kind of reaction'
    }
  }
  return null
}

const finalizeManualCharacterization = (
  draft: LectioManualCharacterizationDraft,
): LectioManualCharacterization | null => draft.mode ? {
  mode: draft.mode,
  ...(draft.mode === 'direct'
    ? {
        directSource: draft.directSource,
        channel: draft.channel,
        sourceCharacterId: draft.sourceCharacterId,
        forms: draft.forms,
      }
    : {
        indirectChannel: draft.indirectChannel,
        indirectDetail: draft.indirectDetail,
        relatedCharacterId: draft.relatedCharacterId,
      }),
  modifiers: draft.modifiers,
} : null

const createSpeakerAnalysisDraft = (
  value?: LectioSpeakerAnalysis,
): LectioSpeakerAnalysis => {
  const normalized = value || readLectioSpeakerAnalysis(undefined)
  return {
    ...normalized,
    addresseeCharacterIds: [...normalized.addresseeCharacterIds],
    functions: [...normalized.functions],
    forms: [...normalized.forms],
    orientations: [...normalized.orientations],
    stances: [...normalized.stances],
    retrospectiveTargets: normalized.retrospectiveTargets.map(target => ({ ...target })),
  }
}

const getSpeakerChoiceLabel = (
  choices: readonly { id: string; label: string }[],
  id: string,
): string => choices.find(choice => choice.id === id)?.label || formatLectioMenuChoice(id)

const summarizeSpeakerChoices = (
  choices: readonly { id: string; label: string }[],
  values: readonly string[],
): string => values.length === 0
  ? 'none'
  : values.map(value => getSpeakerChoiceLabel(choices, value)).join(', ')

const LECTIO_ANNOTATION_GRAMMARS = [
  {
    id: 'synthesis',
    label: 'The Synthesis',
    note: 'vermilion half-brackets + overrule = focalization; sienna low brackets + 12% wash = characterization',
  },
  {
    id: 'dry-ink',
    label: 'Dry Ink',
    note: 'as the Synthesis; the characterization wash becomes a 2.5px sienna underline',
  },
  {
    id: 'full-rubric',
    label: 'Full Rubric',
    note: 'no span brackets: focalization words are vermilion; characterization words are sepia',
  },
  {
    id: 'black-letter',
    label: 'Black Letter',
    note: 'black half-brackets; only each span’s first word takes its type ink',
  },
  {
    id: 'synthesis-at-work',
    label: 'The Synthesis at Work',
    note: 'Synthesis + standing: faded/broken received, full admitted, obelus-only sub judice; other evidence 34%',
  },
  {
    id: 'running-rubric',
    label: 'The Running Rubric',
    note: 'black prose; upper vermilion = focalization, lower sienna = characterization, both rails = overlap; solid admitted, broken received, obelus sub judice',
  },
] as const

type LectioAnnotationGrammar = typeof LECTIO_ANNOTATION_GRAMMARS[number]['id']
const DEFAULT_LECTIO_ANNOTATION_GRAMMAR: LectioAnnotationGrammar = 'synthesis'

interface ReaderCharacterAnchor {
  evidenceId?: string
  entry: CharacterIndexEntry
  anchor: CharacterIndexSourceAnchor
  role: string | null
  mentionType: CharacterMentionType
  chip: string
  color: string
  standing?: LectioEvidenceStanding
  referentCharacterIds: string[]
  referents: ReaderCharacterReferent[]
  sharedReference: boolean
}

interface ReaderCharacterReferent {
  id: string
  label: string
  chip: string
  color: string
}

interface CharacterDecoration {
  chip: string
  color: string
}

interface CharacterRailOccurrence {
  id: string
  anchor: ReaderCharacterAnchor
  rowLabel: string
  windowLabel: string
}

interface ReaderLayerAnnotation {
  id: string
  note: CharacterLayerNote
  kind: ReaderLayerKind
  characterId: string
  chip: string
  localStart: number
  localEnd: number
  rowId: string
  rowLabel: string
  label: string
  detail: string
  color: string
  glyph: string
  ruleStyle: string
  sourceVoice: string
  subtypeLabel: string
  classifications: string[]
  standing: LectioEvidenceStanding
  speechTurnId?: string
  speechLevel?: number
  speechFragmentIndex?: number
  speakerAnalysis?: LectioSpeakerAnalysis
}

interface ReaderUnitLayerSummaryCharacter {
  characterId: string
  label: string
  chip: string
  color: string
  standing: LectioEvidenceStanding
  spanCount: number
  firstNoteId: string
}

interface ReaderUnitLayerSummary {
  kind: ReaderLayerKind
  overlayVisible: boolean
  characters: ReaderUnitLayerSummaryCharacter[]
}

interface ReaderSpeakerTurnRail {
  turnId: string
  sourceTurnIds: string[]
  characterId: string
  label: string
  color: string
  standing: LectioEvidenceStanding
  level: number
  startRowId: string
  startRowLabel: string
  startOffset: number
  endRowId: string
  endRowLabel: string
  endOffset: number
  firstNoteId: string
  labelPlacement: 'center' | 'entry'
  overlayVisible: boolean
}

interface ReaderSpeakerTurnRailLayout extends ReaderSpeakerTurnRail {
  top: number
  height: number
  labelOffset: number
}

interface ReaderUnitStructureItem {
  section: TextSection
  sectionType: TextSectionType
}

interface ReaderUnitStructureSummary {
  starts: ReaderUnitStructureItem[]
  ends: ReaderUnitStructureItem[]
}

const EMPTY_READER_UNIT_STRUCTURE_ITEMS: ReaderUnitStructureItem[] = []
const EMPTY_READER_CHARACTER_ANCHORS: ReaderCharacterAnchor[] = []
const EMPTY_READER_LAYER_ANNOTATIONS: ReaderLayerAnnotation[] = []
const EMPTY_READER_UNIT_LAYER_SUMMARIES: ReaderUnitLayerSummary[] = []
const EMPTY_READER_UNIT_STRUCTURE_SUMMARY: ReaderUnitStructureSummary = {
  starts: EMPTY_READER_UNIT_STRUCTURE_ITEMS,
  ends: EMPTY_READER_UNIT_STRUCTURE_ITEMS,
}

interface LectioAdjudicationEntryOptions {
  firstReading?: boolean
  alignFirstReading?: boolean
}

type ReaderTextAnnotation =
  | {
    kind: 'character'
    id: string
    entry: CharacterIndexEntry
    anchor: CharacterIndexSourceAnchor
    role: string | null
    mentionType: CharacterMentionType
    chip: string
    color: string
    standing: LectioEvidenceStanding
    referentCharacterIds: string[]
    referents: ReaderCharacterReferent[]
    sharedReference: boolean
    localStart: number
    localEnd: number
  }
  | {
    kind: 'layer'
    id: string
    layer: ReaderLayerAnnotation
    layerKind: ReaderLayerKind
    characterId: string
    color: string
    standing: LectioEvidenceStanding
    localStart: number
    localEnd: number
  }

interface ReaderTextAnnotationSegment {
  start: number
  end: number
  covering: ReaderTextAnnotation[]
  opening: ReaderTextAnnotation[]
}

interface ReaderTextAnnotationLayout {
  segments: ReaderTextAnnotationSegment[]
  droppedCount: number
}

interface LectioBoundaryDrag {
  readingId: string
  sourceKind: 'occurrence' | 'layer'
  edge: 'start' | 'end'
  originalStart: number
  originalEnd: number
  previewStart: number
  previewEnd: number
  pointerX: number
  pointerY: number
}

interface LectioEvidenceMenuTarget {
  sourceKind: 'occurrence' | 'layer' | 'draft'
  evidenceId: string
  characterId: string
  referentCharacterIds?: readonly string[]
  rowId: string
  rowLabel: string
  start: number
  end: number
  text: string
  standing?: LectioEvidenceStanding
  mentionType?: CharacterMentionType
  layerKind?: ReaderLayerKind
  classification?: string
  classifications?: readonly string[]
  voice?: LectioVoice
  manualCharacterization?: LectioManualCharacterization | null
  speakerAnalysis?: LectioSpeakerAnalysis
  logicalId?: string
}

interface LectioResearchNoteEditorState {
  chapterId: string
  bookId?: string
  target: AnnotationResearchNoteTarget
  passageSnapshot: AnnotationResearchNotePassageSnapshot
  targetLabel: string
  anchorLeft: number
  anchorBottom: number
}

interface LectioPendingSpeakerFragment {
  evidenceId: string
  rowId: string
  start: number
  end: number
  sourceText: string
}

interface LectioPendingSpeakerTurn {
  turnId: string
  speechLevel: number
  fragments: LectioPendingSpeakerFragment[]
}

interface LectioManualCharacterizationDraft {
  mode: LectioManualCharacterization['mode'] | null
  directSource?: LectioManualCharacterizationDirectSource
  channel?: LectioManualCharacterizationChannel
  sourceCharacterId?: string
  forms: LectioManualCharacterizationForm[]
  indirectChannel?: LectioManualCharacterizationIndirectChannel
  indirectDetail?: LectioManualCharacterizationIndirectDetail
  relatedCharacterId?: string
  modifiers: LectioManualCharacterizationModifier[]
}

interface LectioEvidenceMenuState {
  target: LectioEvidenceMenuTarget
  anchorLeft: number
  anchorTop: number
  anchorBottom: number
  submenu: LectioEvidenceSubmenu | null
  classification: LectioLayerClassification
  manualCharacterization: LectioManualCharacterizationDraft
  characterizationBranch: LectioCharacterizationBranch | null
  characterizationDetail: LectioCharacterizationDetail | null
  speakerAnalysis: LectioSpeakerAnalysis
  speakerBranch: LectioSpeakerBranch | null
  pendingSpeakerTurn: LectioPendingSpeakerTurn | null
  voice: LectioVoice
}

interface LectioRapidSpanAnchor {
  rowId: string
  start: number
  end: number
}

interface LectioRapidMarkingContext {
  tool: LectioRapidTool
  characterLabel: string
  pendingSpan: LectioRapidSpanAnchor | null
  sectionTypeLabel?: string
  pendingSectionStart?: TextSectionBoundary | null
  sectionBoundaryAction?: 'start' | 'end' | null
}

interface LectioLectionSpan {
  type: 'span'
  id: string
  rowId: string
  start: number
  end: number
  text: string
  evidenceIds: string[]
  kinds: LectioEvidenceKind[]
}

interface LectioIntraFold {
  type: 'fold'
  scope: 'intra'
  id: string
  rowIds: string[]
  start: number
  end: number
  text: string
}

interface LectioInterFold {
  type: 'fold'
  scope: 'inter'
  id: string
  rowIds: string[]
  text: string
}

interface LectioLectionRow {
  type: 'row'
  rowId: string
  items: Array<LectioLectionSpan | LectioIntraFold>
}

type LectioLectionBlock = LectioLectionRow | LectioInterFold


interface LectioMarginalAnchor {
  characterId: string
  scrollTop: number
  viewportY: number
}

interface LectioInlineCandidate {
  rowId: string
  text: string
  pasteOpen: boolean
  pastedResponse: string
  status: 'idle' | 'copied' | 'staged' | 'error'
  feedback: string
}

const CHARACTER_COLOR_PALETTE = [
  'var(--cte-char-1)',
  'var(--cte-char-2)',
  'var(--cte-char-3)',
  'var(--cte-char-4)',
  'var(--cte-char-5)',
  'var(--cte-char-6)',
  'var(--cte-char-7)',
  'var(--cte-char-8)',
  'var(--cte-char-9)',
  'var(--cte-char-10)',
  'var(--cte-char-11)',
  'var(--cte-char-12)',
]

const FOCALIZATION_CLASS_STYLE: Record<string, { glyph: string; ruleStyle: string; label: string }> = {
  perception: { glyph: '⊙', ruleStyle: 'solid', label: 'perc' },
  cognition: { glyph: '◔', ruleStyle: 'dashed', label: 'cogn' },
  affect: { glyph: '❤', ruleStyle: 'double', label: 'affc' },
  emotion: { glyph: '❤', ruleStyle: 'double', label: 'emot' },
  volition: { glyph: '➤', ruleStyle: 'double', label: 'voli' },
  intention: { glyph: '➤', ruleStyle: 'double', label: 'intent' },
  interior: { glyph: '◑', ruleStyle: 'dashed', label: 'intr' },
  interior_discourse: { glyph: '◑', ruleStyle: 'dashed', label: 'intr' },
  evidential: { glyph: '❖', ruleStyle: 'dotted', label: 'evid' },
  narratorial_evidential_distance: { glyph: '❖', ruleStyle: 'dotted', label: 'evid' },
  knowledge_asymmetry: { glyph: '∅', ruleStyle: 'dotted', label: 'asym' },
}

const CHARACTERIZATION_DEVICE_STYLE: Record<string, { glyph: string; ruleStyle: string; label: string }> = {
  /* Direct predication */
  naat: { glyph: '◆', ruleStyle: 'solid', label: 'naʿt' },
  khabar: { glyph: '▣', ruleStyle: 'double', label: 'khabar' },
  kana_pred: { glyph: '▣', ruleStyle: 'double', label: 'kāna' },
  kana_lahu: { glyph: '▣', ruleStyle: 'double', label: 'kāna·lahu' },
  /* Portrait and comparison */
  body_part: { glyph: '◆', ruleStyle: 'solid', label: 'portrait' },
  tashbih: { glyph: '◆', ruleStyle: 'solid', label: 'tashbīh' },
  elative: { glyph: '◆', ruleStyle: 'solid', label: 'elative' },
  tamyiz: { glyph: '◆', ruleStyle: 'solid', label: 'tamyīz' },
  /* State, manner, and motive */
  hal: { glyph: '↺', ruleStyle: 'dotted', label: 'ḥāl' },
  maf3ul_mutlaq: { glyph: '↺', ruleStyle: 'dotted', label: 'mafʿūl' },
  maf3ul_liajlih: { glyph: '↺', ruleStyle: 'dotted', label: 'li-ajl' },
  manner_pp: { glyph: '↺', ruleStyle: 'dotted', label: 'manner' },
  /* Explicit evaluation */
  madh_dhamm: { glyph: '❝', ruleStyle: 'dashed', label: 'madh/dhamm' },
  ta3ajjub: { glyph: '❝', ruleStyle: 'dashed', label: 'taʿajjub' },
  tasghir: { glyph: '❝', ruleStyle: 'dashed', label: 'taṣghīr' },
  dua: { glyph: '❝', ruleStyle: 'dashed', label: 'duʿāʾ' },
  /* Descriptive constructions */
  idafa_wasfiyya: { glyph: '◆', ruleStyle: 'solid', label: 'iḍāfa' },
  negated_trait: { glyph: '◆', ruleStyle: 'dashed', label: 'negated' },
  vocative_eval: { glyph: '❝', ruleStyle: 'dashed', label: 'nidāʾ' },
  urf_reputation: { glyph: '◆', ruleStyle: 'dashed', label: 'ʿurf' },
  naming: { glyph: '◆', ruleStyle: 'dashed', label: 'naming' },
  /* Action */
  habitual_kana: { glyph: '▣', ruleStyle: 'double', label: 'kāna·h' },
  reaction: { glyph: '↺', ruleStyle: 'dotted', label: 'reaction' },
}

const ARABIC_DIACRITICS_PATTERN = /[\u064B-\u065F\u0670]/g

const hasArabicText = (value: string): boolean => /[\u0600-\u06FF]/.test(value)

const isFiniteOffset = (value: unknown): value is number => (
  typeof value === 'number' && Number.isFinite(value)
)

const stableHash = (value: string): number => {
  let hash = 2166136261
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index)
    hash = Math.imul(hash, 16777619)
  }
  return hash >>> 0
}

const getCharacterColor = (characterId: string): string => (
  CHARACTER_COLOR_PALETTE[stableHash(characterId) % CHARACTER_COLOR_PALETTE.length] || CHARACTER_COLOR_PALETTE[0]
)

const asMetadataRecord = (value: unknown): Record<string, any> => (
  value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, any> : {}
)

const isDefined = <T,>(value: T | null | undefined): value is T => value != null

const getReaderLayerSubtype = (
  note: CharacterLayerNote,
  kind: ReaderLayerKind,
): { glyph: string; ruleStyle: string; label: string } => {
  const metadata = note.metadata || {}
  if (kind === 'speaker') {
    const level = typeof metadata.speech_level === 'number' ? metadata.speech_level : 0
    return { glyph: '❯', ruleStyle: 'solid', label: level > 0 ? `speaker · level ${level}` : 'speaker' }
  }
  if (kind === 'focalization') {
    const classId = typeof metadata.class === 'string' ? metadata.class.trim() : ''
    return FOCALIZATION_CLASS_STYLE[classId] || {
      glyph: '⊙',
      ruleStyle: 'solid',
      label: classId || 'foc',
    }
  }
  const manual = readLectioManualCharacterization(metadata.manual_characterization)
  if (manual) {
    return manual.mode === 'direct'
      ? { glyph: '◆', ruleStyle: 'solid', label: 'direct' }
      : { glyph: '↺', ruleStyle: 'dotted', label: 'indirect' }
  }
  const devices = Array.isArray(metadata.devices)
    ? metadata.devices.filter((device): device is string => typeof device === 'string' && device.trim().length > 0)
    : []
  const primaryDevice = devices[0] || ''
  return CHARACTERIZATION_DEVICE_STYLE[primaryDevice] || {
    glyph: '◆',
    ruleStyle: 'solid',
    label: primaryDevice || 'char',
  }
}

const getReaderLayerClassifications = (
  note: CharacterLayerNote,
  kind: ReaderLayerKind,
): string[] => {
  if (kind === 'speaker') {
    return []
  }
  if (kind === 'focalization') {
    const markerClass = note.metadata?.class
    return typeof markerClass === 'string' && markerClass.trim() ? [markerClass.trim()] : []
  }
  const manual = readLectioManualCharacterization(note.metadata?.manual_characterization)
  if (manual) {
    return manual.mode === 'direct'
      ? [...(manual.forms || [])]
      : [manual.indirectChannel, manual.indirectDetail].filter(isDefined)
  }
  const devices = note.metadata?.devices
  return Array.isArray(devices)
    ? devices.filter((device): device is string => (
        typeof device === 'string' && device.trim().length > 0
      ))
    : []
}

const getReaderLayerSourceVoice = (note: CharacterLayerNote): string => {
  if (note.metadata?.character_index_layer === 'speaker') {
    return 'spk'
  }
  const source = asMetadataRecord(note.metadata?.source)
  if (source.voice === 'figure') {
    return source.channel === 'interior' ? '◔' : '❝'
  }
  if (source.voice === 'communal') {
    return 'C'
  }
  if (source.voice === 'narrator') {
    const level = typeof source.level === 'number' && Number.isFinite(source.level) ? source.level : 0
    return `N${level}`
  }
  return ''
}

const getReaderLayerVoice = (note: CharacterLayerNote): LectioVoice => {
  const voice = asMetadataRecord(note.metadata?.source).voice
  return LECTIO_VOICES.includes(voice as LectioVoice) ? voice as LectioVoice : 'figure'
}

const normalizeCharacterMentionType = (value: unknown): CharacterMentionType | null => (
  value === 'name' || value === 'coref' ? value : null
)

const formatLectioMenuChoice = (value: string): string => (
  value.length > 0 ? `${value.charAt(0).toUpperCase()}${value.slice(1)}` : value
)

const slugifySectionType = (value: string): string => (
  value
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, '-')
    .replace(/^-+|-+$/g, '')
)

const getSourceAnchorMentionType = (anchor: CharacterIndexSourceAnchor): CharacterMentionType | null => (
  normalizeCharacterMentionType(anchor.mentionType)
)

const deriveCharacterChipBase = (
  entry: CharacterIndexEntry,
  ordinal: number,
  mode: CharacterChipMode,
): string => {
  if (mode === 'ordinal') {
    return String(ordinal + 1)
  }
  const source = (entry.lemma || entry.names.primary || '').replace(ARABIC_DIACRITICS_PATTERN, '').trim()
  const articleAware = source.replace(/^ال/u, '').replace(/^(al-|the\s+)/i, '').trim() || source
  const match = articleAware.match(/[\u0621-\u064A\u066E-\u06D3A-Za-z0-9]/u)
  if (!match?.[0]) {
    return String(ordinal + 1)
  }
  return /[A-Za-z]/.test(match[0]) ? match[0].toUpperCase() : match[0]
}

const buildCharacterDecorations = (
  entries: readonly CharacterIndexEntry[],
  chipMode: CharacterChipMode,
): Map<string, CharacterDecoration> => {
  const baseChips = entries.map((entry, index) => deriveCharacterChipBase(entry, index, chipMode))
  const chipCounts = new Map<string, number>()
  baseChips.forEach(chip => {
    chipCounts.set(chip, (chipCounts.get(chip) || 0) + 1)
  })

  const usedDuplicateOrdinals = new Map<string, number>()
  /* First appearance is the stable roster order. Probe from each canonical ID's
     hash until an unused ink is found; once all inks are occupied, reuse the
     least recently seen one. Appending characters therefore never recolors
     existing records, and adjacent entries cannot collide. */
  const lastColorUse = CHARACTER_COLOR_PALETTE.map(() => -1)
  const decorations = new Map<string, CharacterDecoration>()
  entries.forEach((entry, index) => {
    const baseChip = baseChips[index] || String(index + 1)
    const duplicateCount = chipCounts.get(baseChip) || 0
    const chipOrdinal = (usedDuplicateOrdinals.get(baseChip) || 0) + 1
    usedDuplicateOrdinals.set(baseChip, chipOrdinal)
    const baseColorIndex = stableHash(entry.id) % CHARACTER_COLOR_PALETTE.length
    let colorIndex = baseColorIndex
    if (lastColorUse.some(lastUse => lastUse < 0)) {
      while (lastColorUse[colorIndex] >= 0) {
        colorIndex = (colorIndex + 1) % CHARACTER_COLOR_PALETTE.length
      }
    } else {
      let longestGap = -1
      for (let offset = 0; offset < CHARACTER_COLOR_PALETTE.length; offset += 1) {
        const candidateIndex = (baseColorIndex + offset) % CHARACTER_COLOR_PALETTE.length
        const gap = index - lastColorUse[candidateIndex]
        if (gap > longestGap) {
          colorIndex = candidateIndex
          longestGap = gap
        }
      }
    }
    lastColorUse[colorIndex] = index
    decorations.set(entry.id, {
      chip: duplicateCount > 1 ? `${baseChip}${chipOrdinal}` : baseChip,
      color: CHARACTER_COLOR_PALETTE[colorIndex] || getCharacterColor(entry.id),
    })
  })
  return decorations
}

const getReaderTextAnnotationLayout = (
  text: string,
  annotations: readonly ReaderTextAnnotation[],
  extraBoundaries: readonly number[] = [],
): ReaderTextAnnotationLayout => {
  const valid = annotations
    .filter(item => (
      item.localStart >= 0 &&
      item.localEnd > item.localStart &&
      item.localEnd <= text.length
    ))
    .sort((left, right) => {
      const startDelta = left.localStart - right.localStart
      if (startDelta !== 0) {
        return startDelta
      }
      if (left.kind !== right.kind) {
        return left.kind === 'character' ? -1 : 1
      }
      if (left.kind === 'layer' && right.kind === 'layer' && left.layerKind !== right.layerKind) {
        return left.layerKind === 'focalization' ? -1 : 1
      }
      return right.localEnd - left.localEnd || left.id.localeCompare(right.id)
    })
  const boundaries = Array.from(new Set([
    0,
    text.length,
    ...valid.flatMap(item => [item.localStart, item.localEnd]),
    ...extraBoundaries.filter(boundary => boundary > 0 && boundary < text.length),
  ])).sort((left, right) => left - right)
  const segments: ReaderTextAnnotationSegment[] = []
  for (let index = 0; index < boundaries.length - 1; index += 1) {
    const start = boundaries[index]
    const end = boundaries[index + 1]
    if (end <= start) {
      continue
    }
    segments.push({
      start,
      end,
      covering: valid.filter(item => item.localStart <= start && item.localEnd >= end),
      opening: valid.filter(item => item.localStart === start),
    })
  }
  return { segments, droppedCount: annotations.length - valid.length }
}

/* Maps a pointer position to a character offset in a row by asking the browser for
   the caret under the point, then adding the hit segment's data-start. Bidi and
   line wrapping are the browser's problem, not ours. */
const resolveLectioRowOffsetFromPoint = (rowId: string, clientX: number, clientY: number): number | null => {
  const doc = document as Document & {
    caretPositionFromPoint?: (x: number, y: number) => { offsetNode: Node; offset: number } | null
    caretRangeFromPoint?: (x: number, y: number) => Range | null
  }
  const position = typeof doc.caretPositionFromPoint === 'function'
    ? doc.caretPositionFromPoint(clientX, clientY)
    : null
  const range = !position && typeof doc.caretRangeFromPoint === 'function'
    ? doc.caretRangeFromPoint(clientX, clientY)
    : null
  const node = position?.offsetNode || range?.startContainer || null
  if (!node) {
    return null
  }
  const nodeOffset = position ? position.offset : range ? range.startOffset : 0
  const element = node.nodeType === Node.TEXT_NODE
    ? node.parentElement
    : node instanceof Element ? node : null
  const segment = element?.closest?.('[data-start][data-end]') as HTMLElement | null
  if (!segment) {
    return null
  }
  const rowHost = segment.closest('[data-row-id]') as HTMLElement | null
  if (!rowHost || rowHost.dataset.rowId !== rowId) {
    return null
  }
  let localOffset = 0
  if (node.nodeType === Node.TEXT_NODE) {
    const walker = document.createTreeWalker(segment, NodeFilter.SHOW_TEXT)
    let current = walker.nextNode()
    while (current && current !== node) {
      localOffset += current.textContent?.length || 0
      current = walker.nextNode()
    }
    localOffset += nodeOffset
  }
  const segmentStart = Number(segment.dataset.start)
  return Number.isFinite(segmentStart) ? segmentStart + localOffset : null
}

const getLectioWordBoundaryAtOffset = (
  text: string,
  rawOffset: number,
): { start: number; end: number; text: string } | null => {
  if (!text) {
    return null
  }
  let offset = Math.max(0, Math.min(text.length - 1, rawOffset))
  if (/\s/u.test(text[offset] || '')) {
    let forward = offset
    while (forward < text.length && /\s/u.test(text[forward] || '')) {
      forward += 1
    }
    if (forward < text.length) {
      offset = forward
    } else {
      let backward = offset
      while (backward >= 0 && /\s/u.test(text[backward] || '')) {
        backward -= 1
      }
      offset = backward
    }
  }
  if (offset < 0 || /\s/u.test(text[offset] || '')) {
    return null
  }
  let start = offset
  let end = offset + 1
  while (start > 0 && !/\s/u.test(text[start - 1] || '')) {
    start -= 1
  }
  while (end < text.length && !/\s/u.test(text[end] || '')) {
    end += 1
  }
  return { start, end, text: text.slice(start, end) }
}

const buildCharacterAnchorsByRowId = (
  entries: readonly CharacterIndexEntry[],
  witnessId?: string,
  chipMode: CharacterChipMode = 'derived',
): Map<string, ReaderCharacterAnchor[]> => {
  const map = new Map<string, ReaderCharacterAnchor[]>()
  const decorations = buildCharacterDecorations(entries, chipMode)
  entries.forEach(entry => {
    const decoration = decorations.get(entry.id) || {
      color: getCharacterColor(entry.id),
      chip: entry.id,
    }
    entry.attestations.forEach(attestation => {
      attestation.sourceAnchors.forEach(anchor => {
        if (witnessId && anchor.witnessId !== witnessId) {
          return
        }
        const anchorMentionType = getSourceAnchorMentionType(anchor)
        const bucket = map.get(anchor.rowId) || []
        bucket.push({
          entry,
          anchor,
          role: attestation.role || null,
          mentionType: anchorMentionType || 'name',
          chip: decoration.chip,
          color: decoration.color,
          referentCharacterIds: [entry.id],
          referents: [{
            id: entry.id,
            label: getCharacterDisplayLabel(entry),
            chip: decoration.chip,
            color: decoration.color,
          }],
          sharedReference: false,
        })
        map.set(anchor.rowId, bucket)
      })
    })
  })
  return map
}

const getReaderLayerKind = (note: CharacterLayerNote): ReaderLayerKind | null => {
  const layer = note.metadata?.character_index_layer
  return layer === 'speaker' || layer === 'focalization' || layer === 'characterization' ? layer : null
}

const getReaderLayerCharacterId = (note: CharacterLayerNote): string => (
  typeof note.metadata?.character_id === 'string' ? note.metadata.character_id.trim() : ''
)

const getReaderLayerLabel = (note: CharacterLayerNote, kind: ReaderLayerKind): string => {
  const metadata = note.metadata || {}
  if (kind === 'speaker') {
    const analysis = readLectioSpeakerAnalysis(metadata.speaker_analysis)
    const address = getSpeakerChoiceLabel(LECTIO_SPEAKER_ADDRESS_MODES, analysis.addressMode)
    const primaryFunction = analysis.functions[0]
      ? getSpeakerChoiceLabel(LECTIO_SPEAKER_DISCOURSE_FUNCTIONS, analysis.functions[0])
      : 'function unclassified'
    const span = typeof metadata.span === 'string' ? metadata.span.trim() : ''
    return [address, primaryFunction, span].filter(Boolean).join(' · ')
  }
  if (kind === 'focalization') {
    const markerClass = typeof metadata.class === 'string' ? metadata.class.trim() : ''
    const marker = typeof metadata.marker === 'string' ? metadata.marker.trim() : ''
    return [markerClass || 'focalization', marker].filter(Boolean).join(' · ')
  }
  const manual = readLectioManualCharacterization(metadata.manual_characterization)
  if (manual) {
    const directLabels = (manual.forms || []).map(form => (
      LECTIO_MANUAL_CHARACTERIZATION_NARRATORIAL_FORMS.find(candidate => candidate.id === form)?.label || form
    ))
    const indirectLabels = [
      LECTIO_MANUAL_CHARACTERIZATION_INDIRECT_CHANNELS.find(
        candidate => candidate.id === manual.indirectChannel,
      )?.label,
      [
        ...LECTIO_MANUAL_CHARACTERIZATION_ACTION_DETAILS,
        ...LECTIO_MANUAL_CHARACTERIZATION_REACTION_DETAILS,
      ].find(candidate => candidate.id === manual.indirectDetail)?.label,
    ].filter(isDefined)
    const span = typeof metadata.span === 'string' ? metadata.span.trim() : ''
    return [manual.mode, ...(manual.mode === 'direct' ? directLabels : indirectLabels), span]
      .filter(Boolean)
      .join(' · ')
  }
  const devices = Array.isArray(metadata.devices)
    ? metadata.devices.filter((device): device is string => typeof device === 'string' && device.trim().length > 0)
    : []
  const span = typeof metadata.span === 'string' ? metadata.span.trim() : ''
  return [devices.join(', ') || 'characterization', span].filter(Boolean).join(' · ')
}

const getReaderLayerDetail = (note: CharacterLayerNote, kind: ReaderLayerKind): string => {
  const metadata = note.metadata || {}
  const context = typeof metadata.context === 'string' ? metadata.context.trim() : ''
  if (kind === 'speaker') {
    const analysis = readLectioSpeakerAnalysis(metadata.speaker_analysis)
    const turnId = typeof metadata.speech_turn_id === 'string' ? metadata.speech_turn_id.trim() : ''
    const level = typeof metadata.speech_level === 'number' ? metadata.speech_level : 0
    const analysisDetail = [
      `address: ${getSpeakerChoiceLabel(LECTIO_SPEAKER_ADDRESS_MODES, analysis.addressMode)}`,
      analysis.functions.length > 0
        ? `functions: ${summarizeSpeakerChoices(LECTIO_SPEAKER_DISCOURSE_FUNCTIONS, analysis.functions)}`
        : '',
      analysis.forms.length > 0
        ? `forms: ${summarizeSpeakerChoices(LECTIO_SPEAKER_DISCOURSE_FORMS, analysis.forms)}`
        : '',
      analysis.orientations.length > 0
        ? `orientations: ${summarizeSpeakerChoices(LECTIO_SPEAKER_ORIENTATIONS, analysis.orientations)}`
        : '',
      analysis.stances.length > 0
        ? `stances: ${summarizeSpeakerChoices(LECTIO_SPEAKER_STANCES, analysis.stances)}`
        : '',
      analysis.retrospectiveStatus !== 'none' ? `retrospective target: ${analysis.retrospectiveStatus}` : '',
    ].filter(Boolean)
    return [turnId ? `turn: ${turnId}` : '', `level: ${level}`, ...analysisDetail].filter(Boolean).join(' · ') || note.text
  }
  if (kind === 'focalization') {
    const scope = typeof metadata.scope === 'string' ? metadata.scope.trim() : ''
    return [scope ? `scope: ${scope}` : '', context ? `context: ${context}` : ''].filter(Boolean).join(' · ') || note.text
  }
  const source = typeof metadata.source_field === 'string' ? metadata.source_field.trim() : ''
  return [source ? `field: ${source}` : '', context ? `context: ${context}` : ''].filter(Boolean).join(' · ') || note.text
}

const getReaderLayerAnchorText = (note: CharacterLayerNote, kind: ReaderLayerKind): string => {
  const metadata = note.metadata || {}
  if (kind === 'focalization') {
    const sourceField = typeof metadata.source_field === 'string' ? metadata.source_field.trim() : ''
    const value = sourceField === 'marker'
      ? (typeof metadata.scope === 'string' && metadata.scope.trim() ? metadata.scope : metadata.marker)
      : (typeof metadata.scope === 'string' && metadata.scope.trim() ? metadata.scope : metadata.marker)
    return typeof value === 'string' ? value.trim() : ''
  }
  return typeof metadata.span === 'string' ? metadata.span.trim() : ''
}

const findUniqueReaderTextRange = (
  text: string,
  needle: string,
): { localStart: number; localEnd: number } | null => {
  if (!needle) {
    return null
  }
  const first = text.indexOf(needle)
  if (first < 0 || text.indexOf(needle, first + 1) >= 0) {
    return null
  }
  return {
    localStart: first,
    localEnd: first + needle.length,
  }
}

const resolveReaderLayerOffsets = ({
  kind,
  localEnd,
  localStart,
  note,
  rowText,
}: {
  kind: ReaderLayerKind
  note: CharacterLayerNote
  rowText: string
  localStart?: number | null
  localEnd?: number | null
}): { localStart: number; localEnd: number } | null => {
  const expectedText = getReaderLayerAnchorText(note, kind)
  const hasValidOffsets = (
    isFiniteOffset(localStart) &&
    isFiniteOffset(localEnd) &&
    localEnd > localStart &&
    localStart >= 0 &&
    localEnd <= rowText.length
  )
  if (hasValidOffsets && (!expectedText || rowText.slice(localStart, localEnd) === expectedText)) {
    return { localStart, localEnd }
  }
  const relocated = findUniqueReaderTextRange(rowText, expectedText)
  if (relocated) {
    return relocated
  }
  return hasValidOffsets ? { localStart, localEnd } : null
}

const buildReaderLayerAnnotationsByRowId = ({
  characterDecorationsById,
  notes,
  rowLabelById,
  rowTextById,
  sourceWitnessId,
}: {
  notes: readonly CharacterLayerNote[]
  sourceWitnessId: string
  rowLabelById: Map<string, string>
  rowTextById: Map<string, string>
  characterDecorationsById: Map<string, CharacterDecoration>
}): Map<string, ReaderLayerAnnotation[]> => {
  const map = new Map<string, ReaderLayerAnnotation[]>()
  notes.forEach(note => {
    const standing = getLectioLayerStanding(note)
    if (standing !== 'admitted' && standing !== 'received') {
      return
    }
    const kind = getReaderLayerKind(note)
    const characterId = getReaderLayerCharacterId(note)
    if (!kind || !characterId || note.anchor.kind !== 'span') {
      return
    }
    const { locator } = note.anchor
    if (
      locator.witnessId !== sourceWitnessId
    ) {
      return
    }
    const rowText = rowTextById.get(locator.rowId) || ''
    const offsets = resolveReaderLayerOffsets({
      kind,
      localStart: locator.localStart,
      localEnd: locator.localEnd,
      note,
      rowText,
    })
    if (!offsets) {
      return
    }
    const decoration = characterDecorationsById.get(characterId)
    const subtype = getReaderLayerSubtype(note, kind)
    const speechTurnId = kind === 'speaker' && typeof note.metadata?.speech_turn_id === 'string'
      ? note.metadata.speech_turn_id.trim()
      : ''
    const speechLevel = kind === 'speaker' && typeof note.metadata?.speech_level === 'number'
      ? Math.max(0, Math.floor(note.metadata.speech_level))
      : 0
    const speechFragmentIndex = kind === 'speaker' && typeof note.metadata?.speech_fragment_index === 'number'
      ? Math.max(0, Math.floor(note.metadata.speech_fragment_index))
      : undefined
    const annotation: ReaderLayerAnnotation = {
      id: note.note_id,
      note,
      kind,
      characterId,
      chip: decoration?.chip || characterId,
      localStart: offsets.localStart,
      localEnd: offsets.localEnd,
      rowId: locator.rowId,
      rowLabel: rowLabelById.get(locator.rowId) || locator.rowId,
      label: getReaderLayerLabel(note, kind),
      detail: getReaderLayerDetail(note, kind),
      color: decoration?.color || getCharacterColor(characterId),
      glyph: subtype.glyph,
      ruleStyle: subtype.ruleStyle,
      sourceVoice: getReaderLayerSourceVoice(note),
      subtypeLabel: subtype.label,
      classifications: getReaderLayerClassifications(note, kind),
      standing,
      ...(kind === 'speaker' ? {
        speechTurnId: speechTurnId || note.note_id,
        speechLevel,
        speechFragmentIndex,
        speakerAnalysis: readLectioSpeakerAnalysis(note.metadata?.speaker_analysis),
      } : {}),
    }
    const bucket = map.get(locator.rowId) || []
    bucket.push(annotation)
    map.set(locator.rowId, bucket)
  })
  map.forEach(annotations => {
    annotations.sort((left, right) => (
      left.localStart - right.localStart ||
      left.localEnd - right.localEnd ||
      left.id.localeCompare(right.id)
    ))
  })
  return map
}

const compareReaderPositions = (
  left: { rowId: string; offset: number },
  right: { rowId: string; offset: number },
  rowOrderById: ReadonlyMap<string, number>,
): number => {
  const rowDelta = (rowOrderById.get(left.rowId) ?? Number.MAX_SAFE_INTEGER) -
    (rowOrderById.get(right.rowId) ?? Number.MAX_SAFE_INTEGER)
  return rowDelta || left.offset - right.offset
}

type ReaderPositionRange = {
  startRowId: string
  startOffset: number
  endRowId: string
  endOffset: number
}

const readerRangeStrictlyContains = (
  outer: ReaderPositionRange,
  inner: ReaderPositionRange,
  rowOrderById: ReadonlyMap<string, number>,
): boolean => {
  const startComparison = compareReaderPositions(
    { rowId: outer.startRowId, offset: outer.startOffset },
    { rowId: inner.startRowId, offset: inner.startOffset },
    rowOrderById,
  )
  const endComparison = compareReaderPositions(
    { rowId: outer.endRowId, offset: outer.endOffset },
    { rowId: inner.endRowId, offset: inner.endOffset },
    rowOrderById,
  )
  return startComparison <= 0 && endComparison >= 0 && (
    startComparison < 0 || endComparison > 0
  )
}

const buildReaderSpeakerTurnRails = ({
  activeCharacterId,
  annotationsByRowId,
  entries,
  isolatedEvidenceKind,
  overlayVisible,
  rowOrderById,
}: {
  activeCharacterId?: string
  annotationsByRowId: ReadonlyMap<string, readonly ReaderLayerAnnotation[]>
  entries: readonly CharacterIndexEntry[]
  isolatedEvidenceKind: LectioIsolationKind | null
  overlayVisible: boolean
  rowOrderById: ReadonlyMap<string, number>
}): ReaderSpeakerTurnRail[] => {
  if (isolatedEvidenceKind && isolatedEvidenceKind !== 'speaker') {
    return []
  }
  const annotationsByTurnId = new Map<string, ReaderLayerAnnotation[]>()
  annotationsByRowId.forEach(annotations => {
    annotations.forEach(annotation => {
      if (
        annotation.kind !== 'speaker' ||
        (isolatedEvidenceKind === 'speaker' && activeCharacterId && annotation.characterId !== activeCharacterId)
      ) {
        return
      }
      const turnId = annotation.speechTurnId || annotation.id
      const bucket = annotationsByTurnId.get(turnId) || []
      bucket.push(annotation)
      annotationsByTurnId.set(turnId, bucket)
    })
  })
  const entryById = new Map(entries.map(entry => [entry.id, entry]))
  const turns = Array.from(annotationsByTurnId.entries()).flatMap(([turnId, fragments]) => {
    const ordered = [...fragments].sort((left, right) => {
      const fragmentDelta = (left.speechFragmentIndex ?? Number.MAX_SAFE_INTEGER) -
        (right.speechFragmentIndex ?? Number.MAX_SAFE_INTEGER)
      if (fragmentDelta !== 0) {
        return fragmentDelta
      }
      return compareReaderPositions(
        { rowId: left.rowId, offset: left.localStart },
        { rowId: right.rowId, offset: right.localStart },
        rowOrderById,
      )
    })
    const first = ordered[0]
    const last = ordered[ordered.length - 1]
    if (!first || !last) {
      return []
    }
    const entry = entryById.get(first.characterId)
    const standing: LectioEvidenceStanding = ordered.some(fragment => fragment.standing === 'admitted')
      ? 'admitted'
      : 'received'
    return [{
      turnId,
      sourceTurnIds: [turnId],
      characterId: first.characterId,
      label: entry ? getCharacterDisplayLabel(entry) : first.characterId,
      color: first.color,
      standing,
      level: first.speechLevel || 0,
      startRowId: first.rowId,
      startRowLabel: first.rowLabel,
      startOffset: first.localStart,
      endRowId: last.rowId,
      endRowLabel: last.rowLabel,
      endOffset: last.localEnd,
      firstNoteId: first.id,
      labelPlacement: 'center' as const,
      overlayVisible,
    }]
  }).sort((left, right) => (
    compareReaderPositions(
      { rowId: left.startRowId, offset: left.startOffset },
      { rowId: right.startRowId, offset: right.startOffset },
      rowOrderById,
    ) || left.level - right.level || left.turnId.localeCompare(right.turnId)
  ))

  /* A rapid marking pass can produce one stored turn per selected unit even when
     the discourse has not changed speaker. Join only the marginal notation: the
     source turn ids and their annotation triggers remain independent editorial
     records. Quotation levels are coalesced separately so an embedded voice does
     not cut an enclosing voice, while a different speaker at the same level does. */
  const visualTurns = Array.from(new Set(turns.map(turn => turn.level))).flatMap(level => {
    const levelTurns = turns.filter(turn => turn.level === level)
    return levelTurns.reduce<ReaderSpeakerTurnRail[]>((runs, turn) => {
      const previous = runs[runs.length - 1]
      const previousEndRow = previous ? rowOrderById.get(previous.endRowId) : undefined
      const currentStartRow = rowOrderById.get(turn.startRowId)
      const followsImmediately = previousEndRow !== undefined && currentStartRow !== undefined &&
        currentStartRow === previousEndRow + 1
      if (
        previous &&
        followsImmediately &&
        previous.characterId === turn.characterId &&
        previous.standing === turn.standing
      ) {
        runs[runs.length - 1] = {
          ...previous,
          sourceTurnIds: [...previous.sourceTurnIds, ...turn.sourceTurnIds],
          endRowId: turn.endRowId,
          endRowLabel: turn.endRowLabel,
          endOffset: turn.endOffset,
        }
        return runs
      }
      runs.push(turn)
      return runs
    }, [])
  }).sort((left, right) => (
    compareReaderPositions(
      { rowId: left.startRowId, offset: left.startOffset },
      { rowId: right.startRowId, offset: right.startOffset },
      rowOrderById,
    ) || left.level - right.level || left.turnId.localeCompare(right.turnId)
  ))

  /* Older and manually created annotations can carry level 0 even when one
     speech span is strictly contained by another. Resolve that containment as
     a visual quotation level so embedded voices occupy separate rails. Exact
     duplicate and partially crossing spans remain same-level conflicts. */
  const resolvedLevelByTurnId = new Map<string, ReaderSpeakerTurnRail>()
  const nestingOrder = [...visualTurns].sort((left, right) => (
    compareReaderPositions(
      { rowId: left.startRowId, offset: left.startOffset },
      { rowId: right.startRowId, offset: right.startOffset },
      rowOrderById,
    ) || compareReaderPositions(
      { rowId: right.endRowId, offset: right.endOffset },
      { rowId: left.endRowId, offset: left.endOffset },
      rowOrderById,
    ) || left.level - right.level || left.turnId.localeCompare(right.turnId)
  ))
  nestingOrder.forEach(turn => {
    const inferredLevel = Array.from(resolvedLevelByTurnId.values()).reduce((level, container) => (
      readerRangeStrictlyContains(container, turn, rowOrderById)
        ? Math.max(level, container.level + 1)
        : level
    ), 0)
    resolvedLevelByTurnId.set(turn.turnId, {
      ...turn,
      level: Math.max(turn.level, inferredLevel),
    })
  })
  const leveledTurns = visualTurns.map(turn => resolvedLevelByTurnId.get(turn.turnId) || turn)

  return leveledTurns.map(turn => {
    const containsNestedTurn = leveledTurns.some(candidate => (
      candidate !== turn &&
      candidate.level > turn.level &&
      readerRangeStrictlyContains(turn, candidate, rowOrderById)
    ))
    return {
      ...turn,
      labelPlacement: turn.level > 0 || containsNestedTurn ? 'entry' : 'center',
    }
  })
}

const inferSpeakerLevelForRange = (
  turns: readonly ReaderSpeakerTurnRail[],
  range: ReaderPositionRange,
  rowOrderById: ReadonlyMap<string, number>,
): number => turns.reduce((level, turn) => (
  readerRangeStrictlyContains(turn, range, rowOrderById)
    ? Math.max(level, turn.level + 1)
    : level
), 0)

const toSuperscriptNumber = (value: number): string => String(value).replace(
  /\d/g,
  digit => '⁰¹²³⁴⁵⁶⁷⁸⁹'[Number(digit)] || digit,
)

const getCharacterDisplayLabel = (entry: CharacterIndexEntry): string => (
  entry.lemma || entry.names.primary || entry.id
)

const buildSharedReferenceGradient = (referents: readonly ReaderCharacterReferent[]): string => {
  const colors = referents.map(referent => referent.color).filter(Boolean)
  if (colors.length === 0) {
    return 'linear-gradient(90deg, var(--cte-ink-3), var(--cte-ink-3))'
  }
  const width = 100 / colors.length
  const stops = colors.flatMap((color, index) => [
    `${color} ${(index * width).toFixed(2)}%`,
    `${color} ${((index + 1) * width).toFixed(2)}%`,
  ])
  return `linear-gradient(90deg, ${stops.join(', ')})`
}

const getSharedReferenceChip = (referents: readonly ReaderCharacterReferent[]): string => {
  const chips = referents.map(referent => referent.chip).filter(Boolean)
  return chips.length <= 3 ? chips.join('·') : `${chips.slice(0, 2).join('·')}+${chips.length - 2}`
}

const getCharacterAnchorWindowLabel = (anchor: CharacterIndexSourceAnchor, fallback: string): string => {
  const window = typeof anchor.window === 'string' ? anchor.window.trim() : ''
  if (window) {
    return window
  }
  if (isFiniteOffset(anchor.localStart) && isFiniteOffset(anchor.localEnd)) {
    return `${anchor.localStart}-${anchor.localEnd}`
  }
  return fallback
}

const getCharacterOccurrenceId = (
  entryId: string,
  anchor: CharacterIndexSourceAnchor,
): string => [
  entryId,
  anchor.witnessId,
  anchor.rowId,
  anchor.localStart ?? 'row',
  anchor.localEnd ?? 'row',
  getSourceAnchorMentionType(anchor) || 'name',
  anchor.window || '',
].join(':')

const findCharacterOccurrenceForAnchor = (
  occurrences: readonly CharacterOccurrence[],
  characterId: string,
  anchor: CharacterIndexSourceAnchor,
): CharacterOccurrence | null => occurrences.find(occurrence => (
  characterOccurrenceRefersTo(occurrence, characterId) &&
  occurrence.locator.witnessId === anchor.witnessId &&
  occurrence.locator.rowId === anchor.rowId &&
  occurrence.locator.localStart === anchor.localStart &&
  occurrence.locator.localEnd === anchor.localEnd &&
  normalizeCharacterMentionType(occurrence.metadata?.mention_type || occurrence.metadata?.mentionType) ===
    (getSourceAnchorMentionType(anchor) || 'name')
)) || null

const formatDiffSummary = (peer: LedgerSafeTraditionPeerUnit): string => {
  const { total, changed, inserts, deletes, substitutes } = peer.diff.stats
  if (total === 0) {
    return '0 tokens'
  }
  const ratio = Math.round((changed / total) * 100)
  return `${changed}/${total} changed · ${ratio}% · +${inserts} −${deletes} ~${substitutes}`
}

const getPeerChangeRatio = (peer: LedgerSafeTraditionPeerUnit): number => {
  const { total, changed } = peer.diff.stats
  return total > 0 ? Math.max(0, changed / total) : 0
}

const getPeerSimilarityPercent = (peer: LedgerSafeTraditionPeerUnit): number => (
  Math.max(0, Math.min(100, Math.round((1 - getPeerChangeRatio(peer)) * 100)))
)

const getVariationTierFromRatio = (ratio: number): VariationTier => {
  if (ratio >= 0.45) {
    return 't2'
  }
  if (ratio >= 0.15) {
    return 't1'
  }
  return 't3'
}

const getPeerVariationTier = (peer: LedgerSafeTraditionPeerUnit): VariationTier => (
  getVariationTierFromRatio(getPeerChangeRatio(peer))
)

const getPeerVariationLabel = (peer: LedgerSafeTraditionPeerUnit): string => {
  const ratio = getPeerChangeRatio(peer)
  if (ratio === 0) {
    return 'Identical'
  }
  return ratio >= 0.45 ? 'Divergent' : 'Near'
}

const getUnitVariationTier = (peers: readonly LedgerSafeTraditionPeerUnit[]): UnitVariationTier => {
  if (peers.length === 0) {
    return 'silent-run'
  }
  const maxRatio = Math.max(...peers.map(peer => getPeerChangeRatio(peer)))
  return getVariationTierFromRatio(maxRatio)
}

const getUnitVariationLabel = (tier: UnitVariationTier): string => {
  if (tier === 'silent-run') {
    return 'Silent'
  }
  if (tier === 't2') {
    return 'Divergent'
  }
  if (tier === 't1') {
    return 'Near'
  }
  return 'Identical'
}

const DiffTokens: React.FC<{ peer: LedgerSafeTraditionPeerUnit }> = ({ peer }) => (
  <div className="lst-diff" dir={hasArabicText(peer.text) ? 'rtl' : 'auto'}>
    {peer.diff.tokens.map((token, index) => {
      if (token.kind === 'space') {
        return <React.Fragment key={`space:${index}`}>{token.text}</React.Fragment>
      }
      return (
        <span
          key={`word:${index}:${token.text}`}
          className={[
            'lst-diff-token',
            token.op && token.op !== 'match' ? `lst-${token.op}` : '',
            token.morph === 'near' ? 'lst-near' : '',
          ].filter(Boolean).join(' ')}
        >
          {token.text}
        </span>
      )
    })}
  </div>
)

const PeerCard: React.FC<{
  peer: LedgerSafeTraditionPeerUnit
  active: boolean
  cardRef?: (element: HTMLElement | null) => void
  onClick: () => void
}> = ({ active, cardRef, onClick, peer }) => {
  const similarityPercent = getPeerSimilarityPercent(peer)
  const variationTier = getPeerVariationTier(peer)
  const variationLabel = getPeerVariationLabel(peer)
  return (
    <article
      ref={cardRef}
      className={[
        'cte-card',
        'ledger-tradition-card',
        variationTier,
        active ? 'focused active' : '',
      ].filter(Boolean).join(' ')}
      data-testid={`ledger-tradition-peer-${peer.rowId}-${peer.witnessId}`}
      onClick={onClick}
    >
      <div className="cte-card-head">
        <span>
          {peer.witnessLabel}
          <small>{peer.traditionLabel}</small>
        </span>
        <span className="cte-tier">{variationLabel}</span>
      </div>
      <div className="cte-card-score">
        <span>{peer.unitLabel || peer.rowLabel}</span>
        <span className="cte-score-bar" title={`${similarityPercent}% similarity`}>
          <i style={{ width: `${similarityPercent}%` }} />
        </span>
        <span>{formatDiffSummary(peer)}</span>
      </div>
      <DiffTokens peer={peer} />
    </article>
  )
}

const ReaderSectionMarginEditor: React.FC<{
  rowLabel: string
  phase: Exclude<LectioRapidSectionPhase, 'opening'>
  sectionTypes: readonly TextSectionType[]
  selectedType?: TextSectionType
  title: string
  editing: boolean
  customTypeOpen: boolean
  customTypeLabel: string
  customTypeAbbreviation: string
  saving: boolean
  onSelectType: (sectionTypeId: string) => void
  onChangeType: () => void
  onChangeTitle: (title: string) => void
  onToggleCustomType: () => void
  onChangeCustomTypeLabel: (label: string) => void
  onChangeCustomTypeAbbreviation: (abbreviation: string) => void
  onAddCustomType: () => void
  onSave: () => void
  onDelete: () => void
  onRemark: () => void
  noteCount: number
  onOpenNotes: (anchor: HTMLButtonElement) => void
  onCancel: () => void
}> = ({
  rowLabel,
  phase,
  sectionTypes,
  selectedType,
  title,
  editing,
  customTypeOpen,
  customTypeLabel,
  customTypeAbbreviation,
  saving,
  onSelectType,
  onChangeType,
  onChangeTitle,
  onToggleCustomType,
  onChangeCustomTypeLabel,
  onChangeCustomTypeAbbreviation,
  onAddCustomType,
  onSave,
  onDelete,
  onRemark,
  noteCount,
  onOpenNotes,
  onCancel,
}) => {
  const [localTitle, setLocalTitle] = useState(title)

  useEffect(() => {
    setLocalTitle(title)
  }, [title])

  return (
    <span
    className="lectio-unit-structure-draft"
    role="group"
    aria-label={editing && selectedType
      ? `Edit ${selectedType.label} beginning in ${rowLabel}`
      : `Section beginning in ${rowLabel}`}
  >
    {phase === 'classify' ? (
      <>
        <span className="lectio-unit-structure-draft-prompt">§ · choose type</span>
        <span className="lectio-unit-structure-type-list">
          {sectionTypes.map(sectionType => (
            <button
              key={sectionType.id}
              type="button"
              disabled={saving}
              aria-label={`Set section type to ${sectionType.label}`}
              onClick={() => onSelectType(sectionType.id)}
            >{sectionType.label}</button>
          ))}
          <button
            type="button"
            disabled={saving}
            aria-expanded={customTypeOpen}
            onClick={onToggleCustomType}
          >Define another section type</button>
        </span>
        {customTypeOpen && (
          <span className="lectio-unit-structure-custom-type">
            <input
              aria-label="Custom section type"
              dir="auto"
              value={customTypeLabel}
              placeholder="Type name"
              onChange={event => onChangeCustomTypeLabel(event.target.value)}
            />
            <input
              aria-label="Custom section abbreviation"
              dir="auto"
              value={customTypeAbbreviation}
              placeholder="margin label"
              onChange={event => onChangeCustomTypeAbbreviation(event.target.value)}
            />
            <button type="button" disabled={saving} onClick={onAddCustomType}>
              Add custom section type
            </button>
          </span>
        )}
      </>
    ) : selectedType ? (
      <>
        <span className="lectio-unit-structure-title-draft">
          <button
            type="button"
            disabled={saving}
            aria-label={`Change ${selectedType.label} section type`}
            onClick={onChangeType}
          >{selectedType.abbreviation} ·</button>
          <input
            autoFocus
            aria-label={`Title for ${selectedType.label} beginning in ${rowLabel}`}
            dir="auto"
            value={localTitle}
            placeholder="untitled"
            disabled={saving}
            onChange={event => {
              setLocalTitle(event.target.value)
              onChangeTitle(event.target.value)
            }}
          />
        </span>
        {phase === 'closing' && (
          <span className="lectio-unit-structure-draft-prompt">choose closing unit or word</span>
        )}
        {phase === 'editing' && (
          <span className="lectio-unit-structure-edit-actions">
            <button type="button" disabled={saving} onClick={onSave}>Save section metadata</button>
            <button type="button" disabled={saving} onClick={onRemark}>Re-mark boundaries</button>
            <button
              type="button"
              disabled={saving}
              data-note-count={noteCount > 0 ? noteCount : undefined}
              aria-label={`Research notes for ${title || selectedType.label}`}
              onClick={event => onOpenNotes(event.currentTarget)}
            >Research notes{noteCount > 0 ? ` (${noteCount})` : ''}</button>
            <button type="button" disabled={saving} onClick={onDelete}>Delete section</button>
          </span>
        )}
      </>
    ) : null}
    <button
      type="button"
      className="lectio-unit-structure-draft-cancel"
      aria-label="Cancel section marking"
      onClick={onCancel}
    >×</button>
    </span>
  )
}

const ReaderResearchNoteEditor: React.FC<{
  state: LectioResearchNoteEditorState
  notes: readonly AnnotationResearchNote[]
  workLabel?: string
  witnessLabel: string
  savingDisabled: boolean
  onSave?: (note: AnnotationResearchNote) => void | Promise<void>
  onArchive?: (noteId: string) => void | Promise<void>
  onClose: () => void
}> = ({
  state,
  notes,
  workLabel,
  witnessLabel,
  savingDisabled,
  onSave,
  onArchive,
  onClose,
}) => {
  const [editingNoteId, setEditingNoteId] = useState('')
  const [title, setTitle] = useState('')
  const [bodyMarkdown, setBodyMarkdown] = useState('')
  const [researchState, setResearchState] = useState<AnnotationResearchNoteState>('observation')
  const [keywords, setKeywords] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [lastSavedNote, setLastSavedNote] = useState<AnnotationResearchNote | null>(null)
  const dialogRef = useRef<HTMLDivElement | null>(null)

  const resetDraft = useCallback(() => {
    setEditingNoteId('')
    setTitle('')
    setBodyMarkdown('')
    setResearchState('observation')
    setKeywords('')
    setError('')
    setLastSavedNote(null)
  }, [])

  useEffect(() => {
    resetDraft()
  }, [resetDraft, state.target.annotationId, state.target.kind, state.target.logicalId])

  useLayoutEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) {
      return
    }
    const bounds = dialog.getBoundingClientRect()
    const gutter = 8
    const left = Math.max(gutter, Math.min(state.anchorLeft, window.innerWidth - bounds.width - gutter))
    const preferredTop = state.anchorBottom + 6
    const top = preferredTop + bounds.height <= window.innerHeight - gutter
      ? preferredTop
      : Math.max(gutter, window.innerHeight - bounds.height - gutter)
    dialog.style.left = `${left}px`
    dialog.style.top = `${top}px`
  }, [notes.length, state.anchorBottom, state.anchorLeft, state.target.annotationId])

  useEffect(() => {
    const handlePointerDown = (event: PointerEvent) => {
      if (!dialogRef.current?.contains(event.target as Node)) {
        onClose()
      }
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        onClose()
      }
    }
    window.addEventListener('pointerdown', handlePointerDown)
    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('pointerdown', handlePointerDown)
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [onClose])

  const selectNote = (note: AnnotationResearchNote) => {
    setEditingNoteId(note.id)
    setTitle(note.title || '')
    setBodyMarkdown(note.bodyMarkdown)
    setResearchState(note.researchState)
    setKeywords(note.keywords.join(', '))
    setLastSavedNote(note)
    setError('')
  }

  const buildDraftNote = (): AnnotationResearchNote => {
    const keywordValues = keywords.split(/[,\n]/u).map(keyword => keyword.trim()).filter(Boolean)
    const existing = notes.find(note => note.id === editingNoteId) || (
      lastSavedNote?.id === editingNoteId ? lastSavedNote : undefined
    )
    if (existing) {
      return {
        ...updateAnnotationResearchNote(existing, {
          title,
          bodyMarkdown,
          researchState,
          keywords: keywordValues,
        }),
        /* Annotation ids are stable across claims. Refresh descriptive target
           metadata so a note follows its tag when ownership is adjudicated. */
        target: state.target,
      }
    }
    return createAnnotationResearchNote({
      chapterId: state.chapterId,
      bookId: state.bookId,
      sourceWitnessId: state.passageSnapshot.sourceWitnessId,
      target: state.target,
      title,
      bodyMarkdown,
      researchState,
      keywords: keywordValues,
      passageSnapshot: state.passageSnapshot,
    })
  }

  const saveNote = async () => {
    if (!onSave || saving || savingDisabled) {
      return
    }
    setSaving(true)
    setError('')
    try {
      const note = buildDraftNote()
      await onSave(note)
      setEditingNoteId(note.id)
      setLastSavedNote(note)
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : 'The research note could not be saved')
    } finally {
      setSaving(false)
    }
  }

  const archiveNote = async () => {
    if (!editingNoteId || !onArchive || saving) {
      return
    }
    setSaving(true)
    setError('')
    try {
      await onArchive(editingNoteId)
      resetDraft()
    } catch (archiveError) {
      setError(archiveError instanceof Error ? archiveError.message : 'The research note could not be archived')
    } finally {
      setSaving(false)
    }
  }

  const copyNote = async () => {
    try {
      const note = buildDraftNote()
      await navigator.clipboard.writeText(renderAnnotationResearchNoteMarkdown(note, {
        workLabel,
        witnessLabel,
      }))
      setError('Copied with tagged passage')
    } catch (copyError) {
      setError(copyError instanceof Error ? copyError.message : 'The research note could not be copied')
    }
  }

  return (
    <div
      ref={dialogRef}
      className="lectio-research-note-popover"
      role="dialog"
      aria-modal="false"
      aria-label={`Research notes for ${state.targetLabel}`}
      style={{ left: state.anchorLeft, top: state.anchorBottom + 6 }}
      onPointerDown={event => event.stopPropagation()}
    >
      <div className="lectio-research-note-head">
        <span>Research notes · {state.target.kind}</span>
        <button type="button" aria-label="Close research notes" onClick={onClose}>×</button>
        <b dir="auto">{state.targetLabel}</b>
        <small>{state.passageSnapshot.unitLabels.join('–') || witnessLabel}</small>
      </div>
      {notes.length > 0 && (
        <div className="lectio-research-note-list" aria-label="Existing research notes">
          {notes.map(note => (
            <button
              key={note.id}
              type="button"
              aria-pressed={editingNoteId === note.id}
              onClick={() => selectNote(note)}
            >
              <b>{note.title || `${note.researchState} note`}</b>
              <span>{note.bodyMarkdown}</span>
            </button>
          ))}
          <button type="button" aria-pressed={!editingNoteId} onClick={resetDraft}>+ New note</button>
        </div>
      )}
      <label>
        <span>Title</span>
        <input
          aria-label="Research note title"
          dir="auto"
          value={title}
          disabled={savingDisabled}
          onChange={event => setTitle(event.target.value)}
        />
      </label>
      <label>
        <span>Markdown</span>
        <textarea
          autoFocus
          aria-label="Markdown research note"
          dir="auto"
          rows={8}
          value={bodyMarkdown}
          disabled={savingDisabled}
          onChange={event => setBodyMarkdown(event.target.value)}
        />
      </label>
      <fieldset className="lectio-research-note-states">
        <legend>Research state</legend>
        {ANNOTATION_RESEARCH_NOTE_STATES.map(option => (
          <label key={option.value}>
            <input
              type="radio"
              name={`research-state-${state.target.kind}-${state.target.annotationId}`}
              checked={researchState === option.value}
              disabled={savingDisabled}
              onChange={() => setResearchState(option.value)}
            />
            <span>{option.label}</span>
          </label>
        ))}
      </fieldset>
      <label>
        <span>Keywords · comma separated</span>
        <input
          aria-label="Research note keywords"
          value={keywords}
          disabled={savingDisabled}
          onChange={event => setKeywords(event.target.value)}
        />
      </label>
      <div className="lectio-research-note-actions">
        <button type="button" disabled={saving || savingDisabled || !bodyMarkdown.trim()} onClick={() => void saveNote()}>
          Save research note
        </button>
        <button type="button" disabled={!bodyMarkdown.trim()} onClick={() => void copyNote()}>
          Copy note with passage
        </button>
        {editingNoteId && onArchive && (
          <button type="button" className="remove" disabled={saving} onClick={() => void archiveNote()}>
            Archive note
          </button>
        )}
      </div>
      {error && <div className="lectio-research-note-status" role="status">{error}</div>}
    </div>
  )
}

const useEventCallback = <Args extends unknown[], Result>(
  callback: (...args: Args) => Result,
): ((...args: Args) => Result) => {
  const callbackRef = useRef(callback)

  useEffect(() => {
    callbackRef.current = callback
  }, [callback])

  return useCallback((...args: Args) => callbackRef.current(...args), [])
}

type ReaderUnitRenderObserver = (rowId: string) => void

let readerUnitRenderObserver: ReaderUnitRenderObserver | null = null

export const setReaderUnitRenderObserver = (observer: ReaderUnitRenderObserver | null): void => {
  readerUnitRenderObserver = observer
}

const ReaderUnit: React.FC<{
  unit: LedgerSafeTraditionUnit
  focused: boolean
  annotationGrammar?: LectioAnnotationGrammar
  characterAnchors?: ReaderCharacterAnchor[]
  layerAnnotations?: ReaderLayerAnnotation[]
  unitLayerSummaries?: ReaderUnitLayerSummary[]
  structureSummary?: ReaderUnitStructureSummary
  structureDraftEditor?: React.ReactNode
  structureDraftOffset?: number
  focusedLayerNoteId?: string | null
  focusedOccurrenceId?: string | null
  selectedCharacterId?: string
  showMappedRowHighlights: boolean
  showRowLabel?: boolean
  lineApparatus?: React.ReactNode
  lineApparatusOpen?: boolean
  inlineCandidate?: React.ReactNode
  subJudiceCount?: number
  tweezedReadingId?: string | null
  variationTier?: UnitVariationTier
  selectFocusLevel: 'reader' | 'apparatus'
  onFocus: (unit: LedgerSafeTraditionUnit, nextFocusLevel: 'reader' | 'apparatus') => void
  onSelect: (unit: LedgerSafeTraditionUnit, nextFocusLevel: 'reader' | 'apparatus') => void
  onToggleLineApparatus?: (rowId: string) => void
  onBoundaryResize?: (command: LectioAdjudicationResizeCommand, rowLabel: string) => void
  onDraftBareWord?: (
    rowId: string,
    start: number,
    end: number,
    text: string,
    anchor: HTMLButtonElement,
  ) => void
  rapidMarking?: LectioRapidMarkingContext | null
  onRapidMarkWord?: (
    rowId: string,
    start: number,
    end: number,
    text: string,
    anchor: HTMLButtonElement,
  ) => void
  onRapidMarkSectionPoint?: (rowId: string, start: number, end: number, text: string) => void
  onRapidMarkUnitBoundary?: (rowId: string) => void
  onOpenEvidenceMenu?: (target: LectioEvidenceMenuTarget, anchor: HTMLElement) => void
  openEvidenceMenuId?: string | null
  onSelectCharacter?: (characterId: string) => void
  onSelectCharacterOccurrence?: (characterId: string, occurrenceId: string) => void
  onSelectLayerAnnotation?: (characterId: string, rowId: string, noteId: string) => void
  onSelectUnitLayerSummary?: (rowId: string, summary: ReaderUnitLayerSummaryCharacter) => void
  onSelectStructureSection?: (sectionId: string) => void
  getResearchNoteCount?: (target: AnnotationResearchNoteTarget) => number
  register: (rowId: string, element: HTMLSpanElement | null) => void
}> = React.memo(({
  annotationGrammar = DEFAULT_LECTIO_ANNOTATION_GRAMMAR,
  characterAnchors = [],
  layerAnnotations = [],
  unitLayerSummaries,
  structureSummary,
  structureDraftEditor,
  structureDraftOffset,
  focusedLayerNoteId,
  focusedOccurrenceId,
  focused,
  inlineCandidate,
  lineApparatus,
  lineApparatusOpen = false,
  onFocus,
  onSelect,
  onSelectCharacter,
  onSelectCharacterOccurrence,
  onSelectLayerAnnotation,
  onSelectUnitLayerSummary,
  onSelectStructureSection,
  getResearchNoteCount,
  onToggleLineApparatus,
  onBoundaryResize,
  onDraftBareWord,
  rapidMarking = null,
  onRapidMarkWord,
  onRapidMarkSectionPoint,
  onRapidMarkUnitBoundary,
  onOpenEvidenceMenu,
  register,
  openEvidenceMenuId,
  selectedCharacterId,
  selectFocusLevel,
  showMappedRowHighlights,
  showRowLabel = false,
  subJudiceCount = 0,
  tweezedReadingId = null,
  unit,
  variationTier = 'silent-run',
}) => {
  readerUnitRenderObserver?.(unit.rowId)
  const [boundaryDrag, setBoundaryDrag] = useState<LectioBoundaryDrag | null>(null)
  const boundaryDragRef = useRef<LectioBoundaryDrag | null>(null)
  const pendingPointerRef = useRef<{ x: number; y: number } | null>(null)
  const boundaryFrameRef = useRef<number | null>(null)
  const boundaryReadoutRef = useRef<HTMLDivElement | null>(null)
  const boundaryDragHostRef = useRef<HTMLElement | null>(null)

  const baseCharacterTextAnnotations: ReaderTextAnnotation[] = characterAnchors
    .filter(item => (
      isFiniteOffset(item.anchor.localStart) &&
      isFiniteOffset(item.anchor.localEnd)
    ))
    .map(item => ({
      kind: 'character',
      id: item.evidenceId || getCharacterOccurrenceId(item.entry.id, item.anchor),
      entry: item.entry,
      anchor: item.anchor,
      role: item.role,
      mentionType: item.mentionType,
      chip: item.chip,
      color: item.color,
      standing: item.standing || 'received',
      referentCharacterIds: item.referentCharacterIds,
      referents: item.referents,
      sharedReference: item.sharedReference,
      localStart: item.anchor.localStart!,
      localEnd: item.anchor.localEnd!,
    }))
  const baseLayerTextAnnotations: ReaderTextAnnotation[] = layerAnnotations.map(layer => ({
    kind: 'layer',
    id: layer.id,
    layer,
    layerKind: layer.kind,
    characterId: layer.characterId,
    color: layer.color,
    standing: layer.standing,
    localStart: layer.localStart,
    localEnd: layer.localEnd,
  }))
  const characterTextAnnotations = baseCharacterTextAnnotations.map(item => (
    boundaryDrag && boundaryDrag.sourceKind === 'occurrence' && item.id === boundaryDrag.readingId
      ? { ...item, localStart: boundaryDrag.previewStart, localEnd: boundaryDrag.previewEnd }
      : item
  ))
  const layerTextAnnotations = baseLayerTextAnnotations.map(item => (
    boundaryDrag && boundaryDrag.sourceKind === 'layer' && item.id === boundaryDrag.readingId
      ? { ...item, localStart: boundaryDrag.previewStart, localEnd: boundaryDrag.previewEnd }
      : item
  ))
  const sectionStarts = structureSummary?.starts || EMPTY_READER_UNIT_STRUCTURE_ITEMS
  const sectionStartOffsets = useMemo(() => Array.from(new Set([
    ...sectionStarts.map(item => item.section.start.offset),
    ...(typeof structureDraftOffset === 'number' ? [structureDraftOffset] : []),
  ])).filter(offset => offset >= 0 && offset <= unit.text.length), [
    sectionStarts,
    structureDraftOffset,
    unit.text.length,
  ])
  const annotationLayout = getReaderTextAnnotationLayout(unit.text, [
    ...layerTextAnnotations,
    ...characterTextAnnotations,
  ], sectionStartOffsets)
  const tweezedReading = tweezedReadingId
    ? [...characterTextAnnotations, ...layerTextAnnotations].find(item => item.id === tweezedReadingId)
    : undefined

  const moveBoundaryReadout = (clientX: number, clientY: number) => {
    if (!boundaryReadoutRef.current) {
      return
    }
    boundaryReadoutRef.current.style.left = `${clientX + 14}px`
    boundaryReadoutRef.current.style.top = `${clientY - 32}px`
  }

  const boundaryDragActive = boundaryDrag !== null
  useEffect(() => {
    if (!boundaryDragActive) {
      return
    }

    const applyPendingPointer = () => {
      boundaryFrameRef.current = null
      const point = pendingPointerRef.current
      pendingPointerRef.current = null
      const current = boundaryDragRef.current
      if (!point || !current) {
        return
      }
      const offset = resolveLectioRowOffsetFromPoint(unit.rowId, point.x, point.y)
      let previewStart = current.previewStart
      let previewEnd = current.previewEnd
      if (offset !== null) {
        if (current.edge === 'start') {
          previewStart = Math.max(0, Math.min(current.previewEnd - 1, offset))
        } else {
          previewEnd = Math.max(current.previewStart + 1, Math.min(unit.text.length, offset))
        }
      }
      const next: LectioBoundaryDrag = {
        ...current,
        previewStart,
        previewEnd,
        pointerX: point.x,
        pointerY: point.y,
      }
      boundaryDragRef.current = next
      if (previewStart !== current.previewStart || previewEnd !== current.previewEnd) {
        setBoundaryDrag(next)
      }
    }

    const handlePointerMove = (event: PointerEvent) => {
      const current = boundaryDragRef.current
      if (!current) {
        return
      }
      const x = Number.isFinite(event.clientX) ? event.clientX : current.pointerX
      const y = Number.isFinite(event.clientY) ? event.clientY : current.pointerY
      moveBoundaryReadout(x, y)
      pendingPointerRef.current = { x, y }
      if (boundaryFrameRef.current === null) {
        boundaryFrameRef.current = window.requestAnimationFrame(applyPendingPointer)
      }
    }

    const clearFrame = () => {
      if (boundaryFrameRef.current !== null) {
        window.cancelAnimationFrame(boundaryFrameRef.current)
        boundaryFrameRef.current = null
      }
    }

    const handlePointerUp = () => {
      clearFrame()
      if (pendingPointerRef.current) {
        applyPendingPointer()
      }
      const current = boundaryDragRef.current
      boundaryDragRef.current = null
      pendingPointerRef.current = null
      setBoundaryDrag(null)
      boundaryDragHostRef.current?.classList.remove('lectio-boundary-dragging')
      boundaryDragHostRef.current = null
      if (!current || (
        current.previewStart === current.originalStart &&
        current.previewEnd === current.originalEnd
      )) {
        return
      }
      onBoundaryResize?.({
        op: 'resize',
        sourceKind: current.sourceKind,
        evidenceId: current.readingId,
        start: current.previewStart,
        end: current.previewEnd,
        sourceText: unit.text,
      }, unit.rowLabel)
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') {
        return
      }
      event.preventDefault()
      event.stopPropagation()
      clearFrame()
      boundaryDragRef.current = null
      pendingPointerRef.current = null
      setBoundaryDrag(null)
      boundaryDragHostRef.current?.classList.remove('lectio-boundary-dragging')
      boundaryDragHostRef.current = null
    }

    window.addEventListener('pointermove', handlePointerMove)
    window.addEventListener('pointerup', handlePointerUp)
    window.addEventListener('keydown', handleKeyDown, true)
    return () => {
      clearFrame()
      boundaryDragHostRef.current?.classList.remove('lectio-boundary-dragging')
      window.removeEventListener('pointermove', handlePointerMove)
      window.removeEventListener('pointerup', handlePointerUp)
      window.removeEventListener('keydown', handleKeyDown, true)
    }
  }, [boundaryDragActive, onBoundaryResize, unit.rowId, unit.rowLabel, unit.text])

  const renderDraftableSegment = (start: number, end: number): React.ReactNode => {
    const segmentText = unit.text.slice(start, end)
    let cursor = start
    return segmentText.split(/(\s+)/u).filter(Boolean).map((part, index) => {
      const partStart = cursor
      cursor += part.length
      if (/^\s+$/u.test(part)) {
        return <React.Fragment key={`draft-space:${partStart}:${index}`}>{part}</React.Fragment>
      }
      const draftEvidenceId = `draft:${unit.rowId}:${partStart}:${partStart + part.length}`
      return (
        <button
          key={`draft-word:${partStart}:${index}`}
          type="button"
          className="lectio-bare-word"
          aria-label={`draft word ${part}`}
          aria-haspopup="menu"
          aria-expanded={openEvidenceMenuId === draftEvidenceId}
          onClick={event => {
            event.stopPropagation()
            onFocus(unit, 'reader')
            onDraftBareWord?.(unit.rowId, partStart, partStart + part.length, part, event.currentTarget)
          }}
        >{part}</button>
      )
    })
  }

  const renderRapidMarkableSegment = (start: number, end: number): React.ReactNode => {
    const segmentText = unit.text.slice(start, end)
    let cursor = start
    return segmentText.split(/(\s+)/u).filter(Boolean).map((part, index) => {
      const partStart = cursor
      cursor += part.length
      if (/^\s+$/u.test(part)) {
        return <React.Fragment key={`rapid-space:${partStart}:${index}`}>{part}</React.Fragment>
      }
      const isPendingStart = rapidMarking?.pendingSpan?.rowId === unit.rowId &&
        rapidMarking.pendingSpan.start === partStart && rapidMarking.pendingSpan.end === partStart + part.length
      const tool = rapidMarking?.tool || 'coref'
      const sectionBoundaryAction = rapidMarking?.sectionBoundaryAction || 'start'
      const action = tool === 'create-character'
        ? `Create character from first occurrence ${part}`
        : tool === 'section'
          ? sectionBoundaryAction === 'end'
            ? `End ${rapidMarking?.sectionTypeLabel || 'Section'} section after ${part}`
            : `Start section at ${part}`
        : tool === 'coref'
          ? `Tag ${part} as coreference for ${rapidMarking?.characterLabel || 'the selected character'}`
          : `${rapidMarking?.pendingSpan ? 'Finish' : 'Start'} ${tool} span at ${part}`
      return (
        <button
          key={`rapid-word:${partStart}:${index}`}
          type="button"
          className={['lectio-rapid-mark-word', isPendingStart ? 'pending-start' : ''].filter(Boolean).join(' ')}
          data-rapid-tool={tool}
          aria-label={action}
          onClick={event => {
            event.stopPropagation()
            onFocus(unit, 'reader')
            onRapidMarkWord?.(unit.rowId, partStart, partStart + part.length, part, event.currentTarget)
          }}
        >{part}</button>
      )
    })
  }

  const openCharacterOccurrenceMenu = (
    item: Extract<ReaderTextAnnotation, { kind: 'character' }>,
    anchor: HTMLElement,
  ) => {
    onOpenEvidenceMenu?.({
      sourceKind: 'occurrence',
      evidenceId: item.id,
      characterId: item.entry.id,
      referentCharacterIds: item.referentCharacterIds,
      rowId: unit.rowId,
      rowLabel: unit.rowLabel,
      start: item.localStart,
      end: item.localEnd,
      text: unit.text.slice(item.localStart, item.localEnd),
      standing: item.standing,
      mentionType: item.mentionType,
    }, anchor)
  }

  const renderAnnotationTrigger = (item: ReaderTextAnnotation): React.ReactNode => {
    if (item.kind === 'layer') {
      const layer = item.layer
      const researchNoteTarget: AnnotationResearchNoteTarget = {
        kind: layer.kind,
        annotationId: layer.id,
        ...(layer.kind === 'speaker' && layer.speechTurnId ? { logicalId: layer.speechTurnId } : {}),
        characterId: layer.characterId,
        annotationLabel: unit.text.slice(item.localStart, item.localEnd),
      }
      const researchNoteCount = getResearchNoteCount?.(researchNoteTarget) || 0
      const isSelectedCharacterLayer = selectedCharacterId === layer.characterId
      const layerColor = layer.kind === 'speaker'
        ? layer.color
        : selectedCharacterId && isSelectedCharacterLayer
        ? 'var(--cte-ink)'
        : layer.color
      const ruleWidth = layer.ruleStyle === 'double' ? '4px' : '2px'
      return (
        <button
          key={`trigger:${layer.id}:${item.localStart}:${item.localEnd}`}
          type="button"
          className={[
            'cte-layer-anchor',
            'lectio-evidence-trigger',
            'lectio-layer-trigger',
            isSelectedCharacterLayer ? 'selected' : '',
            focusedLayerNoteId === layer.id ? 'focused-layer' : '',
          ].filter(Boolean).join(' ')}
          data-layer-kind={layer.kind}
          data-layer-id={layer.id}
          data-character-id={layer.characterId}
          data-chip={layer.chip}
          data-layer-glyph={layer.glyph}
          data-layer-rule={layer.ruleStyle}
          data-layer-voice={layer.sourceVoice}
          data-layer-subtype={layer.subtypeLabel}
          data-standing={layer.standing}
          data-note-count={researchNoteCount > 0 ? researchNoteCount : undefined}
          data-annotation-text={unit.text.slice(item.localStart, item.localEnd)}
          style={{
            '--cte-layer-color': layerColor,
            '--cte-layer-rule-style': layer.ruleStyle,
            '--cte-layer-rule-width': ruleWidth,
          } as React.CSSProperties}
          title={[layer.kind, layer.label, layer.detail].filter(Boolean).join(' · ')}
          aria-label={`${layer.kind} layer for ${layer.characterId}`}
          aria-haspopup={onOpenEvidenceMenu ? 'menu' : undefined}
          aria-expanded={onOpenEvidenceMenu ? openEvidenceMenuId === layer.id : undefined}
          onClick={event => {
            event.stopPropagation()
            onFocus(unit, 'reader')
            if (onOpenEvidenceMenu) {
              onOpenEvidenceMenu({
                sourceKind: 'layer',
                evidenceId: layer.id,
                characterId: layer.characterId,
                rowId: unit.rowId,
                rowLabel: unit.rowLabel,
                start: item.localStart,
                end: item.localEnd,
                text: unit.text.slice(item.localStart, item.localEnd),
                standing: layer.standing,
                layerKind: layer.kind,
                classification: layer.classifications[0],
                classifications: layer.classifications,
                voice: getReaderLayerVoice(layer.note),
                manualCharacterization: readLectioManualCharacterization(
                  layer.note.metadata?.manual_characterization,
                ),
                speakerAnalysis: layer.speakerAnalysis,
                logicalId: layer.kind === 'speaker' ? layer.speechTurnId : undefined,
              }, event.currentTarget)
            } else {
              onSelectLayerAnnotation?.(layer.characterId, unit.rowId, layer.id)
            }
          }}
          onContextMenu={onOpenEvidenceMenu ? event => {
            event.preventDefault()
            event.stopPropagation()
            onFocus(unit, 'reader')
            onOpenEvidenceMenu({
              sourceKind: 'layer',
              evidenceId: layer.id,
              characterId: layer.characterId,
              rowId: unit.rowId,
              rowLabel: unit.rowLabel,
              start: item.localStart,
              end: item.localEnd,
              text: unit.text.slice(item.localStart, item.localEnd),
              standing: layer.standing,
              layerKind: layer.kind,
              classification: layer.classifications[0],
              classifications: layer.classifications,
              voice: getReaderLayerVoice(layer.note),
              manualCharacterization: readLectioManualCharacterization(
                layer.note.metadata?.manual_characterization,
              ),
              speakerAnalysis: layer.speakerAnalysis,
              logicalId: layer.kind === 'speaker' ? layer.speechTurnId : undefined,
            }, event.currentTarget)
          } : undefined}
        />
      )
    }

    const characterLabel = getCharacterDisplayLabel(item.entry)
    const occurrenceId = item.id
    const researchNoteTarget: AnnotationResearchNoteTarget = {
      kind: 'coreference',
      annotationId: occurrenceId,
      characterId: item.entry.id,
      annotationLabel: unit.text.slice(item.localStart, item.localEnd),
    }
    const researchNoteCount = item.mentionType === 'coref'
      ? getResearchNoteCount?.(researchNoteTarget) || 0
      : 0
    const referentLabels = item.referents.map(referent => referent.label)
    const sharedReferenceGradient = buildSharedReferenceGradient(item.referents)
    const occurrenceAriaLabel = item.sharedReference
      ? `Shared reference to ${referentLabels.join(' and ')} (${item.mentionType})`
      : `${characterLabel} character occurrence (${item.mentionType})`
    return (
      <button
        key={`trigger:${item.entry.id}:${item.localStart}:${item.localEnd}`}
        type="button"
        className={[
          'cte-character-anchor',
          'lectio-evidence-trigger',
          'lectio-character-trigger',
          item.mentionType === 'coref' && !item.sharedReference ? 'lectio-ink-only-trigger' : '',
          item.sharedReference ? 'shared-reference' : '',
          item.referentCharacterIds.includes(selectedCharacterId || '') ? 'selected' : '',
          focusedOccurrenceId === occurrenceId ? 'focused-mention' : '',
        ].filter(Boolean).join(' ')}
        data-mention-type={item.mentionType}
        data-chip={item.sharedReference ? getSharedReferenceChip(item.referents) : item.chip}
        data-occurrence-id={occurrenceId}
        data-shared-reference={item.sharedReference ? 'true' : undefined}
        data-referent-character-ids={item.referentCharacterIds.join(' ')}
        data-standing={item.standing}
        data-note-count={researchNoteCount > 0 ? researchNoteCount : undefined}
        data-annotation-text={unit.text.slice(item.localStart, item.localEnd)}
        style={{
          '--cte-character-color': item.color,
          '--lectio-shared-reference-gradient': sharedReferenceGradient,
        } as React.CSSProperties}
        title={[
          item.sharedReference ? `Shared: ${referentLabels.join(' + ')}` : characterLabel,
          item.mentionType,
          item.role,
        ].filter(Boolean).join(' · ')}
        aria-label={occurrenceAriaLabel}
        aria-haspopup={onOpenEvidenceMenu ? 'menu' : undefined}
        aria-expanded={onOpenEvidenceMenu ? openEvidenceMenuId === occurrenceId : undefined}
          onClick={event => {
            event.stopPropagation()
            onFocus(unit, 'reader')
            if (onOpenEvidenceMenu) {
              openCharacterOccurrenceMenu(item, event.currentTarget)
            } else if (onSelectCharacterOccurrence) {
              onSelectCharacterOccurrence(item.entry.id, occurrenceId)
            } else {
            onSelectCharacter?.(item.entry.id)
          }
        }}
        onContextMenu={onOpenEvidenceMenu ? event => {
          event.preventDefault()
          event.stopPropagation()
          onFocus(unit, 'reader')
          openCharacterOccurrenceMenu(item, event.currentTarget)
        } : undefined}
      />
    )
  }

  const isRtlRow = hasArabicText(unit.text)
  const beginBoundaryDrag = (edge: 'start' | 'end', event: React.PointerEvent<HTMLButtonElement>) => {
    if (!tweezedReading || !onBoundaryResize) {
      return
    }
    const drag: LectioBoundaryDrag = {
      readingId: tweezedReading.id,
      sourceKind: tweezedReading.kind === 'character' ? 'occurrence' : 'layer',
      edge,
      originalStart: tweezedReading.localStart,
      originalEnd: tweezedReading.localEnd,
      previewStart: tweezedReading.localStart,
      previewEnd: tweezedReading.localEnd,
      pointerX: Number.isFinite(event.clientX) ? event.clientX : 0,
      pointerY: Number.isFinite(event.clientY) ? event.clientY : 0,
    }
    boundaryDragRef.current = drag
    boundaryDragHostRef.current = event.currentTarget.closest('[data-testid="ledger-safe-tradition-reader"]')
    boundaryDragHostRef.current?.classList.add('lectio-boundary-dragging')
    setBoundaryDrag(drag)
  }

  const nudgeBoundaryWithKeyboard = (edge: 'start' | 'end', delta: number) => {
    if (!tweezedReading || !onBoundaryResize) {
      return
    }
    const start = edge === 'start'
      ? Math.max(0, Math.min(tweezedReading.localEnd - 1, tweezedReading.localStart + delta))
      : tweezedReading.localStart
    const end = edge === 'end'
      ? Math.max(tweezedReading.localStart + 1, Math.min(unit.text.length, tweezedReading.localEnd + delta))
      : tweezedReading.localEnd
    if (start === tweezedReading.localStart && end === tweezedReading.localEnd) {
      return
    }
    onBoundaryResize({
      op: 'resize',
      sourceKind: tweezedReading.kind === 'character' ? 'occurrence' : 'layer',
      evidenceId: tweezedReading.id,
      start,
      end,
      sourceText: unit.text,
    }, unit.rowLabel)
  }

  const renderBoundaryTweezer = (edge: 'start' | 'end', color: string) => (
    <button
      key={`tweezer:${edge}`}
      type="button"
      className={`lectio-tweezer ${edge}`}
      style={{ '--cte-character-color': color } as React.CSSProperties}
      title={`Drag to move the ${edge} of this reading · arrow keys nudge by one letter`}
      aria-label={`Drag ${edge} boundary of the focused reading`}
      onClick={event => event.stopPropagation()}
      onPointerDown={event => {
        event.preventDefault()
        event.stopPropagation()
        beginBoundaryDrag(edge, event)
      }}
      onKeyDown={event => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') {
          return
        }
        event.preventDefault()
        event.stopPropagation()
        /* Visual arrows follow the script: in RTL prose, left is forward. */
        const forward = event.key === 'ArrowLeft' ? (isRtlRow ? 1 : -1) : (isRtlRow ? -1 : 1)
        nudgeBoundaryWithKeyboard(edge, forward)
      }}
    />
  )

  const content = annotationLayout.segments.map(segment => {
    const characterAnnotations = segment.covering.filter(
      (item): item is Extract<ReaderTextAnnotation, { kind: 'character' }> => item.kind === 'character',
    )
    const focalizationAnnotations = segment.covering.filter(
      (item): item is Extract<ReaderTextAnnotation, { kind: 'layer' }> => (
        item.kind === 'layer' && item.layerKind === 'focalization'
      ),
    )
    const speakerAnnotations = segment.covering.filter(
      (item): item is Extract<ReaderTextAnnotation, { kind: 'layer' }> => (
        item.kind === 'layer' && item.layerKind === 'speaker'
      ),
    )
    const characterizationAnnotations = segment.covering.filter(
      (item): item is Extract<ReaderTextAnnotation, { kind: 'layer' }> => (
        item.kind === 'layer' && item.layerKind === 'characterization'
      ),
    )
    const preferred = <T extends ReaderTextAnnotation,>(items: T[], isSelected: (item: T) => boolean): T | undefined => (
      items.find(isSelected) || items.find(item => item.standing === 'admitted') || items.slice().sort((left, right) => (
        (left.localEnd - left.localStart) - (right.localEnd - right.localStart) || left.id.localeCompare(right.id)
      ))[0]
    )
    const character = preferred(characterAnnotations, item => (
      item.referentCharacterIds.includes(selectedCharacterId || '')
    ))
    const speaker = preferred(speakerAnnotations, item => selectedCharacterId === item.characterId)
    const focalization = preferred(focalizationAnnotations, item => selectedCharacterId === item.characterId)
    const characterization = preferred(characterizationAnnotations, item => selectedCharacterId === item.characterId)
    /* Two hands claiming the very same span is the conflict the editor must rule on. */
    const conflict = characterAnnotations.some((left, leftIndex) => (
      characterAnnotations.some((right, rightIndex) => (
        rightIndex > leftIndex &&
        left.id !== right.id &&
        left.entry.id !== right.entry.id &&
        left.localStart === right.localStart &&
        left.localEnd === right.localEnd
      ))
    ))
    const hasSelectedCoverage = segment.covering.some(item => (
      item.kind === 'character'
        ? item.referentCharacterIds.includes(selectedCharacterId || '')
        : selectedCharacterId === item.characterId
    ))
    const hasFocusedCoverage = segment.covering.some(item => (
      item.kind === 'character'
        ? focusedOccurrenceId === item.id
        : focusedLayerNoteId === item.id
    ))
    const focalizationWidth = focalization?.layer.ruleStyle === 'double' ? '4px' : '2px'
    const characterizationWidth = characterization?.layer.ruleStyle === 'double' ? '4px' : '2px'
    const sharedReference = Boolean(character?.sharedReference)
    const selectedReferent = character?.referents.find(referent => referent.id === selectedCharacterId)
    const characterColor = selectedReferent?.color || character?.color || 'var(--cte-ink-3)'
    const sharedReferenceGradient = character
      ? buildSharedReferenceGradient(character.referents)
      : buildSharedReferenceGradient([])
    const tweezed = tweezedReadingId
      ? segment.covering.find(item => item.id === tweezedReadingId)
      : undefined
    const tweezerColor = tweezed?.color || 'var(--cte-ink)'
    const segmentText = unit.text.slice(segment.start, segment.end)
    /* Every reading owns its true endpoints. A preferred reading may determine the
       shared body treatment, but it must never erase a concurrent reading's cap. */
    const openingFocalizations = focalizationAnnotations.filter(item => item.localStart === segment.start)
    const closingFocalizations = focalizationAnnotations.filter(item => item.localEnd === segment.end)
    const openingCharacterizations = characterizationAnnotations.filter(item => item.localStart === segment.start)
    const closingCharacterizations = characterizationAnnotations.filter(item => item.localEnd === segment.end)
    const openingSpeakers = speakerAnnotations.filter(item => item.localStart === segment.start)
    const closingSpeakers = speakerAnnotations.filter(item => item.localEnd === segment.end)
    const focalizationOpens = openingFocalizations.length > 0
    const characterizationOpens = openingCharacterizations.length > 0
    const conflictOpens = conflict && characterAnnotations.some(item => item.localStart === segment.start)
    const conflictCloses = conflict && characterAnnotations.some(item => item.localEnd === segment.end)
    const blackLetterIncipit = annotationGrammar === 'black-letter' && (focalizationOpens || characterizationOpens)
      ? segmentText.match(/^(\s*)(\S+)([\s\S]*)$/u)
      : null
    const sourceText = blackLetterIncipit ? (
      <>
        {blackLetterIncipit[1]}
        <span
          className={[
            'lectio-annotation-incipit',
            focalizationOpens ? 'focalization' : '',
            characterizationOpens ? 'characterization' : '',
          ].filter(Boolean).join(' ')}
        >{blackLetterIncipit[2]}</span>
        {blackLetterIncipit[3]}
      </>
    ) : segmentText
    return (
      <React.Fragment key={`segment:${segment.start}:${segment.end}`}>
        {tweezed && tweezed.localStart === segment.start && renderBoundaryTweezer('start', tweezerColor)}
        <span
          className={[
            'lectio-annotation-segment',
            segment.covering.length > 0 ? 'has-annotation' : '',
            character ? 'has-character' : '',
            character?.mentionType === 'name' ? 'mention-name' : '',
            character?.mentionType === 'coref' ? 'mention-coref' : '',
            speaker ? 'cte-layer-speaker' : '',
            focalization ? 'cte-layer-focalization' : '',
            characterization ? 'cte-layer-characterization' : '',
            sharedReference ? 'shared-reference' : '',
            conflict ? 'conflict' : '',
            hasSelectedCoverage ? 'selected-annotation' : '',
            hasFocusedCoverage ? 'focused-annotation' : '',
            characterAnnotations.some(item => focusedOccurrenceId === item.id) ? 'focused-mention' : '',
            [...speakerAnnotations, ...focalizationAnnotations, ...characterizationAnnotations]
              .some(item => focusedLayerNoteId === item.id)
              ? 'focused-layer'
              : '',
          ].filter(Boolean).join(' ')}
          data-occurrence-ids={characterAnnotations.map(item => item.id).join(' ') || undefined}
          data-layer-ids={[...speakerAnnotations, ...focalizationAnnotations, ...characterizationAnnotations]
            .map(item => item.id).join(' ') || undefined}
          data-character-selected={characterAnnotations.some(item => (
            item.referentCharacterIds.includes(selectedCharacterId || '')
          ))
            ? 'true'
            : undefined}
          data-shared-reference={sharedReference ? 'true' : undefined}
          data-referent-character-ids={character?.referentCharacterIds.join(' ') || undefined}
          data-character-standing={character?.standing}
          data-speaker-standing={speaker?.standing}
          data-focalization-standing={focalization?.standing}
          data-characterization-standing={characterization?.standing}
          data-speaker-selected={speaker && selectedCharacterId === speaker.characterId ? 'true' : undefined}
          data-focalization-selected={focalization && selectedCharacterId === focalization.characterId ? 'true' : undefined}
          data-characterization-selected={characterization && selectedCharacterId === characterization.characterId ? 'true' : undefined}
          data-editable-occurrence={character?.mentionType === 'coref' && onOpenEvidenceMenu ? 'true' : undefined}
          data-start={segment.start}
          data-end={segment.end}
          onClick={character?.mentionType === 'coref' && onOpenEvidenceMenu ? event => {
            event.stopPropagation()
            onFocus(unit, 'reader')
            openCharacterOccurrenceMenu(character, event.currentTarget)
          } : undefined}
          onContextMenu={character?.mentionType === 'coref' && onOpenEvidenceMenu ? event => {
            event.preventDefault()
            event.stopPropagation()
            onFocus(unit, 'reader')
            openCharacterOccurrenceMenu(character, event.currentTarget)
          } : undefined}
          style={{
            '--cte-character-color': characterColor,
            '--lectio-shared-reference-gradient': sharedReferenceGradient,
            '--lectio-speaker-color': speaker?.color || 'var(--cte-ink-3)',
            '--lectio-focalization-color': focalization?.color || 'var(--cte-ink-3)',
            '--lectio-focalization-rule-style': focalization?.layer.ruleStyle || 'solid',
            '--lectio-focalization-rule-width': focalizationWidth,
            '--lectio-characterization-color': characterization?.color || 'var(--cte-ink-3)',
            '--lectio-characterization-rule-style': characterization?.layer.ruleStyle || 'solid',
            '--lectio-characterization-rule-width': characterizationWidth,
          } as React.CSSProperties}
        >
          {sectionStartOffsets.includes(segment.start) && (
            <span
              className="lectio-structure-opening-anchor"
              data-section-opening-offset={segment.start}
              aria-hidden="true"
            />
          )}
          {conflictOpens && <span className="lectio-conflict-bracket open" aria-hidden="true" />}
          {openingSpeakers.map(item => (
            <span
              key={`boundary:${item.id}:open`}
              className={[
                'lectio-layer-bracket',
                'speaker',
                'open',
                selectedCharacterId === item.characterId ? 'selected' : '',
              ].filter(Boolean).join(' ')}
              data-layer-id={item.id}
              data-character-id={item.characterId}
              data-standing={item.standing}
              style={{ '--lectio-speaker-color': item.color } as React.CSSProperties}
              aria-hidden="true"
            />
          ))}
          {openingFocalizations.map(item => (
            <span
              key={`boundary:${item.id}:open`}
              className={[
                'lectio-layer-bracket',
                'focalization',
                'open',
                selectedCharacterId === item.characterId ? 'selected' : '',
              ].filter(Boolean).join(' ')}
              data-layer-id={item.id}
              data-character-id={item.characterId}
              data-standing={item.standing}
              aria-hidden="true"
            />
          ))}
          {openingCharacterizations.map(item => (
            <span
              key={`boundary:${item.id}:open`}
              className={[
                'lectio-layer-bracket',
                'characterization',
                'open',
                selectedCharacterId === item.characterId ? 'selected' : '',
              ].filter(Boolean).join(' ')}
              data-layer-id={item.id}
              data-character-id={item.characterId}
              data-standing={item.standing}
              aria-hidden="true"
            />
          ))}
          {rapidMarking && rapidMarking.tool !== 'section' && onRapidMarkWord
            ? renderRapidMarkableSegment(segment.start, segment.end)
            : onDraftBareWord && segment.covering.length === 0
              ? renderDraftableSegment(segment.start, segment.end)
              : sourceText}
          {closingCharacterizations.map(item => (
            <span
              key={`boundary:${item.id}:close`}
              className={[
                'lectio-layer-bracket',
                'characterization',
                'close',
                selectedCharacterId === item.characterId ? 'selected' : '',
              ].filter(Boolean).join(' ')}
              data-layer-id={item.id}
              data-character-id={item.characterId}
              data-standing={item.standing}
              aria-hidden="true"
            />
          ))}
          {closingFocalizations.map(item => (
            <span
              key={`boundary:${item.id}:close`}
              className={[
                'lectio-layer-bracket',
                'focalization',
                'close',
                selectedCharacterId === item.characterId ? 'selected' : '',
              ].filter(Boolean).join(' ')}
              data-layer-id={item.id}
              data-character-id={item.characterId}
              data-standing={item.standing}
              aria-hidden="true"
            />
          ))}
          {closingSpeakers.map(item => (
            <span
              key={`boundary:${item.id}:close`}
              className={[
                'lectio-layer-bracket',
                'speaker',
                'close',
                selectedCharacterId === item.characterId ? 'selected' : '',
              ].filter(Boolean).join(' ')}
              data-layer-id={item.id}
              data-character-id={item.characterId}
              data-standing={item.standing}
              style={{ '--lectio-speaker-color': item.color } as React.CSSProperties}
              aria-hidden="true"
            />
          ))}
          {conflictCloses && <span className="lectio-conflict-bracket close" aria-hidden="true" />}
        </span>
        {segment.covering
          .filter(item => item.localEnd === segment.end)
          .map(renderAnnotationTrigger)}
        {tweezed && tweezed.localEnd === segment.end && renderBoundaryTweezer('end', tweezerColor)}
      </React.Fragment>
    )
  })

  const sectionBoundaryAction = rapidMarking?.tool === 'section'
    ? rapidMarking.sectionBoundaryAction || null
    : null
  const markSectionWordAtPointer = (event: React.MouseEvent<HTMLSpanElement>) => {
    if (!sectionBoundaryAction || !onRapidMarkSectionPoint) {
      return
    }
    event.stopPropagation()
    onFocus(unit, 'reader')
    const resolvedOffset = resolveLectioRowOffsetFromPoint(unit.rowId, event.clientX, event.clientY)
    const target = event.target instanceof Element
      ? event.target.closest<HTMLElement>('[data-start][data-end]')
      : null
    const fallbackOffset = target ? Number(target.dataset.start) : Number.NaN
    const boundary = getLectioWordBoundaryAtOffset(
      unit.text,
      resolvedOffset ?? (Number.isFinite(fallbackOffset) ? fallbackOffset : 0),
    )
    if (boundary) {
      onRapidMarkSectionPoint(unit.rowId, boundary.start, boundary.end, boundary.text)
    }
  }

  const textNode = (
    <span
      ref={element => register(unit.rowId, element)}
      role={showRowLabel ? undefined : 'button'}
      tabIndex={showRowLabel ? undefined : 0}
      aria-label={showRowLabel ? undefined : `${unit.rowLabel} collation unit`}
      aria-current={focused && showMappedRowHighlights ? 'true' : undefined}
      className={[
        'cte-seg',
        showRowLabel ? 'lectio-row-text' : '',
        showMappedRowHighlights && variationTier !== 'silent-run' ? variationTier : '',
        characterAnchors.length > 0 || layerAnnotations.length > 0 ? 'has-characters' : '',
        sectionBoundaryAction ? 'lectio-section-hit-target' : '',
        focused && showMappedRowHighlights ? 'focused' : '',
      ].filter(Boolean).join(' ')}
      dir={hasArabicText(unit.text) ? 'rtl' : 'auto'}
      data-row-id={unit.rowId}
      data-native-source-text={unit.text}
      data-section-boundary-action={sectionBoundaryAction || undefined}
      data-dropped-annotation-count={annotationLayout.droppedCount || undefined}
      onClick={sectionBoundaryAction
        ? markSectionWordAtPointer
        : showRowLabel
          ? undefined
          : () => onSelect(unit, selectFocusLevel)}
      onFocus={showRowLabel ? undefined : () => onFocus(unit, 'reader')}
      onKeyDown={showRowLabel ? undefined : event => {
        if (event.key === ' ') {
          event.preventDefault()
          if (focused) {
            onSelect(unit, selectFocusLevel)
          }
        }
      }}
    >
      {content.length > 0 ? content : unit.text}
    </span>
  )

  const focalizationSummary = unitLayerSummaries?.find(summary => summary.kind === 'focalization')
  const speakerSummary = unitLayerSummaries?.find(summary => summary.kind === 'speaker')
  const primarySpeaker = speakerSummary?.characters[0]
  const primaryFocalizer = focalizationSummary?.characters[0]
  const focalizerOverflow = Math.max(0, (focalizationSummary?.characters.length || 0) - 1)
  const focalizerNames = focalizationSummary?.characters.map(character => character.label).join(', ') || ''
  const sectionEnds = structureSummary?.ends || []
  const sectionBoundaryMode = rapidMarking?.tool === 'section' && Boolean(sectionBoundaryAction && onRapidMarkUnitBoundary)
  const sectionBoundaryType = rapidMarking?.sectionTypeLabel || 'Section'
  const hasUnitMetadataLane = unitLayerSummaries !== undefined || structureSummary !== undefined
  const hasUnitMetadata = Boolean(
    structureDraftEditor || primarySpeaker || primaryFocalizer || sectionStarts.length > 0 || sectionEnds.length > 0,
  )

  return (
    <>
      {unit.folioMarkersBefore?.map(marker => (
        <span key={`${unit.rowId}:${marker.marker}`} className="cte-folio">{marker.marker}</span>
      ))}
      {showRowLabel ? (
        <div
          className={[
            'lectio-row-block',
            hasUnitMetadataLane ? 'has-unit-metadata-lane' : '',
            sectionBoundaryMode ? 'section-boundary-ready' : '',
            focused ? 'cursor-row' : '',
            lineApparatusOpen ? 'apparatus-open' : '',
          ].filter(Boolean).join(' ')}
          data-row-block={unit.rowId}
        >
          <span className="lectio-row-label">
            {onToggleLineApparatus ? (
              <button
                type="button"
                aria-label={sectionBoundaryMode
                  ? sectionBoundaryAction === 'end'
                    ? `End ${sectionBoundaryType} section at ${unit.rowLabel} unit boundary`
                    : `Start section at ${unit.rowLabel} unit boundary`
                  : `${unit.rowLabel} line apparatus`}
                aria-expanded={sectionBoundaryMode ? undefined : lineApparatusOpen}
                onClick={event => {
                  event.stopPropagation()
                  onFocus(unit, 'reader')
                  if (sectionBoundaryMode) {
                    onRapidMarkUnitBoundary?.(unit.rowId)
                  } else {
                    onToggleLineApparatus(unit.rowId)
                  }
                }}
              >
                {unit.rowLabel}
              </button>
            ) : unit.rowLabel}
            {subJudiceCount > 0 && onToggleLineApparatus && (
              <button
                type="button"
                className="lectio-obelus"
                title={`${subJudiceCount} reading${subJudiceCount === 1 ? '' : 's'} sub judice · open the apparatus`}
                aria-label={`${subJudiceCount} reading${subJudiceCount === 1 ? '' : 's'} sub judice in ${unit.rowLabel}`}
                onClick={() => {
                  onFocus(unit, 'reader')
                  onToggleLineApparatus(unit.rowId)
                }}
              >† {subJudiceCount}</button>
            )}
          </span>
          {hasUnitMetadata && (
            <span
              className="lectio-unit-layer-summary"
              data-overlay-visible={focalizationSummary ? String(focalizationSummary.overlayVisible) : undefined}
              data-speaker-overlay-visible={speakerSummary ? String(speakerSummary.overlayVisible) : undefined}
              data-standing={primarySpeaker?.standing || primaryFocalizer?.standing}
            >
              {sectionStartOffsets.map(offset => {
                const startsAtOffset = sectionStarts.filter(item => item.section.start.offset === offset)
                const draftAtOffset = structureDraftEditor && structureDraftOffset === offset
                if (!draftAtOffset && startsAtOffset.length === 0) {
                  return null
                }
                return (
                  <span
                    key={`section-labels:${offset}`}
                    className="lectio-unit-structure-label-group"
                    data-section-label-offset={offset}
                    data-unit-edge={offset === 0 ? 'true' : undefined}
                  >
                    {draftAtOffset ? structureDraftEditor : null}
                    {startsAtOffset.map(({ section, sectionType }) => (
                      <button
                        key={`section-start:${section.id}`}
                        type="button"
                        className="lectio-unit-structure-start"
                        data-note-count={getResearchNoteCount?.({
                          kind: 'section',
                          annotationId: section.id,
                          annotationLabel: section.title || sectionType.label,
                        }) || undefined}
                        aria-label={`${sectionType.label} section begins in ${unit.rowLabel}: ${section.title || 'Untitled'}`}
                        onClick={event => {
                          event.stopPropagation()
                          onFocus(unit, 'reader')
                          onSelectStructureSection?.(section.id)
                        }}
                      >
                        <span>{sectionType.abbreviation} ·</span>
                        <b dir="auto">{section.title || 'untitled'}</b>
                      </button>
                    ))}
                  </span>
                )
              })}
              {primaryFocalizer && focalizationSummary && (
                <span className="lectio-unit-focalization-line" title={`Focalization in ${unit.rowLabel}: ${focalizerNames}`}>
                  <button
                    type="button"
                    className="lectio-unit-layer-primary"
                    data-selected={selectedCharacterId === primaryFocalizer.characterId ? 'true' : undefined}
                    data-standing={primaryFocalizer.standing}
                    aria-label={`Focalization in ${unit.rowLabel}: ${primaryFocalizer.label}, ${primaryFocalizer.spanCount} span${primaryFocalizer.spanCount === 1 ? '' : 's'}, ${primaryFocalizer.standing}`}
                    style={{ '--cte-character-color': primaryFocalizer.color } as React.CSSProperties}
                    onClick={event => {
                      event.stopPropagation()
                      onFocus(unit, 'reader')
                      onSelectUnitLayerSummary?.(unit.rowId, primaryFocalizer)
                    }}
                  >
                    <span aria-hidden="true">foc ·</span>
                    <b dir="auto">{primaryFocalizer.label}</b>
                  </button>
                  {focalizerOverflow > 0 && onToggleLineApparatus && (
                    <button
                      type="button"
                      className="lectio-unit-layer-overflow"
                      aria-label={`${lineApparatusOpen ? 'Close' : 'Open'} ${focalizerOverflow} more focalizer${focalizerOverflow === 1 ? '' : 's'} in ${unit.rowLabel}`}
                      onClick={event => {
                        event.stopPropagation()
                        onFocus(unit, 'reader')
                        onToggleLineApparatus(unit.rowId)
                      }}
                    >+{focalizerOverflow}</button>
                  )}
                </span>
              )}
              {sectionEnds.length > 0 && (
                <span className="lectio-unit-structure-end-group">
                  {sectionEnds.map(({ section, sectionType }) => (
                    <span
                      key={`section-end:${section.id}`}
                      className="lectio-unit-structure-end"
                      title={`${sectionType.label} ends in ${unit.rowLabel}: ${section.title || 'Untitled'}`}
                    >ends · {sectionType.abbreviation}</span>
                  ))}
                </span>
              )}
            </span>
          )}
          {textNode}
          {inlineCandidate}
          {lineApparatusOpen && lineApparatus}
        </div>
      ) : (
        <>{textNode}{' '}</>
      )}
      {boundaryDrag && (() => {
        const delta = boundaryDrag.edge === 'start'
          ? boundaryDrag.previewStart - boundaryDrag.originalStart
          : boundaryDrag.previewEnd - boundaryDrag.originalEnd
        return (
          <div
            ref={boundaryReadoutRef}
            className="lectio-tweezer-readout"
            data-testid="lectio-tweezer-readout"
            style={{ left: boundaryDrag.pointerX + 14, top: boundaryDrag.pointerY - 32 }}
            aria-hidden="true"
          >
            {boundaryDrag.edge} {delta >= 0 ? '+' : ''}{delta} · {boundaryDrag.previewStart}–{boundaryDrag.previewEnd}
          </div>
        )
      })()}
    </>
  )
})

export const LedgerSafeTraditionReaderView: React.FC<LedgerSafeTraditionReaderViewProps> = ({
  characterLayer,
  characterChipMode = 'derived',
  characterIndexEnabled = false,
  characterIndexEntries = [],
  characterIndexControlPanel = null,
  characterOccurrences = [],
  characterLayerNotes = [],
  annotationResearchNotes = [],
  contextLabel,
  initialResumeRowId,
  initialResumeEvidenceId,
  initialResumeEvidenceKind,
  onCreateCharacter,
  onRenameCharacter,
  onDeleteCharacter,
  onAdjudicateCharacterEvidence,
  onEditTextStructure,
  onSaveAnnotationResearchNote,
  onArchiveAnnotationResearchNote,
  onArchiveAnnotationResearchNotesForTarget,
  analyticalTaskSet,
  onCreateAnalyticalCheckpoint,
  onSetAnalyticalWorkstreamState,
  onSetAnalyticalCheckpointState,
  onClose,
  onSelectCharacter,
  rows,
  selectedCharacterId = '',
  sourceWitnessId,
  textStructureLayer,
  textLookupByWitnessId,
  witnesses,
}) => {
  const model = useMemo(
    () => buildLedgerSafeTraditionReaderModel({
      rows,
      witnesses,
      sourceWitnessId,
      textLookupByWitnessId,
    }),
    [rows, sourceWitnessId, textLookupByWitnessId, witnesses],
  )
  const [selectedRowId, setSelectedRowId] = useState<string>(() => (
    initialResumeRowId && model.units.some(unit => unit.rowId === initialResumeRowId)
      ? initialResumeRowId
      : model.units[0]?.rowId || ''
  ))
  const [selectedPeerIndex, setSelectedPeerIndex] = useState(0)
  const [focusLevel, setFocusLevel] = useState<'reader' | 'apparatus'>('reader')
  const [readerSection, setReaderSection] = useState<ReaderSection>(() => (
    characterIndexEnabled ? 'character-index' : 'compare'
  ))
  const [annotationGrammar, setAnnotationGrammar] = useState<LectioAnnotationGrammar>(
    DEFAULT_LECTIO_ANNOTATION_GRAMMAR,
  )
  const [visibleEvidenceKinds, setVisibleEvidenceKinds] = useState<Record<LectioIsolationKind, boolean>>({
    chain: true,
    speaker: true,
    focalization: true,
    characterization: true,
  })
  const [rapidTool, setRapidTool] = useState<LectioRapidTool | null>(null)
  const [rapidSpanAnchor, setRapidSpanAnchor] = useState<LectioRapidSpanAnchor | null>(null)
  const [rapidSectionStart, setRapidSectionStart] = useState<TextSectionBoundary | null>(null)
  const [rapidSectionPhase, setRapidSectionPhase] = useState<LectioRapidSectionPhase>('opening')
  const [rapidSectionTypeId, setRapidSectionTypeId] = useState('')
  const [rapidSectionTitle, setRapidSectionTitle] = useState('')
  const rapidSectionTitleRef = useRef('')
  const [editingStructureSectionId, setEditingStructureSectionId] = useState('')
  const [customSectionTypeOpen, setCustomSectionTypeOpen] = useState(false)
  const [customSectionTypeLabel, setCustomSectionTypeLabel] = useState('')
  const [customSectionTypeAbbreviation, setCustomSectionTypeAbbreviation] = useState('')
  const [structureSaving, setStructureSaving] = useState(false)
  const [rapidCharacterCreationPending, setRapidCharacterCreationPending] = useState(false)
  const [activeMarginalCharacterId, setActiveMarginalCharacterId] = useState('')
  const [showContextualAssistance, setShowContextualAssistance] = useState(false)
  const [adjudicationSession, setAdjudicationSession] = useState<LectioAdjudicationSession | null>(null)
  const [adjudicationNotice, setAdjudicationNotice] = useState('')
  const [showTextualBeings, setShowTextualBeings] = useState(false)
  const [evidenceProjection, setEvidenceProjection] = useState<LectioProjection>('full-text')
  const [isolatedEvidenceKind, setIsolatedEvidenceKind] = useState<LectioIsolationKind | null>(null)
  const [expandedLectioFoldIds, setExpandedLectioFoldIds] = useState<Set<string>>(() => new Set())
  const [openLineApparatusRowIds, setOpenLineApparatusRowIds] = useState<Set<string>>(() => new Set())
  const [inlineCandidate, setInlineCandidate] = useState<LectioInlineCandidate | null>(null)
  const [characterCreationOpen, setCharacterCreationOpen] = useState(false)
  const [characterCreationName, setCharacterCreationName] = useState('')
  const [characterCreationKind, setCharacterCreationKind] = useState<CharacterIndexFigureKind>('person')
  const [characterCreationPending, setCharacterCreationPending] = useState(false)
  const [characterCreationError, setCharacterCreationError] = useState('')
  const [renamingCharacterId, setRenamingCharacterId] = useState('')
  const [characterRenameName, setCharacterRenameName] = useState('')
  const [characterRenameKind, setCharacterRenameKind] = useState<CharacterIndexFigureKind>('person')
  const [characterRenamePending, setCharacterRenamePending] = useState(false)
  const [characterRenameError, setCharacterRenameError] = useState('')
  const [deletingCharacterId, setDeletingCharacterId] = useState('')
  const [characterDeletePending, setCharacterDeletePending] = useState(false)
  const [characterDeleteError, setCharacterDeleteError] = useState('')
  const [focusedCharacterOccurrenceId, setFocusedCharacterOccurrenceId] = useState<string | null>(null)
  const [focusedLayerNoteId, setFocusedLayerNoteId] = useState<string | null>(null)
  const [evidenceMenu, setEvidenceMenu] = useState<LectioEvidenceMenuState | null>(null)
  const [researchNoteEditor, setResearchNoteEditor] = useState<LectioResearchNoteEditorState | null>(null)
  const [alignmentRequest, setAlignmentRequest] = useState<{
    characterId: string
    evidenceId: string
    rowId?: string
    token: number
  } | null>(null)
  const alignmentRequestToken = useRef(0)
  const handledResumeEvidenceSignatureRef = useRef('')
  const pendingMarginalAnchorRef = useRef<LectioMarginalAnchor | null>(null)
  const unitRefs = useRef(new Map<string, HTMLSpanElement>())
  const peerRefs = useRef(new Map<number, HTMLElement>())
  const readerSurfaceRef = useRef<HTMLElement | null>(null)
  const speakerRailHostRef = useRef<HTMLDivElement | null>(null)
  const [speakerRailLayouts, setSpeakerRailLayouts] = useState<ReaderSpeakerTurnRailLayout[]>([])
  const restoreReaderSurfaceFocusRef = useRef(false)
  const characterMarginRef = useRef<HTMLElement | null>(null)
  const evidenceMenuRef = useRef<HTMLDivElement | null>(null)
  const evidenceMenuAnchorRef = useRef<HTMLElement | null>(null)
  const rapidToolBeforeCreationRef = useRef<Exclude<LectioRapidTool, 'create-character' | 'section'> | null>(null)
  const activeCharacterId = activeMarginalCharacterId || selectedCharacterId

  useEffect(() => {
    if (model.units.length === 0) {
      setSelectedRowId('')
      return
    }
    if (!model.units.some(unit => unit.rowId === selectedRowId)) {
      setSelectedRowId(model.units[0].rowId)
      setSelectedPeerIndex(0)
    }
  }, [model.units, selectedRowId])

  useEffect(() => {
    if (initialResumeRowId && model.units.some(unit => unit.rowId === initialResumeRowId)) {
      setSelectedRowId(initialResumeRowId)
      setSelectedPeerIndex(0)
    }
  }, [initialResumeRowId, model.units])

  useEffect(() => {
    if (readerSection === 'character-index' && alignmentRequest) {
      return
    }
    const element = unitRefs.current.get(selectedRowId)
    if (!element || typeof element.scrollIntoView !== 'function') {
      return
    }
    element.scrollIntoView({ block: 'center', behavior: 'smooth' })
  }, [alignmentRequest, readerSection, selectedRowId])

  const selectedUnitIndex = model.units.findIndex(unit => unit.rowId === selectedRowId)
  const boundedUnitIndex = selectedUnitIndex >= 0 ? selectedUnitIndex : 0
  const selectedUnit = model.units[boundedUnitIndex] || null
  const selectedPeers = useMemo(
    () => selectedUnit ? model.apparatusByRowId[selectedUnit.rowId] || [] : [],
    [model.apparatusByRowId, selectedUnit],
  )
  const selectedPeerWitnessIds = useMemo(
    () => new Set(selectedPeers.map(peer => peer.witnessId)),
    [selectedPeers],
  )
  const boundedPeerIndex = selectedPeers.length > 0
    ? Math.max(0, Math.min(selectedPeerIndex, selectedPeers.length - 1))
    : 0
  const selectedPeer = selectedPeers[boundedPeerIndex] || null
  const sourceLabel = model.sourceWitness?.sigla || 'Source'
  const targets = witnesses.filter(witness => witness.id !== sourceWitnessId)
  const effectiveCharacterIndexEntries = useMemo(() => {
    if (!characterIndexEnabled) {
      return []
    }
    const entriesById = new Map<string, CharacterIndexEntry>()
    characterIndexEntries.forEach(entry => {
      if (!entriesById.has(entry.id)) {
        entriesById.set(entry.id, entry)
      }
    })
    return Array.from(entriesById.values())
  }, [characterIndexEnabled, characterIndexEntries])
  const characterDecorationsById = useMemo(
    () => buildCharacterDecorations(effectiveCharacterIndexEntries, characterChipMode),
    [characterChipMode, effectiveCharacterIndexEntries],
  )
  const rowLabelById = useMemo(
    () => new Map(model.units.map(unit => [unit.rowId, unit.rowLabel])),
    [model.units],
  )
  const rowTextById = useMemo(
    () => new Map(model.units.map(unit => [unit.rowId, unit.text])),
    [model.units],
  )
  const rowOrderById = useMemo(
    () => new Map(model.units.map((unit, index) => [unit.rowId, index])),
    [model.units],
  )
  const chapterId = rows.find(row => row.chapterId)?.chapterId || characterLayer?.chapter_id || ''
  const researchBookId = characterLayer?.book_id
  const getResearchNoteCount = useCallback((target: AnnotationResearchNoteTarget): number => (
    getAnnotationResearchNotesForTarget(annotationResearchNotes, target).length
  ), [annotationResearchNotes])
  const textSectionTypeById = useMemo(
    () => new Map((textStructureLayer?.types || []).map(sectionType => [sectionType.id, sectionType])),
    [textStructureLayer?.types],
  )
  const unitStructureSummariesByRowId = useMemo(() => {
    const summaries = new Map<string, ReaderUnitStructureSummary>()
    textStructureLayer?.sections.forEach(section => {
      const sectionType = textSectionTypeById.get(section.typeId)
      if (!sectionType || section.start.witnessId !== sourceWitnessId || section.end.witnessId !== sourceWitnessId) {
        return
      }
      const item = { section, sectionType }
      if (section.id !== editingStructureSectionId) {
        const startSummary = summaries.get(section.start.rowId) || { starts: [], ends: [] }
        startSummary.starts.push(item)
        summaries.set(section.start.rowId, startSummary)
      }
      const endSummary = summaries.get(section.end.rowId) || { starts: [], ends: [] }
      endSummary.ends.push(item)
      summaries.set(section.end.rowId, endSummary)
    })
    summaries.forEach(summary => {
      summary.starts.sort((left, right) => (
        left.section.start.offset - right.section.start.offset ||
        left.sectionType.label.localeCompare(right.sectionType.label)
      ))
      summary.ends.sort((left, right) => (
        left.section.end.offset - right.section.end.offset ||
        left.sectionType.label.localeCompare(right.sectionType.label)
      ))
    })
    return summaries
  }, [editingStructureSectionId, sourceWitnessId, textSectionTypeById, textStructureLayer?.sections])

  useLayoutEffect(() => {
    if (readerSection !== 'character-index') {
      return
    }
    const alignStructureLabels = () => {
      const surface = readerSurfaceRef.current
      if (!surface) {
        return
      }
      surface.querySelectorAll<HTMLElement>('[data-row-block]').forEach(row => {
        const labels = row.querySelectorAll<HTMLElement>('[data-section-label-offset]')
        if (labels.length === 0) {
          return
        }
        const anchorsByOffset = new Map(
          Array.from(row.querySelectorAll<HTMLElement>('[data-section-opening-offset]')).map(anchor => [
            anchor.dataset.sectionOpeningOffset || '',
            anchor,
          ]),
        )
        const rowTop = row.getBoundingClientRect().top
        labels.forEach(label => {
          const anchor = anchorsByOffset.get(label.dataset.sectionLabelOffset || '')
          if (!anchor) {
            return
          }
          const anchorTop = Math.max(0, anchor.getBoundingClientRect().top - rowTop)
          label.style.setProperty('--lectio-section-anchor-top', `${Math.round(anchorTop)}px`)
        })
      })
    }
    alignStructureLabels()
    window.addEventListener('resize', alignStructureLabels)
    const observer = typeof ResizeObserver === 'function'
      ? new ResizeObserver(alignStructureLabels)
      : null
    if (readerSurfaceRef.current) {
      observer?.observe(readerSurfaceRef.current)
    }
    return () => {
      window.removeEventListener('resize', alignStructureLabels)
      observer?.disconnect()
    }
  }, [
    rapidSectionStart?.offset,
    rapidSectionStart?.rowId,
    readerSection,
    textStructureLayer?.sections,
  ])

  useEffect(() => {
    if (!rapidSectionTypeId || textStructureLayer?.types.some(sectionType => sectionType.id === rapidSectionTypeId)) {
      return
    }
    setRapidSectionTypeId('')
  }, [rapidSectionTypeId, textStructureLayer?.types])
  const sourceLayerAnnotationsByRowId = useMemo(
    () => buildReaderLayerAnnotationsByRowId({
      notes: characterIndexEnabled ? characterLayerNotes : [],
      sourceWitnessId,
      rowLabelById,
      rowTextById,
      characterDecorationsById,
    }),
    [characterDecorationsById, characterIndexEnabled, characterLayerNotes, rowLabelById, rowTextById, sourceWitnessId],
  )
  const unitVariationTierByRowId = useMemo(() => {
    const map = new Map<string, UnitVariationTier>()
    model.units.forEach(unit => {
      map.set(unit.rowId, getUnitVariationTier(model.apparatusByRowId[unit.rowId] || []))
    })
    return map
  }, [model.apparatusByRowId, model.units])
  const sourceCharacterAnchorsByRowId = useMemo(() => {
    const anchorsByRowId = buildCharacterAnchorsByRowId(
      effectiveCharacterIndexEntries,
      sourceWitnessId,
      characterChipMode,
    )
    const entryById = new Map(effectiveCharacterIndexEntries.map(entry => [entry.id, entry]))
    const mapped = new Map<string, ReaderCharacterAnchor[]>()
    anchorsByRowId.forEach((anchors, rowId) => {
      const rowAnchorsById = new Map<string, ReaderCharacterAnchor>()
      anchors.forEach(anchor => {
        const occurrence = findCharacterOccurrenceForAnchor(characterOccurrences, anchor.entry.id, anchor.anchor)
        const standing = occurrence
          ? getLectioOccurrenceStanding(occurrence)
          : anchor.entry.status.phase === 'accepted' ? 'admitted' : 'received'
        if (standing !== 'admitted' && standing !== 'received') {
          return
        }
        const primaryEntry = occurrence
          ? entryById.get(occurrence.character_id) || anchor.entry
          : anchor.entry
        const primaryDecoration = characterDecorationsById.get(primaryEntry.id) || {
          chip: anchor.chip,
          color: anchor.color,
        }
        const referentCharacterIds = occurrence
          ? getCharacterOccurrenceReferentIds(occurrence)
          : [primaryEntry.id]
        const referents = referentCharacterIds.map(characterId => {
          const entry = entryById.get(characterId)
          const decoration = characterDecorationsById.get(characterId) || {
            chip: characterId,
            color: getCharacterColor(characterId),
          }
          return {
            id: characterId,
            label: entry ? getCharacterDisplayLabel(entry) : characterId,
            chip: decoration.chip,
            color: decoration.color,
          }
        })
        const nextAnchor: ReaderCharacterAnchor = {
          ...anchor,
          entry: primaryEntry,
          chip: primaryDecoration.chip,
          color: primaryDecoration.color,
          standing,
          referentCharacterIds,
          referents,
          sharedReference: occurrence ? isJointCharacterOccurrence(occurrence) : false,
          ...(occurrence ? { evidenceId: occurrence.occurrence_id } : {}),
        }
        const identity = nextAnchor.evidenceId || getCharacterOccurrenceId(nextAnchor.entry.id, nextAnchor.anchor)
        if (!rowAnchorsById.has(identity)) {
          rowAnchorsById.set(identity, nextAnchor)
        }
      })
      const rowAnchors = Array.from(rowAnchorsById.values())
      if (rowAnchors.length > 0) {
        mapped.set(rowId, rowAnchors)
      }
    })
    return mapped
  }, [
    characterChipMode,
    characterDecorationsById,
    characterOccurrences,
    effectiveCharacterIndexEntries,
    sourceWitnessId,
  ])
  const visibleSourceCharacterAnchorsByRowId = useMemo(() => {
    const filterForCurrentReading = (anchors: readonly ReaderCharacterAnchor[]) => (
      activeCharacterId && isolatedEvidenceKind
        ? anchors.filter(anchor => anchor.referentCharacterIds.includes(activeCharacterId))
        : [...anchors]
    )
    if (isolatedEvidenceKind && isolatedEvidenceKind !== 'chain') {
      return new Map<string, ReaderCharacterAnchor[]>()
    }
    if (!isolatedEvidenceKind && !visibleEvidenceKinds.chain) {
      return new Map<string, ReaderCharacterAnchor[]>()
    }
    const map = new Map<string, ReaderCharacterAnchor[]>()
    sourceCharacterAnchorsByRowId.forEach((anchors, rowId) => {
      const selectedAnchors = filterForCurrentReading(anchors)
      if (selectedAnchors.length > 0) {
        map.set(rowId, selectedAnchors)
      }
    })
    return map
  }, [activeCharacterId, isolatedEvidenceKind, sourceCharacterAnchorsByRowId, visibleEvidenceKinds.chain])
  const visibleSourceLayerAnnotationsByRowId = useMemo(() => {
    if (isolatedEvidenceKind === 'chain') {
      return new Map<string, ReaderLayerAnnotation[]>()
    }
    if (!isolatedEvidenceKind) {
      const map = new Map<string, ReaderLayerAnnotation[]>()
      sourceLayerAnnotationsByRowId.forEach((annotations, rowId) => {
        const visible = annotations.filter(annotation => visibleEvidenceKinds[annotation.kind])
        if (visible.length > 0) {
          map.set(rowId, visible)
        }
      })
      return map
    }
    const map = new Map<string, ReaderLayerAnnotation[]>()
    sourceLayerAnnotationsByRowId.forEach((annotations, rowId) => {
      const selectedAnnotations = annotations.filter(annotation => (
        (!activeCharacterId || annotation.characterId === activeCharacterId) &&
        annotation.kind === isolatedEvidenceKind
      ))
      if (selectedAnnotations.length > 0) {
        map.set(rowId, selectedAnnotations)
      }
    })
    return map
  }, [activeCharacterId, isolatedEvidenceKind, sourceLayerAnnotationsByRowId, visibleEvidenceKinds])
  const sourceCharacterOccurrencesByEntryId = useMemo(() => {
    const map = new Map<string, CharacterRailOccurrence[]>()
    sourceCharacterAnchorsByRowId.forEach(anchors => {
      anchors.forEach(anchor => {
        const rowLabel = rowLabelById.get(anchor.anchor.rowId) || anchor.anchor.rowId
        anchor.referentCharacterIds.forEach(characterId => {
          const bucket = map.get(characterId) || []
          bucket.push({
            id: anchor.evidenceId || getCharacterOccurrenceId(anchor.entry.id, anchor.anchor),
            anchor,
            rowLabel,
            windowLabel: getCharacterAnchorWindowLabel(anchor.anchor, rowLabel),
          })
          map.set(characterId, bucket)
        })
      })
    })
    map.forEach(occurrences => {
      occurrences.sort((left, right) => {
        const rowDelta = (rowOrderById.get(left.anchor.anchor.rowId) ?? Number.MAX_SAFE_INTEGER) -
          (rowOrderById.get(right.anchor.anchor.rowId) ?? Number.MAX_SAFE_INTEGER)
        if (rowDelta !== 0) {
          return rowDelta
        }
        const startDelta = (left.anchor.anchor.localStart ?? Number.MAX_SAFE_INTEGER) -
          (right.anchor.anchor.localStart ?? Number.MAX_SAFE_INTEGER)
        if (startDelta !== 0) {
          return startDelta
        }
        return (left.anchor.anchor.localEnd ?? Number.MAX_SAFE_INTEGER) -
          (right.anchor.anchor.localEnd ?? Number.MAX_SAFE_INTEGER)
      })
    })
    return map
  }, [rowLabelById, rowOrderById, sourceCharacterAnchorsByRowId])
  const sourceLayerAnnotationsByCharacterId = useMemo(() => {
    const map = new Map<string, ReaderLayerAnnotation[]>()
    sourceLayerAnnotationsByRowId.forEach(annotations => {
      annotations.forEach(annotation => {
        const bucket = map.get(annotation.characterId) || []
        bucket.push(annotation)
        map.set(annotation.characterId, bucket)
      })
    })
    map.forEach(annotations => {
      annotations.sort((left, right) => {
        const rowDelta = (rowOrderById.get(left.rowId) ?? Number.MAX_SAFE_INTEGER) -
          (rowOrderById.get(right.rowId) ?? Number.MAX_SAFE_INTEGER)
        if (rowDelta !== 0) {
          return rowDelta
        }
        return left.localStart - right.localStart || left.id.localeCompare(right.id)
      })
    })
    return map
  }, [rowOrderById, sourceLayerAnnotationsByRowId])
  const lectioRowOrder = useMemo(() => model.units.map(unit => unit.rowId), [model.units])
  const allLectioEvidence = useMemo(() => {
    const evidence: LectioEvidenceRecord[] = []
    if (characterOccurrences.length > 0) {
      characterOccurrences.forEach(occurrence => {
        const start = occurrence.locator.localStart ?? -1
        const end = occurrence.locator.localEnd ?? -1
        evidence.push({
          id: occurrence.occurrence_id,
          characterId: occurrence.character_id,
          referentCharacterIds: getCharacterOccurrenceReferentIds(occurrence),
          witnessId: occurrence.locator.witnessId,
          rowId: occurrence.locator.rowId,
          start,
          end,
          kind: normalizeCharacterMentionType(occurrence.metadata?.mention_type || occurrence.metadata?.mentionType) || 'name',
          text: rowTextById.get(occurrence.locator.rowId)?.slice(start, end) || occurrence.label || '',
          sourceKind: 'occurrence',
          standing: getLectioOccurrenceStanding(occurrence),
        })
      })
    } else {
      sourceCharacterOccurrencesByEntryId.forEach(occurrences => {
        occurrences.forEach(occurrence => {
          const { anchor } = occurrence.anchor
          const start = anchor.localStart ?? -1
          const end = anchor.localEnd ?? -1
          evidence.push({
            id: occurrence.id,
            characterId: occurrence.anchor.entry.id,
            referentCharacterIds: occurrence.anchor.referentCharacterIds,
            witnessId: sourceWitnessId,
            rowId: anchor.rowId,
            start,
            end,
            kind: occurrence.anchor.mentionType,
            text: rowTextById.get(anchor.rowId)?.slice(start, end) || occurrence.windowLabel,
            sourceKind: 'occurrence',
            standing: occurrence.anchor.entry.status.phase === 'accepted' ? 'admitted' : 'received',
          })
        })
      })
    }
    characterLayerNotes.forEach(note => {
      const kind = getReaderLayerKind(note)
      const characterId = getReaderLayerCharacterId(note)
      if (!kind || !characterId || note.anchor.kind !== 'span' || note.anchor.locator.witnessId !== sourceWitnessId) {
        return
      }
      const rowText = rowTextById.get(note.anchor.locator.rowId) || ''
      const offsets = resolveReaderLayerOffsets({
        kind,
        localStart: note.anchor.locator.localStart,
        localEnd: note.anchor.locator.localEnd,
        note,
        rowText,
      })
      if (!offsets) {
        return
      }
      const source = asMetadataRecord(note.metadata?.source)
      const subtype = getReaderLayerSubtype(note, kind)
      evidence.push({
        id: note.note_id,
        characterId,
        witnessId: sourceWitnessId,
        rowId: note.anchor.locator.rowId,
        start: offsets.localStart,
        end: offsets.localEnd,
        kind,
        text: rowText.slice(offsets.localStart, offsets.localEnd) || getReaderLayerLabel(note, kind),
        sourceKind: 'layer',
        standing: getLectioLayerStanding(note),
        subtype: subtype.label,
        voice: typeof source.voice === 'string' ? source.voice : 'figure',
        groupId: kind === 'speaker' && typeof note.metadata?.speech_turn_id === 'string'
          ? note.metadata.speech_turn_id
          : undefined,
      })
    })
    return evidence
  }, [
    characterLayerNotes,
    characterOccurrences,
    rowTextById,
    sourceCharacterOccurrencesByEntryId,
    sourceWitnessId,
  ])
  const lectioEvidence = useMemo(() => allLectioEvidence.filter(item => (
    item.standing === 'admitted' || item.standing === 'received'
  )), [allLectioEvidence])
  const lectioRoster = useMemo(() => buildLectioRoster({
    characters: effectiveCharacterIndexEntries.map(entry => ({
      id: entry.id,
      label: getCharacterDisplayLabel(entry),
    })),
    evidence: lectioEvidence,
    rowOrder: lectioRowOrder,
    witnessId: sourceWitnessId,
  }), [effectiveCharacterIndexEntries, lectioEvidence, lectioRowOrder, sourceWitnessId])
  const lectioRosterById = useMemo(
    () => new Map(lectioRoster.map(entry => [entry.id, entry])),
    [lectioRoster],
  )
  const textualBeingCardsById = useMemo(() => new Map(buildTextualBeingCards({
    characters: effectiveCharacterIndexEntries.map(entry => ({
      id: entry.id,
      label: getCharacterDisplayLabel(entry),
    })),
    evidence: lectioEvidence,
    rowOrder: lectioRowOrder,
    witnessId: sourceWitnessId,
  }).map(card => [card.id, card])), [
    effectiveCharacterIndexEntries,
    lectioEvidence,
    lectioRowOrder,
    sourceWitnessId,
  ])
  const characterMarginEntries = useMemo(() => {
    const entryById = new Map(effectiveCharacterIndexEntries.map(entry => [entry.id, entry]))
    return lectioRoster
      .map(item => entryById.get(item.id))
      .filter((entry): entry is CharacterIndexEntry => Boolean(entry))
  }, [effectiveCharacterIndexEntries, lectioRoster])
  const unitLayerSummariesByRowId = useMemo(() => {
    const summaries = new Map<string, ReaderUnitLayerSummary[]>()
    if (isolatedEvidenceKind === 'chain' || isolatedEvidenceKind === 'characterization') {
      return summaries
    }

    const entryById = new Map(effectiveCharacterIndexEntries.map(entry => [entry.id, entry]))
    const rosterOrderById = new Map(characterMarginEntries.map((entry, index) => [entry.id, index]))
    sourceLayerAnnotationsByRowId.forEach((annotations, rowId) => {
      const rowSummaries = (['speaker', 'focalization'] as const).flatMap(kind => {
        if (isolatedEvidenceKind && isolatedEvidenceKind !== kind) {
          return []
        }
        const annotationsByCharacter = new Map<string, ReaderLayerAnnotation[]>()
        annotations
          .filter(annotation => (
            annotation.kind === kind &&
            (!activeCharacterId || isolatedEvidenceKind !== kind || annotation.characterId === activeCharacterId)
          ))
          .forEach(annotation => {
            const bucket = annotationsByCharacter.get(annotation.characterId) || []
            bucket.push(annotation)
            annotationsByCharacter.set(annotation.characterId, bucket)
          })

        const characters: ReaderUnitLayerSummaryCharacter[] = Array.from(annotationsByCharacter.entries())
          .map(([characterId, items]) => {
            const orderedItems = [...items].sort((left, right) => (
              left.localStart - right.localStart || left.localEnd - right.localEnd
            ))
            const firstItem = orderedItems[0]
            const entry = entryById.get(characterId)
            const standing: LectioEvidenceStanding = orderedItems.some(item => item.standing === 'admitted')
              ? 'admitted'
              : 'received'
            return {
              characterId,
              label: entry ? getCharacterDisplayLabel(entry) : characterId,
              chip: firstItem?.chip || characterId,
              color: firstItem?.color || getCharacterColor(characterId),
              standing,
              spanCount: orderedItems.length,
              firstNoteId: firstItem?.id || '',
            }
          })
          .sort((left, right) => {
            const activeDifference = Number(right.characterId === activeCharacterId) - Number(left.characterId === activeCharacterId)
            if (activeDifference !== 0) {
              return activeDifference
            }
            const standingDifference = Number(right.standing === 'admitted') - Number(left.standing === 'admitted')
            if (standingDifference !== 0) {
              return standingDifference
            }
            const rosterDifference = (rosterOrderById.get(left.characterId) ?? Number.MAX_SAFE_INTEGER) -
              (rosterOrderById.get(right.characterId) ?? Number.MAX_SAFE_INTEGER)
            return rosterDifference || left.label.localeCompare(right.label)
          })

        return characters.length > 0 ? [{
          kind,
          overlayVisible: isolatedEvidenceKind === kind || visibleEvidenceKinds[kind],
          characters,
        }] : []
      })

      if (rowSummaries.length > 0) {
        summaries.set(rowId, rowSummaries)
      }
    })
    return summaries
  }, [
    activeCharacterId,
    characterMarginEntries,
    effectiveCharacterIndexEntries,
    isolatedEvidenceKind,
    sourceLayerAnnotationsByRowId,
    visibleEvidenceKinds,
  ])
  const speakerTurnRails = useMemo(() => buildReaderSpeakerTurnRails({
    activeCharacterId,
    annotationsByRowId: sourceLayerAnnotationsByRowId,
    entries: effectiveCharacterIndexEntries,
    isolatedEvidenceKind,
    overlayVisible: isolatedEvidenceKind === 'speaker' || visibleEvidenceKinds.speaker,
    rowOrderById,
  }), [
    activeCharacterId,
    effectiveCharacterIndexEntries,
    isolatedEvidenceKind,
    rowOrderById,
    sourceLayerAnnotationsByRowId,
    visibleEvidenceKinds.speaker,
  ])
  const resolveSpeakerLevelForRange = useCallback((range: ReaderPositionRange): number => (
    inferSpeakerLevelForRange(speakerTurnRails, range, rowOrderById)
  ), [rowOrderById, speakerTurnRails])

  useLayoutEffect(() => {
    const host = speakerRailHostRef.current
    if (!host || speakerTurnRails.length === 0 || readerSection !== 'character-index') {
      setSpeakerRailLayouts(current => current.length === 0 ? current : [])
      return
    }

    const alignSpeakerRails = () => {
      const hostRect = host.getBoundingClientRect()
      const measured = speakerTurnRails.flatMap(turn => {
        const startRow = unitRefs.current.get(turn.startRowId)?.closest<HTMLElement>('[data-row-block]')
        const endRow = unitRefs.current.get(turn.endRowId)?.closest<HTMLElement>('[data-row-block]')
        if (!startRow || !endRow) {
          return []
        }
        const startRect = startRow.getBoundingClientRect()
        const endRect = endRow.getBoundingClientRect()
        const top = Math.max(0, startRect.top - hostRect.top + host.scrollTop + 2)
        const bottom = Math.max(top + 18, endRect.bottom - hostRect.top + host.scrollTop - 2)
        return [{ ...turn, top, height: bottom - top, labelOffset: 0 }]
      })
      const entryLabelTops: number[] = []
      const labelOffsetByTurnId = new Map<string, number>()
      measured
        .filter(turn => turn.labelPlacement === 'entry')
        .sort((left, right) => left.top - right.top || left.level - right.level || left.turnId.localeCompare(right.turnId))
        .forEach(turn => {
          let labelTop = turn.top
          let overlapsLabel = true
          while (overlapsLabel) {
            overlapsLabel = false
            for (const occupiedTop of entryLabelTops) {
              if (Math.abs(labelTop - occupiedTop) < 18) {
                overlapsLabel = true
                break
              }
            }
            if (!overlapsLabel) {
              break
            }
            labelTop += 18
          }
          entryLabelTops.push(labelTop)
          labelOffsetByTurnId.set(turn.turnId, labelTop - turn.top)
        })
      const next = measured.map(turn => ({
        ...turn,
        labelOffset: labelOffsetByTurnId.get(turn.turnId) || 0,
      }))
      setSpeakerRailLayouts(current => {
        const unchanged = current.length === next.length && current.every((layout, index) => {
          const candidate = next[index]
          return candidate &&
            layout.turnId === candidate.turnId &&
            layout.top === candidate.top &&
            layout.height === candidate.height &&
            layout.labelOffset === candidate.labelOffset &&
            layout.level === candidate.level &&
            layout.labelPlacement === candidate.labelPlacement &&
            layout.overlayVisible === candidate.overlayVisible &&
            layout.characterId === candidate.characterId &&
            layout.label === candidate.label &&
            layout.color === candidate.color &&
            layout.standing === candidate.standing &&
            layout.sourceTurnIds.join('\u0000') === candidate.sourceTurnIds.join('\u0000')
        })
        return unchanged ? current : next
      })
    }

    alignSpeakerRails()
    window.addEventListener('resize', alignSpeakerRails)
    const observer = typeof ResizeObserver === 'function'
      ? new ResizeObserver(alignSpeakerRails)
      : null
    observer?.observe(host)
    return () => {
      window.removeEventListener('resize', alignSpeakerRails)
      observer?.disconnect()
    }
  }, [readerSection, speakerTurnRails])
  const subJudiceCharacterCount = allLectioEvidence.filter(item => item.standing === 'subjudice').length
  const subJudiceCountByRowId = useMemo(() => {
    const map = new Map<string, number>()
    allLectioEvidence.forEach(item => {
      if (item.standing === 'subjudice' && item.witnessId === sourceWitnessId) {
        map.set(item.rowId, (map.get(item.rowId) || 0) + 1)
      }
    })
    return map
  }, [allLectioEvidence, sourceWitnessId])
  /* The reading whose boundaries carry drag tweezers: the focused reading of the
     attended figure during adjudication, as long as it is placed in the text. */
  const tweezedReading = useMemo(() => {
    if (!adjudicationSession || !activeCharacterId) {
      return null
    }
    const focusedId = focusedCharacterOccurrenceId || focusedLayerNoteId
    if (!focusedId) {
      return null
    }
    return allLectioEvidence.find(item => (
      item.id === focusedId &&
      lectioEvidenceRefersTo(item, activeCharacterId) &&
      item.witnessId === sourceWitnessId &&
      item.standing !== 'relegated' &&
      item.standing !== 'subjudice' &&
      item.start >= 0 &&
      item.end > item.start
    )) || null
  }, [
    activeCharacterId,
    adjudicationSession,
    allLectioEvidence,
    focusedCharacterOccurrenceId,
    focusedLayerNoteId,
    sourceWitnessId,
  ])
  const canShowCharacterIndex = characterIndexEnabled
  const showMappedRowHighlights = readerSection === 'compare'

  useEffect(() => {
    if (selectedPeerIndex !== boundedPeerIndex) {
      setSelectedPeerIndex(boundedPeerIndex)
    }
  }, [boundedPeerIndex, selectedPeerIndex])

  useEffect(() => {
    if (!canShowCharacterIndex && readerSection === 'character-index') {
      setReaderSection('read')
    }
  }, [canShowCharacterIndex, readerSection])

  useEffect(() => {
    if (!restoreReaderSurfaceFocusRef.current || readerSection !== 'character-index') {
      return
    }
    const reader = readerSurfaceRef.current
    if (reader) {
      reader.focus({ preventScroll: true })
      restoreReaderSurfaceFocusRef.current = false
    }
  }, [evidenceProjection, inlineCandidate, readerSection, showContextualAssistance, showTextualBeings])

  useEffect(() => {
    /* The keyboard grammar is scoped to the reader, so the stage must hold focus from
       the first paint — otherwise every entry starts with a dead keyboard. */
    readerSurfaceRef.current?.focus({ preventScroll: true })
  }, [readerSection])

  useEffect(() => {
    if (focusLevel !== 'apparatus') {
      return
    }
    const element = peerRefs.current.get(boundedPeerIndex)
    if (!element || typeof element.scrollIntoView !== 'function') {
      return
    }
    element.scrollIntoView({ block: 'center', behavior: 'smooth' })
  }, [boundedPeerIndex, focusLevel, selectedRowId])

  const selectUnit = useEventCallback((unit: LedgerSafeTraditionUnit, nextFocusLevel: 'reader' | 'apparatus' = focusLevel) => {
    setSelectedRowId(unit.rowId)
    setSelectedPeerIndex(0)
    setFocusLevel(nextFocusLevel)
  })

  const clearCharacterSelection = useCallback(() => {
    onSelectCharacter?.('')
    setActiveMarginalCharacterId('')
    setShowContextualAssistance(false)
    setAdjudicationSession(null)
    setAdjudicationNotice('')
    setOpenLineApparatusRowIds(new Set())
    setEvidenceProjection('full-text')
    setIsolatedEvidenceKind(null)
    setExpandedLectioFoldIds(new Set())
    setInlineCandidate(null)
    setAlignmentRequest(null)
    pendingMarginalAnchorRef.current = null
    setFocusedCharacterOccurrenceId(null)
    setFocusedLayerNoteId(null)
    setRapidTool(null)
    setRapidSpanAnchor(null)
    setRapidSectionStart(null)
    setRapidSectionPhase('opening')
    setRapidSectionTypeId('')
    setEditingStructureSectionId('')
  }, [onSelectCharacter])

  const selectCharacterInReader = useEventCallback((characterId: string, occurrenceId: string | null = null) => {
    const isSameCharacter = activeCharacterId === characterId
    const isSameOccurrence = occurrenceId && focusedCharacterOccurrenceId === occurrenceId
    const isCharacterToggle = isSameCharacter && !occurrenceId
    if (isCharacterToggle || isSameOccurrence) {
      clearCharacterSelection()
      return
    }
    if (!isSameCharacter) {
      setAdjudicationSession(null)
    }
    setActiveMarginalCharacterId(characterId)
    setShowTextualBeings(false)
    onSelectCharacter?.(characterId)
    setFocusedCharacterOccurrenceId(occurrenceId)
    setFocusedLayerNoteId(null)
  })

  const selectLayerAnnotationInReader = useEventCallback((characterId: string, rowId: string, noteId: string) => {
    if (readerSection !== 'character-index') {
      const unit = model.units.find(candidate => candidate.rowId === rowId)
      if (unit) {
        selectUnit(unit, 'apparatus')
      }
    }
    if (activeCharacterId !== characterId) {
      setAdjudicationSession(null)
    }
    setActiveMarginalCharacterId(characterId)
    setShowTextualBeings(false)
    onSelectCharacter?.(characterId)
    setFocusedCharacterOccurrenceId(null)
    setFocusedLayerNoteId(noteId)
  })

  const selectUnitLayerSummary = useCallback((
    rowId: string,
    summary: ReaderUnitLayerSummaryCharacter,
  ) => {
    setVisibleEvidenceKinds(current => current.focalization
      ? current
      : { ...current, focalization: true })
    setEvidenceProjection('full-text')
    selectLayerAnnotationInReader(summary.characterId, rowId, summary.firstNoteId)
    setAdjudicationNotice(`${rowLabelById.get(rowId) || rowId} · foc · ${summary.label}`)
  }, [rowLabelById, selectLayerAnnotationInReader])

  const selectMarginalCharacter = useCallback((
    characterId: string,
    marginalButton: HTMLElement | null = null,
    requestedEvidenceId = '',
  ) => {
    const marginName = marginalButton?.querySelector<HTMLElement>('[data-lectio-character-name]') || marginalButton
    const margin = characterMarginRef.current
    if (marginName && margin) {
      const rect = marginName.getBoundingClientRect()
      pendingMarginalAnchorRef.current = {
        characterId,
        scrollTop: margin.scrollTop,
        viewportY: rect.top + rect.height / 2,
      }
    } else {
      pendingMarginalAnchorRef.current = null
    }

    const targetEvidenceId = requestedEvidenceId || lectioRosterById.get(characterId)?.targetEvidenceId || ''
    const occurrence = (sourceCharacterOccurrencesByEntryId.get(characterId) || [])
      .find(item => item.id === targetEvidenceId) || null
    const layer = (sourceLayerAnnotationsByCharacterId.get(characterId) || [])
      .find(item => item.id === targetEvidenceId) || null
    const rowId = occurrence?.anchor.anchor.rowId || layer?.rowId || ''
    const unit = model.units.find(candidate => candidate.rowId === rowId)

    if (activeCharacterId !== characterId) {
      setAdjudicationSession(null)
      setAdjudicationNotice('')
    }
    setActiveMarginalCharacterId(characterId)
    setShowContextualAssistance(false)
    setShowTextualBeings(false)
    setEvidenceProjection('full-text')
    setIsolatedEvidenceKind(null)
    setExpandedLectioFoldIds(new Set())
    onSelectCharacter?.(characterId)
    setFocusedCharacterOccurrenceId(occurrence?.id || null)
    setFocusedLayerNoteId(occurrence ? null : layer?.id || null)
    if (targetEvidenceId) {
      alignmentRequestToken.current += 1
      setAlignmentRequest({
        characterId,
        evidenceId: targetEvidenceId,
        token: alignmentRequestToken.current,
      })
    } else {
      setAlignmentRequest(null)
    }
    if (unit) {
      selectUnit(unit, 'reader')
    }
  }, [
    activeCharacterId,
    model.units,
    lectioRosterById,
    onSelectCharacter,
    selectUnit,
    sourceCharacterOccurrencesByEntryId,
    sourceLayerAnnotationsByCharacterId,
  ])

  useEffect(() => {
    if (!initialResumeEvidenceId || !activeCharacterId || readerSection !== 'character-index') {
      return
    }
    const signature = [activeCharacterId, initialResumeEvidenceKind || '', initialResumeEvidenceId].join('::')
    if (handledResumeEvidenceSignatureRef.current === signature) {
      return
    }
    const occurrenceExists = (sourceCharacterOccurrencesByEntryId.get(activeCharacterId) || [])
      .some(item => item.id === initialResumeEvidenceId)
    const layerExists = (sourceLayerAnnotationsByCharacterId.get(activeCharacterId) || [])
      .some(item => item.id === initialResumeEvidenceId)
    const targetExists = initialResumeEvidenceKind === 'coreference'
      ? occurrenceExists
      : initialResumeEvidenceKind === 'focalization' || initialResumeEvidenceKind === 'characterization'
        ? layerExists
        : occurrenceExists || layerExists
    if (!targetExists) {
      return
    }
    handledResumeEvidenceSignatureRef.current = signature
    selectMarginalCharacter(activeCharacterId, null, initialResumeEvidenceId)
  }, [
    activeCharacterId,
    initialResumeEvidenceId,
    initialResumeEvidenceKind,
    readerSection,
    selectMarginalCharacter,
    sourceCharacterOccurrencesByEntryId,
    sourceLayerAnnotationsByCharacterId,
  ])

  useEffect(() => {
    if (!alignmentRequest || readerSection !== 'character-index') {
      return
    }
    const reader = readerSurfaceRef.current
    const margin = characterMarginRef.current
    if (!reader || !margin) {
      return
    }
    const occurrenceSegment = Array.from(reader.querySelectorAll<HTMLElement>('[data-occurrence-ids]'))
      .find(element => element.dataset.occurrenceIds?.split(' ').includes(alignmentRequest.evidenceId))
    const layerSegment = Array.from(reader.querySelectorAll<HTMLElement>('[data-layer-ids]'))
      .find(element => element.dataset.layerIds?.split(' ').includes(alignmentRequest.evidenceId))
    const occurrenceTrigger = Array.from(reader.querySelectorAll<HTMLElement>('[data-occurrence-id]'))
      .find(element => element.dataset.occurrenceId === alignmentRequest.evidenceId)
    const layerTrigger = Array.from(reader.querySelectorAll<HTMLElement>('[data-layer-id]'))
      .find(element => element.dataset.layerId === alignmentRequest.evidenceId)
    /* Sub judice readings never mark the line; their walk stop aligns on the row block. */
    const rowBlockFallback = alignmentRequest.rowId ? unitRefs.current.get(alignmentRequest.rowId) || null : null
    const target = occurrenceSegment || layerSegment || occurrenceTrigger || layerTrigger || rowBlockFallback
    const marginEntry = Array.from(margin.querySelectorAll<HTMLElement>('[data-character-index-entry]'))
      .find(element => element.dataset.characterIndexEntry === alignmentRequest.characterId)
    const marginName = marginEntry?.querySelector<HTMLElement>('[data-lectio-character-name]') || null
    if (!target || !marginName) {
      return
    }

    const anchorSnapshot = pendingMarginalAnchorRef.current?.characterId === alignmentRequest.characterId
      ? pendingMarginalAnchorRef.current
      : null
    if (anchorSnapshot) {
      margin.scrollTop = anchorSnapshot.scrollTop
      const restoredNameRect = marginName.getBoundingClientRect()
      const restoredNameCenter = restoredNameRect.top + restoredNameRect.height / 2
      const maxMarginScrollTop = Math.max(0, margin.scrollHeight - margin.clientHeight)
      margin.scrollTop = Math.max(0, Math.min(
        maxMarginScrollTop,
        margin.scrollTop + restoredNameCenter - anchorSnapshot.viewportY,
      ))
    }

    const targetRects = typeof target.getClientRects === 'function' ? target.getClientRects() : null
    const targetRect = targetRects?.length ? targetRects[0] : target.getBoundingClientRect()
    const marginRect = marginName.getBoundingClientRect()
    const targetCenter = targetRect.top + targetRect.height / 2
    const marginCenter = anchorSnapshot?.viewportY ?? (marginRect.top + marginRect.height / 2)
    const maxScrollTop = Math.max(0, reader.scrollHeight - reader.clientHeight)
    const nextScrollTop = Math.max(0, Math.min(maxScrollTop, reader.scrollTop + targetCenter - marginCenter))
    const reduceMotion = typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (typeof reader.scrollTo === 'function') {
      reader.scrollTo({
        top: nextScrollTop,
        behavior: reduceMotion ? 'auto' : 'smooth',
      })
    } else {
      reader.scrollTop = nextScrollTop
    }
    pendingMarginalAnchorRef.current = null
  }, [alignmentRequest, readerSection])

  const changeReaderSection = useCallback((nextSection: ReaderSection) => {
    setReaderSection(nextSection)
    setFocusLevel('reader')
    setShowContextualAssistance(false)
    setAdjudicationSession(null)
    setAdjudicationNotice('')
    setShowTextualBeings(false)
    setEvidenceProjection('full-text')
    setIsolatedEvidenceKind(null)
    setExpandedLectioFoldIds(new Set())
    setInlineCandidate(null)
    setAlignmentRequest(null)
    setRapidTool(null)
    setRapidSpanAnchor(null)
    setRapidSectionStart(null)
    setRapidSectionPhase('opening')
    setRapidSectionTypeId('')
    setEditingStructureSectionId('')
    if (nextSection !== 'character-index') {
      onSelectCharacter?.('')
      setActiveMarginalCharacterId('')
      setFocusedCharacterOccurrenceId(null)
      setFocusedLayerNoteId(null)
    }
  }, [onSelectCharacter])

  const moveUnit = useCallback((delta: number) => {
    if (model.units.length === 0) {
      return
    }
    const nextIndex = Math.max(0, Math.min(model.units.length - 1, boundedUnitIndex + delta))
    const nextUnit = model.units[nextIndex]
    if (!nextUnit) {
      return
    }
    setSelectedRowId(nextUnit.rowId)
    setSelectedPeerIndex(0)
    setAlignmentRequest(null)
  }, [boundedUnitIndex, model.units])

  const lectioEvidenceItinerary = useMemo(() => {
    const rowOrder = new Map(lectioRowOrder.map((rowId, index) => [rowId, index]))
    const walkableEvidence = adjudicationSession
      ? allLectioEvidence.filter(item => item.standing !== 'relegated')
      : lectioEvidence
    return walkableEvidence
      .filter(item => (
        item.witnessId === sourceWitnessId &&
        (!activeCharacterId || lectioEvidenceRefersTo(item, activeCharacterId)) &&
        (!isolatedEvidenceKind || (
          isolatedEvidenceKind === 'chain'
            ? item.kind === 'name' || item.kind === 'coref'
            : item.kind === isolatedEvidenceKind
        ))
      ))
      .slice()
      .sort((left, right) => (
        (rowOrder.get(left.rowId) ?? Number.MAX_SAFE_INTEGER) -
          (rowOrder.get(right.rowId) ?? Number.MAX_SAFE_INTEGER) ||
        left.start - right.start ||
        left.end - right.end ||
        left.id.localeCompare(right.id)
      ))
  }, [activeCharacterId, adjudicationSession, allLectioEvidence, isolatedEvidenceKind, lectioEvidence, lectioRowOrder, sourceWitnessId])

  const focusLectioEvidence = useCallback((evidence: LectioEvidenceRecord) => {
    if (activeCharacterId !== evidence.characterId) {
      setActiveMarginalCharacterId(evidence.characterId)
      onSelectCharacter?.(evidence.characterId)
    }
    if (evidence.kind === 'name' || evidence.kind === 'coref') {
      setFocusedCharacterOccurrenceId(evidence.id)
      setFocusedLayerNoteId(null)
    } else {
      setFocusedCharacterOccurrenceId(null)
      setFocusedLayerNoteId(evidence.id)
    }
    pendingMarginalAnchorRef.current = null
    alignmentRequestToken.current += 1
    setAlignmentRequest({
      characterId: evidence.characterId,
      evidenceId: evidence.id,
      rowId: evidence.rowId,
      token: alignmentRequestToken.current,
    })
  }, [activeCharacterId, onSelectCharacter])

  const moveLectioEvidence = useCallback((delta: number) => {
    if (lectioEvidenceItinerary.length === 0) {
      return
    }
    const focusedId = focusedCharacterOccurrenceId || focusedLayerNoteId
    const currentIndex = lectioEvidenceItinerary.findIndex(item => item.id === focusedId)
    const nextIndex = currentIndex < 0
      ? (delta < 0 ? lectioEvidenceItinerary.length - 1 : 0)
      : Math.max(0, Math.min(lectioEvidenceItinerary.length - 1, currentIndex + delta))
    const nextEvidence = lectioEvidenceItinerary[nextIndex]
    if (nextEvidence) {
      focusLectioEvidence(nextEvidence)
    }
  }, [focusLectioEvidence, focusedCharacterOccurrenceId, focusedLayerNoteId, lectioEvidenceItinerary])

  const toggleLineApparatus = useCallback((rowId: string) => {
    setOpenLineApparatusRowIds(current => {
      const next = new Set(current)
      if (next.has(rowId)) {
        next.delete(rowId)
      } else {
        next.add(rowId)
      }
      return next
    })
  }, [])

  const openSecondHand = useCallback(() => {
    setEvidenceMenu(null)
    evidenceMenuAnchorRef.current = null
    setAdjudicationSession(null)
    setOpenLineApparatusRowIds(new Set())
    setRapidTool(null)
    setRapidSpanAnchor(null)
    setRapidSectionStart(null)
    setRapidSectionPhase('opening')
    setRapidSectionTypeId('')
    setEditingStructureSectionId('')
    setShowContextualAssistance(true)
  }, [])

  const closeAdjudication = useCallback(() => {
    restoreReaderSurfaceFocusRef.current = true
    setEvidenceMenu(null)
    evidenceMenuAnchorRef.current = null
    setAdjudicationSession(null)
    setOpenLineApparatusRowIds(new Set())
    setRapidTool(null)
    setRapidSpanAnchor(null)
    setRapidSectionStart(null)
    setRapidSectionPhase('opening')
    setRapidSectionTypeId('')
    setEditingStructureSectionId('')
    setAdjudicationNotice('Adjudication closed · judgments kept')
  }, [])

  const enterAdjudicationForCharacter = useCallback((
    characterId: string,
    {
      firstReading = false,
      alignFirstReading = true,
    }: LectioAdjudicationEntryOptions = {},
  ) => {
    if (!characterId || !onAdjudicateCharacterEvidence) {
      return
    }
    setShowContextualAssistance(false)
    setShowTextualBeings(false)
    setEvidenceProjection('full-text')
    setIsolatedEvidenceKind(null)
    setExpandedLectioFoldIds(new Set())
    setAdjudicationSession(characterLayer
      ? beginLectioAdjudicationSession(characterLayer, characterId)
      : {
          characterId,
          openingCharacterId: characterId,
          characters: [],
          occurrences: [],
          notes: [],
          touched: [],
          mergedCharacterIds: [],
        })
    /* Marginal entry attends the first reading; rapid-palette entry deliberately
       keeps the current passage. The unit number remains the local disclosure
       control in either case, so the apparatus is opt-in. */
    setOpenLineApparatusRowIds(new Set())
    const first = allLectioEvidence.find(item => (
      lectioEvidenceRefersTo(item, characterId) && item.standing !== 'relegated'
    ))
    if (first && alignFirstReading) {
      focusLectioEvidence(first)
    }
    setAdjudicationNotice(firstReading
      ? 'Click the first named occurrence, then admit it as a name'
      : 'Choose a unit number to reveal its apparatus')
  }, [
    allLectioEvidence,
    characterLayer,
    focusLectioEvidence,
    onAdjudicateCharacterEvidence,
  ])

  const enterAdjudication = useCallback(() => {
    if (activeCharacterId) {
      enterAdjudicationForCharacter(activeCharacterId)
    }
  }, [activeCharacterId, enterAdjudicationForCharacter])

  const createCharacterInMargin = useCallback(async () => {
    const displayName = characterCreationName.trim()
    if (!displayName || !onCreateCharacter || characterCreationPending) {
      return
    }
    setCharacterCreationPending(true)
    setCharacterCreationError('')
    try {
      const characterId = await onCreateCharacter(displayName, characterCreationKind)
      if (!characterId) {
        throw new Error('The character record was not created')
      }
      setCharacterCreationName('')
      setCharacterCreationKind('person')
      setCharacterCreationOpen(false)
      setActiveMarginalCharacterId(characterId)
      setFocusedCharacterOccurrenceId(null)
      setFocusedLayerNoteId(null)
      onSelectCharacter?.(characterId)
      const hasExistingEvidence = allLectioEvidence.some(item => (
        lectioEvidenceRefersTo(item, characterId) && item.standing !== 'relegated'
      ))
      enterAdjudicationForCharacter(characterId, { firstReading: !hasExistingEvidence })
    } catch (error) {
      setCharacterCreationError(error instanceof Error ? error.message : 'The character record could not be created')
    } finally {
      setCharacterCreationPending(false)
    }
  }, [
    allLectioEvidence,
    characterCreationName,
    characterCreationKind,
    characterCreationPending,
    enterAdjudicationForCharacter,
    onCreateCharacter,
    onSelectCharacter,
  ])

  const saveCanonicalName = useCallback(async () => {
    const displayName = characterRenameName.trim()
    if (!displayName || !renamingCharacterId || !onRenameCharacter || characterRenamePending) {
      return
    }
    setCharacterRenamePending(true)
    setCharacterRenameError('')
    try {
      await onRenameCharacter(renamingCharacterId, displayName, characterRenameKind)
      setRenamingCharacterId('')
      setCharacterRenameName('')
      setCharacterRenameKind('person')
      setAdjudicationNotice(`Canonical record revised to ${displayName} · ${characterRenameKind}`)
    } catch (error) {
      setCharacterRenameError(error instanceof Error ? error.message : 'The canonical name could not be saved')
    } finally {
      setCharacterRenamePending(false)
    }
  }, [
    characterRenameName,
    characterRenameKind,
    characterRenamePending,
    onRenameCharacter,
    renamingCharacterId,
  ])

  const deleteCharacterFromMargin = useCallback(async (characterId: string, label: string) => {
    if (!characterId || !onDeleteCharacter || characterDeletePending) {
      return
    }
    setCharacterDeletePending(true)
    setCharacterDeleteError('')
    try {
      await onDeleteCharacter(characterId)
      setDeletingCharacterId('')
      setRenamingCharacterId('')
      setCharacterRenameName('')
      setCharacterRenameError('')
      clearCharacterSelection()
      setAdjudicationNotice(`${label} deleted from the working character index`)
    } catch (error) {
      setCharacterDeleteError(error instanceof Error ? error.message : 'The character record could not be deleted')
    } finally {
      setCharacterDeletePending(false)
    }
  }, [characterDeletePending, clearCharacterSelection, onDeleteCharacter])

  const dispatchAdjudicationCommand = useCallback((command: LectioAdjudicationCommand) => {
    if (!onAdjudicateCharacterEvidence) {
      return
    }
    if (command.op !== 'restore') {
      setAdjudicationSession(current => {
        if (!current) {
          return current
        }
        const target = { sourceKind: command.sourceKind, evidenceId: command.evidenceId }
        const alreadyTracked = current.touched.some(item => (
          item.sourceKind === target.sourceKind && item.evidenceId === target.evidenceId
        ))
        const touched = alreadyTracked ? current.touched : [...current.touched, target]
        if (command.op !== 'merge-character') {
          return touched === current.touched ? current : { ...current, touched }
        }
        return {
          ...current,
          touched,
          mergedCharacterIds: Array.from(new Set([
            ...(current.mergedCharacterIds || []),
            command.sourceCharacterId,
            command.characterId,
          ])),
        }
      })
    }
    const result = onAdjudicateCharacterEvidence(command)
    if (result && typeof (result as Promise<void>).catch === 'function') {
      void (result as Promise<void>).catch(error => {
        setAdjudicationNotice(error instanceof Error ? error.message : 'The reading could not be saved')
      })
    }
  }, [onAdjudicateCharacterEvidence])

  const closeEvidenceMenu = useCallback((restoreFocus = true) => {
    const anchor = evidenceMenuAnchorRef.current
    setEvidenceMenu(null)
    evidenceMenuAnchorRef.current = null
    if (restoreFocus) {
      anchor?.focus()
    }
  }, [])

  const openEvidenceMenu = useCallback((
    target: LectioEvidenceMenuTarget,
    anchor: HTMLElement,
    initialSubmenu: LectioClassifiedLayerKind | null = null,
    pendingSpeakerTurn: LectioPendingSpeakerTurn | null = null,
  ) => {
    const rect = anchor.getBoundingClientRect()
    const effectiveLayerKind = initialSubmenu || target.layerKind
    const isKnownFocalizationClass = effectiveLayerKind === 'focalization' && (
      LECTIO_FOCALIZATION_CLASSES.includes(target.classification as typeof LECTIO_FOCALIZATION_CLASSES[number])
    )
    const classification = isKnownFocalizationClass && target.classification
      ? target.classification as LectioLayerClassification
      : LECTIO_FOCALIZATION_CLASSES[0]
    evidenceMenuAnchorRef.current = anchor
    setFocusedCharacterOccurrenceId(target.sourceKind === 'occurrence' ? target.evidenceId : null)
    setFocusedLayerNoteId(target.sourceKind === 'layer' ? target.evidenceId : null)
    setEvidenceMenu({
      target,
      anchorLeft: rect.left,
      anchorTop: rect.top,
      anchorBottom: rect.bottom,
      submenu: initialSubmenu || (
        target.sourceKind === 'layer'
          ? target.layerKind || 'focalization'
          : null
      ),
      classification,
      manualCharacterization: createManualCharacterizationDraft(target.manualCharacterization),
      characterizationBranch: null,
      characterizationDetail: null,
      speakerAnalysis: createSpeakerAnalysisDraft(target.speakerAnalysis),
      speakerBranch: null,
      pendingSpeakerTurn,
      voice: target.voice || 'figure',
    })
  }, [])

  const buildResearchNoteTargetForEvidence = useCallback((
    target: LectioEvidenceMenuTarget,
  ): AnnotationResearchNoteTarget | null => {
    const kind = target.sourceKind === 'occurrence'
      ? target.mentionType === 'coref' ? 'coreference' : null
      : target.sourceKind === 'layer'
        ? target.layerKind || null
        : null
    if (!kind) {
      return null
    }
    const entry = effectiveCharacterIndexEntries.find(candidate => candidate.id === target.characterId)
    return {
      kind,
      annotationId: target.evidenceId,
      ...(kind === 'speaker' && target.logicalId ? { logicalId: target.logicalId } : {}),
      ...(target.characterId ? { characterId: target.characterId } : {}),
      ...(entry ? { characterLabel: getCharacterDisplayLabel(entry) } : {}),
      annotationLabel: target.text,
    }
  }, [effectiveCharacterIndexEntries])

  const buildResearchNotePassageSnapshotForEvidence = useCallback((
    target: LectioEvidenceMenuTarget,
  ): AnnotationResearchNotePassageSnapshot => {
    const maxSnapshotCharacters = 20_000
    const cap = (value: string): { value: string; truncated: boolean } => value.length > maxSnapshotCharacters
      ? { value: `${value.slice(0, maxSnapshotCharacters)}\n[…]`, truncated: true }
      : { value, truncated: false }
    const speakerFragments = target.layerKind === 'speaker' && target.logicalId
      ? Array.from(sourceLayerAnnotationsByRowId.values()).flat().filter(annotation => (
          annotation.kind === 'speaker' && annotation.speechTurnId === target.logicalId
        )).sort((left, right) => (
          (rowOrderById.get(left.rowId) ?? Number.MAX_SAFE_INTEGER) -
            (rowOrderById.get(right.rowId) ?? Number.MAX_SAFE_INTEGER) ||
          left.localStart - right.localStart || left.id.localeCompare(right.id)
        ))
      : []
    const rowIds = speakerFragments.length > 0
      ? Array.from(new Set(speakerFragments.map(fragment => fragment.rowId)))
      : [target.rowId]
    const unitLabels = rowIds.map(rowId => rowLabelById.get(rowId) || rowId)
    const quoted = cap(speakerFragments.length > 0
      ? speakerFragments.map(fragment => (
          (rowTextById.get(fragment.rowId) || '').slice(fragment.localStart, fragment.localEnd)
        )).join('\n')
      : target.text)
    const context = cap(rowIds.map(rowId => rowTextById.get(rowId) || '').join('\n'))
    return {
      sourceWitnessId,
      rowIds,
      unitLabels,
      quotedText: quoted.value,
      contextText: context.value,
      capturedAt: new Date().toISOString(),
      truncated: quoted.truncated || context.truncated,
    }
  }, [rowLabelById, rowOrderById, rowTextById, sourceLayerAnnotationsByRowId, sourceWitnessId])

  const openResearchNotesForEvidence = useCallback(() => {
    if (!evidenceMenu) {
      return
    }
    const target = buildResearchNoteTargetForEvidence(evidenceMenu.target)
    if (!target || !chapterId) {
      return
    }
    const anchor = evidenceMenuAnchorRef.current?.getBoundingClientRect()
    setResearchNoteEditor({
      chapterId,
      ...(researchBookId ? { bookId: researchBookId } : {}),
      target,
      passageSnapshot: buildResearchNotePassageSnapshotForEvidence(evidenceMenu.target),
      targetLabel: evidenceMenu.target.text,
      anchorLeft: Math.max(8, Math.min(anchor?.left ?? evidenceMenu.anchorLeft, window.innerWidth - 430)),
      anchorBottom: anchor?.bottom ?? evidenceMenu.anchorBottom,
    })
    closeEvidenceMenu(false)
  }, [
    buildResearchNotePassageSnapshotForEvidence,
    buildResearchNoteTargetForEvidence,
    chapterId,
    closeEvidenceMenu,
    evidenceMenu,
    researchBookId,
  ])

  const buildResearchNotePassageSnapshotForSection = useCallback((
    section: TextSection,
  ): AnnotationResearchNotePassageSnapshot => {
    const startOrder = rowOrderById.get(section.start.rowId) ?? 0
    const endOrder = rowOrderById.get(section.end.rowId) ?? startOrder
    const from = Math.min(startOrder, endOrder)
    const to = Math.max(startOrder, endOrder)
    const units = model.units.slice(from, to + 1)
    const quotedText = units.map((unit, index) => {
      const startsHere = unit.rowId === section.start.rowId
      const endsHere = unit.rowId === section.end.rowId
      const start = startsHere ? Math.max(0, section.start.offset) : 0
      const end = endsHere ? Math.min(unit.text.length, section.end.offset) : unit.text.length
      return unit.text.slice(Math.min(start, end), Math.max(start, end))
    }).join('\n')
    const contextText = units.map(unit => unit.text).join('\n')
    const maxSnapshotCharacters = 20_000
    const truncated = quotedText.length > maxSnapshotCharacters || contextText.length > maxSnapshotCharacters
    return {
      sourceWitnessId,
      rowIds: units.map(unit => unit.rowId),
      unitLabels: units.map(unit => unit.rowLabel),
      quotedText: quotedText.length > maxSnapshotCharacters
        ? `${quotedText.slice(0, maxSnapshotCharacters)}\n[…]`
        : quotedText,
      contextText: contextText.length > maxSnapshotCharacters
        ? `${contextText.slice(0, maxSnapshotCharacters)}\n[…]`
        : contextText,
      capturedAt: new Date().toISOString(),
      truncated,
    }
  }, [model.units, rowOrderById, sourceWitnessId])

  const openResearchNotesForSection = useCallback((section: TextSection, anchor: HTMLElement) => {
    if (!chapterId) {
      return
    }
    const sectionType = textSectionTypeById.get(section.typeId)
    const targetLabel = section.title || sectionType?.label || 'Untitled section'
    const rect = anchor.getBoundingClientRect()
    setResearchNoteEditor({
      chapterId,
      ...(researchBookId ? { bookId: researchBookId } : {}),
      target: {
        kind: 'section',
        annotationId: section.id,
        annotationLabel: targetLabel,
      },
      passageSnapshot: buildResearchNotePassageSnapshotForSection(section),
      targetLabel,
      anchorLeft: Math.max(8, Math.min(rect.left, window.innerWidth - 430)),
      anchorBottom: rect.bottom,
    })
  }, [
    buildResearchNotePassageSnapshotForSection,
    chapterId,
    researchBookId,
    textSectionTypeById,
  ])

  const activeRapidSectionType = textStructureLayer?.types.find(sectionType => (
    sectionType.id === rapidSectionTypeId
  ))
  const editingStructureSection = textStructureLayer?.sections.find(section => (
    section.id === editingStructureSectionId
  ))
  const editingStructureSectionNoteCount = editingStructureSection
    ? getResearchNoteCount({
        kind: 'section',
        annotationId: editingStructureSection.id,
        annotationLabel: editingStructureSection.title || activeRapidSectionType?.label,
      })
    : 0

  const buildRapidSectionBoundary = useCallback((
    rowId: string,
    offset: number,
    affinity: TextSectionBoundary['affinity'],
    quote = '',
  ): TextSectionBoundary | null => {
    const unit = model.units.find(candidate => candidate.rowId === rowId)
    if (!unit) {
      return null
    }
    const boundedOffset = Math.max(0, Math.min(unit.text.length, offset))
    return {
      witnessId: sourceWitnessId,
      unitUuid: unit.sourceUnitUuid,
      rowId,
      offset: boundedOffset,
      affinity,
      quote: quote || undefined,
      contextBefore: unit.text.slice(Math.max(0, boundedOffset - 32), boundedOffset) || undefined,
      contextAfter: unit.text.slice(boundedOffset, Math.min(unit.text.length, boundedOffset + 32)) || undefined,
    }
  }, [model.units, sourceWitnessId])

  const markRapidSectionBoundary = useCallback(async ({
    rowId,
    start,
    end,
    text,
    wholeUnit,
  }: {
    rowId: string
    start: number
    end: number
    text: string
    wholeUnit: boolean
  }) => {
    const unit = model.units.find(candidate => candidate.rowId === rowId)
    if (!unit || !textStructureLayer || !onEditTextStructure || structureSaving) {
      return
    }
    if (rapidSectionPhase === 'opening') {
      const boundary = buildRapidSectionBoundary(
        rowId,
        wholeUnit ? 0 : start,
        'before',
        wholeUnit ? unit.text.slice(0, 32) : text,
      )
      if (!boundary) {
        return
      }
      setRapidSectionStart(boundary)
      setRapidSectionPhase(editingStructureSectionId ? 'closing' : 'classify')
      setAdjudicationNotice(editingStructureSectionId
        ? `New opening set at ${unit.rowLabel} · choose the closing unit or word`
        : `Opening set at ${unit.rowLabel} · choose or define its type in the margin`)
      return
    }
    if (rapidSectionPhase !== 'closing' || !rapidSectionStart || !activeRapidSectionType) {
      setAdjudicationNotice('Choose the section type beside its opening before closing it')
      return
    }

    const boundary = buildRapidSectionBoundary(
      rowId,
      wholeUnit ? unit.text.length : end,
      'after',
      wholeUnit ? unit.text.slice(Math.max(0, unit.text.length - 32)) : text,
    )
    if (!boundary) {
      return
    }
    const now = new Date().toISOString()
    const existing = editingStructureSectionId
      ? textStructureLayer.sections.find(section => section.id === editingStructureSectionId)
      : undefined
    const command: TextStructureCommand = existing
      ? {
          op: 'update-section',
          sectionId: existing.id,
          updates: {
            typeId: activeRapidSectionType.id,
            title: rapidSectionTitleRef.current.trim() || undefined,
            start: rapidSectionStart,
            end: boundary,
          },
        }
      : {
          op: 'add-section',
          section: {
            id: `reader-section-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
            typeId: activeRapidSectionType.id,
            title: rapidSectionTitleRef.current.trim() || undefined,
            start: rapidSectionStart,
            end: boundary,
            origin: 'human',
            standing: 'admitted',
            createdAt: now,
            updatedAt: now,
          },
        }
    setStructureSaving(true)
    try {
      await onEditTextStructure(command)
      setRapidSectionStart(null)
      setRapidSectionPhase('opening')
      setRapidSectionTypeId('')
      setRapidSectionTitle('')
      rapidSectionTitleRef.current = ''
      setEditingStructureSectionId('')
      setAdjudicationNotice(
        `${activeRapidSectionType.label} saved through ${unit.rowLabel} · section tool remains armed`,
      )
    } catch (error) {
      setAdjudicationNotice(error instanceof Error ? error.message : 'The section could not be saved')
    } finally {
      setStructureSaving(false)
    }
  }, [
    activeRapidSectionType,
    buildRapidSectionBoundary,
    editingStructureSectionId,
    model.units,
    onEditTextStructure,
    rapidSectionStart,
    rapidSectionPhase,
    structureSaving,
    textStructureLayer,
  ])

  const activateSectionTool = useCallback(() => {
    const nextActive = rapidTool !== 'section'
    setRapidTool(nextActive ? 'section' : null)
    setRapidSpanAnchor(null)
    setRapidSectionStart(null)
    setRapidSectionPhase('opening')
    setRapidSectionTypeId('')
    setRapidSectionTitle('')
    rapidSectionTitleRef.current = ''
    setEditingStructureSectionId('')
    setCustomSectionTypeOpen(false)
    setAdjudicationNotice(nextActive
      ? 'Section tool armed · choose the opening unit or word'
      : 'Section marking off · the contextual menu remains available')
  }, [rapidTool])

  const selectRapidSectionType = useCallback((sectionTypeId: string) => {
    const sectionType = textStructureLayer?.types.find(candidate => candidate.id === sectionTypeId)
    if (!sectionType) {
      return
    }
    setRapidSectionTypeId(sectionTypeId)
    setRapidSectionPhase(editingStructureSectionId ? 'editing' : 'closing')
    setCustomSectionTypeOpen(false)
    setAdjudicationNotice(editingStructureSectionId
      ? `${sectionType.label} selected · revise its title or save`
      : `${sectionType.label} selected · title it in the margin, then choose its closing unit or word`)
  }, [editingStructureSectionId, textStructureLayer?.types])

  const addCustomSectionType = useCallback(async () => {
    if (!textStructureLayer || !onEditTextStructure || structureSaving) {
      return
    }
    const label = customSectionTypeLabel.trim()
    const abbreviation = customSectionTypeAbbreviation.trim()
    const id = slugifySectionType(label) || `section-type-${Date.now().toString(36)}`
    if (!label || !abbreviation) {
      setAdjudicationNotice('A custom section type needs a name and abbreviation')
      return
    }
    const sectionType: TextSectionType = {
      id,
      label,
      abbreviation,
      createdAt: new Date().toISOString(),
    }
    setStructureSaving(true)
    try {
      await onEditTextStructure({ op: 'add-type', sectionType })
      setRapidSectionTypeId(id)
      setRapidSectionPhase(editingStructureSectionId ? 'editing' : 'closing')
      setCustomSectionTypeLabel('')
      setCustomSectionTypeAbbreviation('')
      setCustomSectionTypeOpen(false)
      setAdjudicationNotice(editingStructureSectionId
        ? `${label} added and selected · revise its title or save`
        : `${label} added and selected · title it in the margin, then choose its closing boundary`)
    } catch (error) {
      setAdjudicationNotice(error instanceof Error ? error.message : 'The section type could not be saved')
    } finally {
      setStructureSaving(false)
    }
  }, [
    customSectionTypeAbbreviation,
    customSectionTypeLabel,
    editingStructureSectionId,
    onEditTextStructure,
    structureSaving,
    textStructureLayer,
  ])

  const saveEditedStructureSection = useCallback(async () => {
    const section = textStructureLayer?.sections.find(candidate => candidate.id === editingStructureSectionId)
    if (!section || !activeRapidSectionType || !onEditTextStructure || structureSaving) {
      return
    }
    setStructureSaving(true)
    try {
      await onEditTextStructure({
        op: 'update-section',
        sectionId: section.id,
        updates: {
          typeId: activeRapidSectionType.id,
          title: rapidSectionTitleRef.current.trim() || undefined,
        },
      })
      setEditingStructureSectionId('')
      setRapidSectionStart(null)
      setRapidSectionPhase('opening')
      setRapidSectionTypeId('')
      setRapidSectionTitle('')
      rapidSectionTitleRef.current = ''
      setAdjudicationNotice(`${activeRapidSectionType.label} metadata revised`)
    } catch (error) {
      setAdjudicationNotice(error instanceof Error ? error.message : 'The section could not be revised')
    } finally {
      setStructureSaving(false)
    }
  }, [
    activeRapidSectionType,
    editingStructureSectionId,
    onEditTextStructure,
    structureSaving,
    textStructureLayer?.sections,
  ])

  const deleteEditedStructureSection = useCallback(async () => {
    if (!editingStructureSectionId || !onEditTextStructure || structureSaving) {
      return
    }
    const sectionTarget: AnnotationResearchNoteTarget = {
      kind: 'section',
      annotationId: editingStructureSectionId,
    }
    setStructureSaving(true)
    try {
      await onEditTextStructure({ op: 'delete-section', sectionId: editingStructureSectionId })
      await onArchiveAnnotationResearchNotesForTarget?.(sectionTarget)
      setEditingStructureSectionId('')
      setRapidSectionTitle('')
      rapidSectionTitleRef.current = ''
      setRapidSectionStart(null)
      setRapidSectionPhase('opening')
      setRapidSectionTypeId('')
      setAdjudicationNotice('Section removed')
    } catch (error) {
      setAdjudicationNotice(error instanceof Error ? error.message : 'The section could not be removed')
    } finally {
      setStructureSaving(false)
    }
  }, [
    editingStructureSectionId,
    onArchiveAnnotationResearchNotesForTarget,
    onEditTextStructure,
    structureSaving,
  ])

  const selectStructureSection = useCallback((sectionId: string) => {
    const section = textStructureLayer?.sections.find(candidate => candidate.id === sectionId)
    if (!section) {
      return
    }
    const sectionType = textSectionTypeById.get(section.typeId)
    setRapidTool('section')
    setRapidSectionTypeId(section.typeId)
    setRapidSectionTitle(section.title || '')
    rapidSectionTitleRef.current = section.title || ''
    setRapidSectionStart(section.start)
    setRapidSectionPhase('editing')
    setRapidSpanAnchor(null)
    setEditingStructureSectionId(section.id)
    setCustomSectionTypeOpen(false)
    setAdjudicationNotice(
      `${sectionType?.label || 'Section'} selected · revise it beside its opening`,
    )
  }, [textSectionTypeById, textStructureLayer?.sections])

  const cancelRapidSectionDraft = useCallback(() => {
    setRapidSectionStart(null)
    setRapidSectionPhase('opening')
    setRapidSectionTypeId('')
    setRapidSectionTitle('')
    rapidSectionTitleRef.current = ''
    setEditingStructureSectionId('')
    setCustomSectionTypeOpen(false)
    setCustomSectionTypeLabel('')
    setCustomSectionTypeAbbreviation('')
    setAdjudicationNotice('Section draft cancelled · choose another opening when ready')
  }, [])

  const remarkEditedStructureSection = useCallback(() => {
    if (!editingStructureSectionId) {
      return
    }
    setRapidSectionStart(null)
    setRapidSectionPhase('opening')
    setCustomSectionTypeOpen(false)
    setAdjudicationNotice('Choose the revised opening unit or word')
  }, [editingStructureSectionId])

  const reopenRapidSectionClassification = useCallback(() => {
    setRapidSectionPhase('classify')
    setCustomSectionTypeOpen(false)
    setAdjudicationNotice('Choose or define the section type beside its opening')
  }, [])

  const activateRapidTool = useCallback((tool: Exclude<LectioRapidTool, 'create-character' | 'section'>) => {
    const entry = effectiveCharacterIndexEntries.find(candidate => candidate.id === activeCharacterId)
    const label = entry ? getCharacterDisplayLabel(entry) : activeCharacterId
    setRapidSpanAnchor(null)
    setRapidSectionStart(null)
    setRapidSectionPhase('opening')
    setRapidSectionTypeId('')
    setEditingStructureSectionId('')
    setRapidTool(current => current === tool ? null : tool)
    if (rapidTool === tool) {
      setAdjudicationNotice('Rapid marking off · the contextual menu remains available')
      return
    }
    if (!activeCharacterId) {
      setAdjudicationNotice(`${tool} tool armed · choose a character from the gutter palette`)
      return
    }
    if (onAdjudicateCharacterEvidence && adjudicationSession?.characterId !== activeCharacterId) {
      enterAdjudicationForCharacter(activeCharacterId, { alignFirstReading: false })
    }
    setAdjudicationNotice(
      tool === 'coref'
        ? `${label} · coreference tool · click each occurrence`
        : `${label} · ${tool} tool · choose the first word`,
    )
  }, [
    activeCharacterId,
    adjudicationSession?.characterId,
    effectiveCharacterIndexEntries,
    enterAdjudicationForCharacter,
    onAdjudicateCharacterEvidence,
    rapidTool,
  ])

  const selectRapidCharacter = useCallback((characterId: string) => {
    const entry = effectiveCharacterIndexEntries.find(candidate => candidate.id === characterId)
    const label = entry ? getCharacterDisplayLabel(entry) : characterId
    setActiveMarginalCharacterId(characterId)
    setShowTextualBeings(false)
    setShowContextualAssistance(false)
    setEvidenceProjection('full-text')
    setIsolatedEvidenceKind(null)
    setRapidSpanAnchor(null)
    setFocusedCharacterOccurrenceId(null)
    setFocusedLayerNoteId(null)
    onSelectCharacter?.(characterId)
    if (rapidTool && rapidTool !== 'create-character' && rapidTool !== 'section' && onAdjudicateCharacterEvidence) {
      if (adjudicationSession?.characterId !== characterId) {
        enterAdjudicationForCharacter(characterId, { alignFirstReading: false })
      }
      setAdjudicationNotice(
        rapidTool === 'coref'
          ? `${label} · coreference tool · click each occurrence`
          : `${label} · ${rapidTool} tool · choose the first word`,
      )
    } else {
      setAdjudicationNotice(`${label} selected in the rapid palette`)
    }
  }, [
    adjudicationSession?.characterId,
    effectiveCharacterIndexEntries,
    enterAdjudicationForCharacter,
    onAdjudicateCharacterEvidence,
    onSelectCharacter,
    rapidTool,
  ])

  const beginRapidCharacterCreation = useCallback(() => {
    if (!onCreateCharacter || rapidCharacterCreationPending) {
      return
    }
    rapidToolBeforeCreationRef.current = rapidTool && rapidTool !== 'create-character' && rapidTool !== 'section'
      ? rapidTool
      : null
    setRapidTool(current => current === 'create-character' ? rapidToolBeforeCreationRef.current : 'create-character')
    setRapidSpanAnchor(null)
    setRapidSectionStart(null)
    setRapidSectionPhase('opening')
    setRapidSectionTypeId('')
    setEditingStructureSectionId('')
    setAdjudicationNotice(
      rapidTool === 'create-character'
        ? 'Character creation cancelled'
        : 'New character · select the complete first named occurrence in the text',
    )
  }, [onCreateCharacter, rapidCharacterCreationPending, rapidTool])

  const handleRapidAnnotationWord = useCallback((
    rowId: string,
    start: number,
    end: number,
    text: string,
    anchor: HTMLButtonElement,
  ) => {
    if (!rapidTool || rapidTool === 'create-character' || rapidTool === 'section' || !activeCharacterId || !onAdjudicateCharacterEvidence) {
      return
    }
    if (adjudicationSession?.characterId !== activeCharacterId) {
      enterAdjudicationForCharacter(activeCharacterId, { alignFirstReading: false })
    }
    const rowLabel = rowLabelById.get(rowId) || rowId
    if (rapidTool === 'coref') {
      const existing = allLectioEvidence.find(item => (
        lectioEvidenceRefersTo(item, activeCharacterId) &&
        item.witnessId === sourceWitnessId &&
        item.rowId === rowId &&
        item.start === start &&
        item.end === end &&
        (item.kind === 'name' || item.kind === 'coref') &&
        item.standing !== 'relegated'
      ))
      if (existing) {
        setFocusedCharacterOccurrenceId(existing.id)
        setFocusedLayerNoteId(null)
        setAdjudicationNotice(`${rowLabel} · ${text} is already in this coreference chain`)
        return
      }
      const evidenceId = `lectio-rapid-occurrence-${Date.now().toString(36)}-${rowId}-${start}`
      dispatchAdjudicationCommand({
        op: 'add',
        sourceKind: 'occurrence',
        evidenceId,
        characterId: activeCharacterId,
        evidenceKind: 'coref',
        witnessId: sourceWitnessId,
        rowId,
        start,
        end,
        sourceText: rowTextById.get(rowId) || '',
      })
      setFocusedCharacterOccurrenceId(evidenceId)
      setFocusedLayerNoteId(null)
      setAdjudicationNotice(`${rowLabel} · ${text} admitted as coreference · tool remains armed`)
      return
    }
    if (!rapidSpanAnchor) {
      setRapidSpanAnchor({ rowId, start, end })
      setAdjudicationNotice(`${rowLabel} · ${rapidTool} starts at ${text} · choose the final word`)
      return
    }
    if (rapidTool === 'speaker') {
      const anchorIndex = rowOrderById.get(rapidSpanAnchor.rowId)
      const currentIndex = rowOrderById.get(rowId)
      if (anchorIndex === undefined || currentIndex === undefined) {
        setRapidSpanAnchor({ rowId, start, end })
        return
      }
      const anchorFirst = anchorIndex < currentIndex || (
        anchorIndex === currentIndex && rapidSpanAnchor.start <= start
      )
      const first = anchorFirst ? rapidSpanAnchor : { rowId, start, end }
      const last = anchorFirst ? { rowId, start, end } : rapidSpanAnchor
      const firstIndex = rowOrderById.get(first.rowId) ?? 0
      const lastIndex = rowOrderById.get(last.rowId) ?? firstIndex
      const speechLevel = resolveSpeakerLevelForRange({
        startRowId: first.rowId,
        startOffset: first.start,
        endRowId: last.rowId,
        endOffset: last.end,
      })
      const turnId = `lectio-speech-turn-${Date.now().toString(36)}-${first.rowId}-${first.start}`
      const fragments = model.units.slice(firstIndex, lastIndex + 1).flatMap((unit, unitOffset) => {
        const fragmentStart = unitOffset === 0 ? first.start : 0
        const fragmentEnd = unitOffset === lastIndex - firstIndex ? last.end : unit.text.length
        return fragmentEnd > fragmentStart ? [{ unit, fragmentStart, fragmentEnd }] : []
      })
      setRapidSpanAnchor(null)
      const pendingFragments = fragments.map(({ unit, fragmentStart, fragmentEnd }, fragmentIndex) => ({
        evidenceId: `${turnId}-fragment-${fragmentIndex + 1}`,
        rowId: unit.rowId,
        start: fragmentStart,
        end: fragmentEnd,
        sourceText: unit.text,
      }))
      const spanText = fragments.map(({ unit, fragmentStart, fragmentEnd }) => (
        unit.text.slice(fragmentStart, fragmentEnd).trim()
      )).filter(Boolean).join(' … ')
      const firstRowLabel = rowLabelById.get(first.rowId) || first.rowId
      const lastRowLabel = rowLabelById.get(last.rowId) || last.rowId
      openEvidenceMenu({
        sourceKind: 'draft',
        evidenceId: turnId,
        characterId: activeCharacterId,
        rowId: first.rowId,
        rowLabel: first.rowId === last.rowId ? firstRowLabel : `${firstRowLabel}–${lastRowLabel}`,
        start: first.start,
        end: last.end,
        text: spanText,
      }, anchor, 'speaker', { turnId, speechLevel, fragments: pendingFragments })
      setAdjudicationNotice(
        `${rowLabel} · speech turn selected across ${fragments.length} ${fragments.length === 1 ? 'unit' : 'units'} · classify it in place`,
      )
      return
    }
    if (rapidSpanAnchor.rowId !== rowId) {
      setRapidSpanAnchor({ rowId, start, end })
      setAdjudicationNotice(`${rowLabel} · ${rapidTool} starts at ${text} · choose the final word`)
      return
    }
    const spanStart = Math.min(rapidSpanAnchor.start, start)
    const spanEnd = Math.max(rapidSpanAnchor.end, end)
    const sourceText = rowTextById.get(rowId) || ''
    const spanText = sourceText.slice(spanStart, spanEnd)
    setRapidSpanAnchor(null)
    openEvidenceMenu({
      sourceKind: 'draft',
      evidenceId: `rapid:${rapidTool}:${rowId}:${spanStart}:${spanEnd}`,
      characterId: activeCharacterId,
      rowId,
      rowLabel,
      start: spanStart,
      end: spanEnd,
      text: spanText,
    }, anchor, rapidTool)
    setAdjudicationNotice(`${rowLabel} · ${spanText} · classify the ${rapidTool} reading`)
  }, [
    activeCharacterId,
    adjudicationSession?.characterId,
    allLectioEvidence,
    dispatchAdjudicationCommand,
    enterAdjudicationForCharacter,
    onAdjudicateCharacterEvidence,
    openEvidenceMenu,
    model.units,
    rapidSpanAnchor,
    rapidTool,
    resolveSpeakerLevelForRange,
    rowLabelById,
    rowOrderById,
    rowTextById,
    sourceWitnessId,
  ])

  const createCharacterFromSourceSpan = useCallback(async (
    rowId: string,
    start: number,
    end: number,
    selectedText: string,
  ) => {
    if (!onCreateCharacter || !onAdjudicateCharacterEvidence || rapidCharacterCreationPending) {
      return
    }
    const existing = allLectioEvidence.find(item => (
      item.witnessId === sourceWitnessId &&
      item.rowId === rowId &&
      item.start === start &&
      item.end === end &&
      (item.kind === 'name' || item.kind === 'coref') &&
      item.standing !== 'relegated'
    ))
    const restoredTool = rapidToolBeforeCreationRef.current || 'coref'
    if (existing) {
      selectRapidCharacter(existing.characterId)
      setRapidTool(restoredTool)
      setAdjudicationNotice(`${selectedText} already belongs to an indexed character · no duplicate created`)
      return
    }
    setRapidCharacterCreationPending(true)
    try {
      const characterId = await onCreateCharacter(selectedText)
      if (!characterId) {
        throw new Error('The character record was not created')
      }
      const evidenceId = `lectio-rapid-name-${Date.now().toString(36)}-${rowId}-${start}`
      setActiveMarginalCharacterId(characterId)
      setFocusedCharacterOccurrenceId(evidenceId)
      setFocusedLayerNoteId(null)
      onSelectCharacter?.(characterId)
      enterAdjudicationForCharacter(characterId, {
        firstReading: true,
        alignFirstReading: false,
      })
      dispatchAdjudicationCommand({
        op: 'add',
        sourceKind: 'occurrence',
        evidenceId,
        characterId,
        evidenceKind: 'name',
        witnessId: sourceWitnessId,
        rowId,
        start,
        end,
        sourceText: rowTextById.get(rowId) || '',
      })
      setRapidTool(restoredTool)
      setRapidSpanAnchor(null)
      setAdjudicationNotice(`${selectedText} created and admitted as the first name · character remains selected`)
    } catch (error) {
      setAdjudicationNotice(error instanceof Error ? error.message : 'The character record could not be created')
      setRapidTool('create-character')
    } finally {
      setRapidCharacterCreationPending(false)
    }
  }, [
    allLectioEvidence,
    dispatchAdjudicationCommand,
    enterAdjudicationForCharacter,
    onAdjudicateCharacterEvidence,
    onCreateCharacter,
    onSelectCharacter,
    rapidCharacterCreationPending,
    rowTextById,
    selectRapidCharacter,
    sourceWitnessId,
  ])

  const handleRapidMarkWord = useCallback((
    rowId: string,
    start: number,
    end: number,
    text: string,
    anchor: HTMLButtonElement,
  ) => {
    if (rapidTool === 'section') {
      void markRapidSectionBoundary({ rowId, start, end, text, wholeUnit: false })
      return
    }
    if (rapidTool === 'create-character') {
      void createCharacterFromSourceSpan(rowId, start, end, text)
      return
    }
    handleRapidAnnotationWord(rowId, start, end, text, anchor)
  }, [createCharacterFromSourceSpan, handleRapidAnnotationWord, markRapidSectionBoundary, rapidTool])

  const evidenceMenuTargetId = evidenceMenu?.target.evidenceId || ''
  const evidenceMenuSubmenu = evidenceMenu?.submenu || null
  const evidenceMenuAnchorLeft = evidenceMenu?.anchorLeft || 0
  const evidenceMenuAnchorTop = evidenceMenu?.anchorTop || 0
  const evidenceMenuAnchorBottom = evidenceMenu?.anchorBottom || 0
  const evidenceMenuCharacterizationBranch = evidenceMenu?.characterizationBranch || null
  const evidenceMenuCharacterizationDetail = evidenceMenu?.characterizationDetail || null
  const evidenceMenuSpeakerBranch = evidenceMenu?.speakerBranch || null

  useEffect(() => {
    if (!evidenceMenuTargetId) {
      return
    }
    const frame = window.requestAnimationFrame(() => {
      const menu = evidenceMenuRef.current
      if (!menu) {
        return
      }
      const bounds = menu.getBoundingClientRect()
      const gutter = 8
      const left = Math.max(gutter, Math.min(evidenceMenuAnchorLeft, window.innerWidth - bounds.width - gutter))
      const preferredTop = evidenceMenuAnchorBottom + 6
      const top = preferredTop + bounds.height <= window.innerHeight - gutter
        ? preferredTop
        : Math.max(gutter, evidenceMenuAnchorTop - bounds.height - 6)
      menu.style.left = `${left}px`
      menu.style.top = `${top}px`
      const focusScope = evidenceMenuSpeakerBranch
        ? menu.querySelector<HTMLElement>('.lectio-speaker-branch-submenu')
        : evidenceMenuCharacterizationDetail
        ? menu.querySelector<HTMLElement>('.lectio-characterization-detail-submenu')
        : evidenceMenuCharacterizationBranch
          ? menu.querySelector<HTMLElement>('.lectio-characterization-branch-submenu')
          : evidenceMenuSubmenu
            ? menu.querySelector<HTMLElement>('.lectio-evidence-context-submenu')
            : menu.querySelector<HTMLElement>('.lectio-evidence-context-menu-panel')
      focusScope?.querySelector<HTMLElement>(
        '[role="menuitemcheckbox"], [role="menuitemradio"], [role="menuitem"]',
      )?.focus()
    })
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target
      if (target instanceof Node && evidenceMenuRef.current?.contains(target)) {
        return
      }
      closeEvidenceMenu(false)
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') {
        return
      }
      event.preventDefault()
      event.stopPropagation()
      if (evidenceMenuCharacterizationDetail) {
        setEvidenceMenu(current => current ? {
          ...current,
          characterizationDetail: current.characterizationDetail === 'other-character-picker'
            ? 'other'
            : current.characterizationDetail === 'reaction-character-picker'
              ? 'reaction'
              : null,
        } : current)
        return
      }
      if (evidenceMenuCharacterizationBranch) {
        setEvidenceMenu(current => current ? { ...current, characterizationBranch: null } : current)
        return
      }
      if (evidenceMenuSpeakerBranch) {
        setEvidenceMenu(current => current ? { ...current, speakerBranch: null } : current)
        return
      }
      closeEvidenceMenu(true)
    }
    window.addEventListener('pointerdown', handlePointerDown, true)
    window.addEventListener('keydown', handleKeyDown, true)
    return () => {
      window.cancelAnimationFrame(frame)
      window.removeEventListener('pointerdown', handlePointerDown, true)
      window.removeEventListener('keydown', handleKeyDown, true)
    }
  }, [
    closeEvidenceMenu,
    evidenceMenuAnchorBottom,
    evidenceMenuAnchorLeft,
    evidenceMenuAnchorTop,
    evidenceMenuCharacterizationBranch,
    evidenceMenuCharacterizationDetail,
    evidenceMenuSpeakerBranch,
    evidenceMenuSubmenu,
    evidenceMenuTargetId,
  ])

  const classifyOccurrenceFromMenu = useCallback((mentionType: CharacterMentionType) => {
    if (!evidenceMenu || evidenceMenu.target.sourceKind === 'layer') {
      return
    }
    if (evidenceMenu.target.sourceKind === 'draft') {
      const target = evidenceMenu.target
      const evidenceId = `lectio-editor-occurrence-${Date.now().toString(36)}-${target.rowId}-${target.start}`
      dispatchAdjudicationCommand({
        op: 'add',
        sourceKind: 'occurrence',
        evidenceId,
        characterId: target.characterId,
        evidenceKind: mentionType,
        witnessId: sourceWitnessId,
        rowId: target.rowId,
        start: target.start,
        end: target.end,
        sourceText: rowTextById.get(target.rowId) || '',
      })
      setFocusedCharacterOccurrenceId(evidenceId)
      setFocusedLayerNoteId(null)
      setAdjudicationNotice(`One ${mentionType === 'coref' ? 'coreference' : 'name'} reading entered under the editor's hand`)
      closeEvidenceMenu(true)
      return
    }
    dispatchAdjudicationCommand({
      op: 'classify-occurrence',
      sourceKind: 'occurrence',
      evidenceId: evidenceMenu.target.evidenceId,
      mentionType,
    })
    setAdjudicationNotice(`Occurrence admitted and read as ${mentionType === 'coref' ? 'coreference' : 'name'}`)
    closeEvidenceMenu(true)
  }, [closeEvidenceMenu, dispatchAdjudicationCommand, evidenceMenu, rowTextById, sourceWitnessId])

  const openLayerSubmenu = useCallback((layerKind: LectioClassifiedLayerKind) => {
    setEvidenceMenu(current => current ? {
      ...current,
      submenu: layerKind,
      characterizationBranch: null,
      characterizationDetail: null,
      speakerBranch: null,
      classification: layerKind === 'focalization'
        ? LECTIO_FOCALIZATION_CLASSES[0]
        : current.classification,
    } : current)
  }, [])

  const openSpeakerBranch = useCallback((branch: LectioSpeakerBranch) => {
    setEvidenceMenu(current => current ? { ...current, speakerBranch: branch } : current)
  }, [])

  const selectSpeakerAddressMode = useCallback((addressMode: LectioSpeakerAddressMode) => {
    setEvidenceMenu(current => current ? {
      ...current,
      speakerAnalysis: {
        ...current.speakerAnalysis,
        addressMode,
        addresseeCharacterIds: addressMode === 'individual' || addressMode === 'collective'
          ? current.speakerAnalysis.addresseeCharacterIds
          : [],
      },
      speakerBranch: addressMode === 'individual' || addressMode === 'collective'
        ? 'addressees'
        : 'address',
    } : current)
  }, [])

  const toggleSpeakerClassification = useCallback((
    field: 'functions' | 'forms' | 'orientations' | 'stances',
    value: LectioSpeakerDiscourseFunction | LectioSpeakerDiscourseForm | LectioSpeakerOrientation | LectioSpeakerStance,
  ) => {
    setEvidenceMenu(current => {
      if (!current) {
        return current
      }
      const values = current.speakerAnalysis[field] as readonly string[]
      const nextValues = values.includes(value)
        ? values.filter(candidate => candidate !== value)
        : [...values, value]
      return {
        ...current,
        speakerAnalysis: { ...current.speakerAnalysis, [field]: nextValues },
      }
    })
  }, [])

  const toggleSpeakerAddressee = useCallback((characterId: string) => {
    setEvidenceMenu(current => {
      if (!current) {
        return current
      }
      const { addressMode, addresseeCharacterIds } = current.speakerAnalysis
      const nextIds = addressMode === 'individual'
        ? [characterId]
        : addresseeCharacterIds.includes(characterId)
          ? addresseeCharacterIds.filter(candidate => candidate !== characterId)
          : [...addresseeCharacterIds, characterId]
      return {
        ...current,
        speakerAnalysis: { ...current.speakerAnalysis, addresseeCharacterIds: nextIds },
      }
    })
  }, [])

  const setSpeakerRetrospectiveStatus = useCallback((status: LectioSpeakerRetrospectiveStatus) => {
    setEvidenceMenu(current => current ? {
      ...current,
      speakerAnalysis: {
        ...current.speakerAnalysis,
        retrospectiveStatus: status,
        retrospectiveTargets: status === 'linked' ? current.speakerAnalysis.retrospectiveTargets : [],
      },
    } : current)
  }, [])

  const toggleSpeakerRetrospectiveTarget = useCallback((target: LectioSpeakerRetrospectiveTarget) => {
    setEvidenceMenu(current => {
      if (!current) {
        return current
      }
      const isSelected = current.speakerAnalysis.retrospectiveTargets.some(candidate => (
        candidate.kind === target.kind && candidate.targetId === target.targetId
      ))
      const retrospectiveTargets = isSelected
        ? current.speakerAnalysis.retrospectiveTargets.filter(candidate => !(
            candidate.kind === target.kind && candidate.targetId === target.targetId
          ))
        : [...current.speakerAnalysis.retrospectiveTargets, target]
      return {
        ...current,
        speakerAnalysis: {
          ...current.speakerAnalysis,
          retrospectiveStatus: retrospectiveTargets.length > 0 ? 'linked' : 'none',
          retrospectiveTargets,
        },
      }
    })
  }, [])

  const commitSpeakerFromMenu = useCallback(() => {
    if (!evidenceMenu) {
      return
    }
    const { target } = evidenceMenu
    const validationError = getLectioSpeakerAnalysisValidationError(evidenceMenu.speakerAnalysis)
    if (validationError) {
      setAdjudicationNotice(validationError)
      return
    }
    if (target.sourceKind === 'layer' && target.layerKind === 'speaker') {
      const targetNote = characterLayerNotes.find(note => note.note_id === target.evidenceId)
      const turnId = typeof targetNote?.metadata?.speech_turn_id === 'string'
        ? targetNote.metadata.speech_turn_id
        : ''
      const fragmentIds = turnId
        ? characterLayerNotes.filter(note => (
            note.metadata?.character_index_layer === 'speaker' && note.metadata?.speech_turn_id === turnId
          )).map(note => note.note_id)
        : [target.evidenceId]
      fragmentIds.forEach(evidenceId => dispatchAdjudicationCommand({
        op: 'classify-layer',
        sourceKind: 'layer',
        evidenceId,
        layerKind: 'speaker',
        voice: 'figure',
        speakerAnalysis: evidenceMenu.speakerAnalysis,
      }))
      setAdjudicationNotice('Speaker analysis updated without changing the speech turn')
      closeEvidenceMenu(true)
      return
    }
    if (evidenceMenu.pendingSpeakerTurn) {
      const { turnId, speechLevel, fragments } = evidenceMenu.pendingSpeakerTurn
      fragments.forEach((fragment, fragmentIndex) => {
        dispatchAdjudicationCommand({
          op: 'add',
          sourceKind: 'layer',
          evidenceId: fragment.evidenceId,
          characterId: target.characterId,
          evidenceKind: 'speaker',
          witnessId: sourceWitnessId,
          rowId: fragment.rowId,
          start: fragment.start,
          end: fragment.end,
          sourceText: fragment.sourceText,
          speechTurnId: turnId,
          speechLevel,
          speechFragmentIndex: fragmentIndex,
          speechFragmentCount: fragments.length,
          speakerAnalysis: evidenceMenu.speakerAnalysis,
        })
      })
      setFocusedLayerNoteId(fragments[0]?.evidenceId || null)
      setFocusedCharacterOccurrenceId(null)
      setAdjudicationNotice(`One classified speaker turn entered across ${fragments.length} ${fragments.length === 1 ? 'unit' : 'units'}`)
      closeEvidenceMenu(true)
      return
    }
    const speechLevel = resolveSpeakerLevelForRange({
      startRowId: target.rowId,
      startOffset: target.start,
      endRowId: target.rowId,
      endOffset: target.end,
    })
    const evidenceId = `lectio-editor-speaker-${Date.now().toString(36)}-${target.rowId}-${target.start}`
    dispatchAdjudicationCommand({
      op: 'add',
      sourceKind: 'layer',
      evidenceId,
      characterId: target.characterId,
      evidenceKind: 'speaker',
      witnessId: sourceWitnessId,
      rowId: target.rowId,
      start: target.start,
      end: target.end,
      sourceText: rowTextById.get(target.rowId) || '',
      speechTurnId: evidenceId,
      speechLevel,
      speechFragmentIndex: 0,
      speechFragmentCount: 1,
      speakerAnalysis: evidenceMenu.speakerAnalysis,
    })
    setFocusedLayerNoteId(evidenceId)
    setFocusedCharacterOccurrenceId(null)
    setAdjudicationNotice('One speaker turn entered under the editor\'s hand')
    closeEvidenceMenu(true)
  }, [
    closeEvidenceMenu,
    characterLayerNotes,
    dispatchAdjudicationCommand,
    evidenceMenu,
    resolveSpeakerLevelForRange,
    rowTextById,
    sourceWitnessId,
  ])

  const openCharacterizationBranch = useCallback((branch: LectioCharacterizationBranch) => {
    setEvidenceMenu(current => current ? {
      ...current,
      submenu: 'characterization',
      characterizationBranch: branch,
      characterizationDetail: null,
      manualCharacterization: branch === 'direct' || branch === 'indirect'
        ? {
            ...current.manualCharacterization,
            mode: branch,
          }
        : current.manualCharacterization,
    } : current)
  }, [])

  const selectDirectCharacterizationSource = useCallback((source: LectioManualCharacterizationDirectSource) => {
    setEvidenceMenu(current => current ? {
      ...current,
      characterizationBranch: 'direct',
      characterizationDetail: source,
      manualCharacterization: {
        mode: 'direct',
        directSource: source,
        channel: source === 'narratorial' ? undefined : current.manualCharacterization.channel,
        sourceCharacterId: source === 'other' ? current.manualCharacterization.sourceCharacterId : undefined,
        forms: source === 'narratorial'
          ? current.manualCharacterization.forms
          : current.manualCharacterization.forms.filter(form => form !== 'reputation'),
        modifiers: current.manualCharacterization.modifiers,
      },
    } : current)
  }, [])

  const selectIndirectCharacterizationChannel = useCallback((
    channel: LectioManualCharacterizationIndirectChannel,
  ) => {
    setEvidenceMenu(current => current ? {
      ...current,
      characterizationBranch: 'indirect',
      characterizationDetail: channel === 'action_conduct'
        ? 'action'
        : channel === 'others_reaction'
          ? 'reaction'
          : null,
      manualCharacterization: {
        mode: 'indirect',
        indirectChannel: channel,
        indirectDetail: channel === 'action_conduct'
          && LECTIO_MANUAL_CHARACTERIZATION_ACTION_DETAILS.some(
            detail => detail.id === current.manualCharacterization.indirectDetail,
          )
          ? current.manualCharacterization.indirectDetail
          : channel === 'others_reaction'
            && LECTIO_MANUAL_CHARACTERIZATION_REACTION_DETAILS.some(
              detail => detail.id === current.manualCharacterization.indirectDetail,
            )
            ? current.manualCharacterization.indirectDetail
            : undefined,
        relatedCharacterId: channel === 'others_reaction'
          ? current.manualCharacterization.relatedCharacterId
          : undefined,
        forms: [],
        modifiers: current.manualCharacterization.modifiers,
      },
    } : current)
  }, [])

  const toggleManualCharacterizationForm = useCallback((form: LectioManualCharacterizationForm) => {
    setEvidenceMenu(current => {
      if (!current) {
        return current
      }
      const selected = current.manualCharacterization.forms.includes(form)
      return {
        ...current,
        manualCharacterization: {
          ...current.manualCharacterization,
          forms: selected
            ? current.manualCharacterization.forms.filter(candidate => candidate !== form)
            : [...current.manualCharacterization.forms, form],
        },
      }
    })
  }, [])

  const toggleManualCharacterizationModifier = useCallback((
    modifier: LectioManualCharacterizationModifier,
  ) => {
    setEvidenceMenu(current => {
      if (!current) {
        return current
      }
      const selected = current.manualCharacterization.modifiers.includes(modifier)
      return {
        ...current,
        manualCharacterization: {
          ...current.manualCharacterization,
          modifiers: selected
            ? current.manualCharacterization.modifiers.filter(candidate => candidate !== modifier)
            : [...current.manualCharacterization.modifiers, modifier],
        },
      }
    })
  }, [])

  const selectManualCharacterizationChannel = useCallback((
    channel: LectioManualCharacterizationChannel,
  ) => {
    setEvidenceMenu(current => current ? {
      ...current,
      manualCharacterization: { ...current.manualCharacterization, channel },
    } : current)
  }, [])

  const selectManualCharacterizationIndirectDetail = useCallback((
    detail: LectioManualCharacterizationIndirectDetail,
  ) => {
    setEvidenceMenu(current => current ? {
      ...current,
      manualCharacterization: { ...current.manualCharacterization, indirectDetail: detail },
    } : current)
  }, [])

  const openManualCharacterizationCharacterPicker = useCallback((kind: 'other' | 'reaction') => {
    setEvidenceMenu(current => current ? {
      ...current,
      characterizationDetail: kind === 'other'
        ? 'other-character-picker'
        : 'reaction-character-picker',
    } : current)
  }, [])

  const selectManualCharacterizationRelatedCharacter = useCallback((
    kind: 'other' | 'reaction',
    characterId: string,
  ) => {
    setEvidenceMenu(current => current ? {
      ...current,
      characterizationDetail: kind,
      manualCharacterization: {
        ...current.manualCharacterization,
        ...(kind === 'other'
          ? { sourceCharacterId: characterId }
          : { relatedCharacterId: characterId }),
      },
    } : current)
  }, [])

  const openClaimSubmenu = useCallback(() => {
    setEvidenceMenu(current => current ? {
      ...current,
      submenu: 'claim',
      characterizationBranch: null,
      characterizationDetail: null,
    } : current)
  }, [])

  const openShareSubmenu = useCallback(() => {
    setEvidenceMenu(current => current ? {
      ...current,
      submenu: 'share',
      characterizationBranch: null,
      characterizationDetail: null,
    } : current)
  }, [])

  const commitLayerClassificationFromMenu = useCallback(() => {
    if (
      !evidenceMenu?.submenu ||
      evidenceMenu.submenu === 'claim' ||
      evidenceMenu.submenu === 'share'
    ) {
      return
    }
    const { target, submenu, voice } = evidenceMenu
    const manualCharacterization = submenu === 'characterization'
      ? finalizeManualCharacterization(evidenceMenu.manualCharacterization)
      : null
    if (submenu === 'characterization') {
      const validationError = getManualCharacterizationValidationError(evidenceMenu.manualCharacterization)
      if (validationError || !manualCharacterization) {
        setAdjudicationNotice(validationError || 'Complete the characterization judgment')
        return
      }
    }
    if (target.sourceKind === 'layer') {
      dispatchAdjudicationCommand({
        op: 'classify-layer',
        sourceKind: 'layer',
        evidenceId: target.evidenceId,
        layerKind: submenu,
        ...(submenu === 'focalization' ? { classification: evidenceMenu.classification } : {}),
        ...(manualCharacterization ? { manualCharacterization } : {}),
        voice,
      })
      setAdjudicationNotice(`${formatLectioMenuChoice(submenu)} reading classified and admitted`)
    } else {
      const evidenceId = `lectio-editor-layer-${Date.now().toString(36)}-${target.rowId}-${target.start}`
      dispatchAdjudicationCommand({
        op: 'add',
        sourceKind: 'layer',
        evidenceId,
        characterId: target.characterId,
        evidenceKind: submenu,
        witnessId: sourceWitnessId,
        rowId: target.rowId,
        start: target.start,
        end: target.end,
        sourceText: rowTextById.get(target.rowId) || '',
        ...(submenu === 'focalization' ? { classification: evidenceMenu.classification } : {}),
        ...(manualCharacterization ? { manualCharacterization } : {}),
        voice,
      })
      setFocusedLayerNoteId(evidenceId)
      setFocusedCharacterOccurrenceId(null)
      setAdjudicationNotice(`One ${submenu} reading entered under the editor's hand`)
    }
    closeEvidenceMenu(true)
  }, [closeEvidenceMenu, dispatchAdjudicationCommand, evidenceMenu, rowTextById, sourceWitnessId])

  const claimReadingFromMenu = useCallback((receiverId: string, receiverLabel: string) => {
    if (!evidenceMenu || evidenceMenu.target.sourceKind === 'draft') {
      return
    }
    if (!receiverId || receiverId === evidenceMenu.target.characterId) {
      return
    }
    dispatchAdjudicationCommand({
      op: 'claim',
      sourceKind: evidenceMenu.target.sourceKind,
      evidenceId: evidenceMenu.target.evidenceId,
      characterId: receiverId,
    })
    const readingLabel = evidenceMenu.target.sourceKind === 'layer'
      ? `${evidenceMenu.target.layerKind || 'analytic'} span`
      : 'occurrence'
    setAdjudicationNotice(`This ${readingLabel} claimed for ${receiverLabel} · other readings unchanged`)
    closeEvidenceMenu(false)
  }, [closeEvidenceMenu, dispatchAdjudicationCommand, evidenceMenu])

  const toggleSharedReferentFromMenu = useCallback((characterId: string, characterLabel: string) => {
    if (!evidenceMenu || evidenceMenu.target.sourceKind === 'layer') {
      return
    }
    const currentReferents = Array.from(new Set([
      evidenceMenu.target.characterId,
      ...(evidenceMenu.target.referentCharacterIds || []),
    ]))
    const isCurrentlyShared = currentReferents.includes(characterId)
    if (evidenceMenu.target.sourceKind === 'occurrence') {
      dispatchAdjudicationCommand({
        op: 'toggle-shared-referent',
        sourceKind: 'occurrence',
        evidenceId: evidenceMenu.target.evidenceId,
        characterId,
      })
    }
    const nextReferents = isCurrentlyShared
      ? currentReferents.filter(id => id !== characterId)
      : [...currentReferents, characterId]
    setEvidenceMenu(current => current ? {
      ...current,
      target: {
        ...current.target,
        referentCharacterIds: nextReferents,
      },
    } : current)
    setAdjudicationNotice(evidenceMenu.target.sourceKind === 'draft'
      ? `${characterLabel} ${isCurrentlyShared ? 'removed from' : 'added to'} the pending shared reference`
      : isCurrentlyShared
        ? `${characterLabel} removed from this shared reference`
        : `${characterLabel} added to this shared reference · one occurrence retained`)
  }, [dispatchAdjudicationCommand, evidenceMenu])

  const commitSharedReferenceFromMenu = useCallback(() => {
    if (!evidenceMenu || evidenceMenu.target.sourceKind !== 'draft') {
      return
    }
    const target = evidenceMenu.target
    const additionalReferentIds = Array.from(new Set(target.referentCharacterIds || []))
      .filter(characterId => characterId !== target.characterId)
    if (additionalReferentIds.length === 0) {
      return
    }
    const evidenceId = `lectio-editor-occurrence-${Date.now().toString(36)}-${target.rowId}-${target.start}`
    dispatchAdjudicationCommand({
      op: 'add',
      sourceKind: 'occurrence',
      evidenceId,
      characterId: target.characterId,
      evidenceKind: 'coref',
      witnessId: sourceWitnessId,
      rowId: target.rowId,
      start: target.start,
      end: target.end,
      sourceText: rowTextById.get(target.rowId) || '',
    })
    additionalReferentIds.forEach(characterId => {
      dispatchAdjudicationCommand({
        op: 'toggle-shared-referent',
        sourceKind: 'occurrence',
        evidenceId,
        characterId,
      })
    })
    setFocusedCharacterOccurrenceId(evidenceId)
    setFocusedLayerNoteId(null)
    setAdjudicationNotice(`One shared coreference entered for ${additionalReferentIds.length + 1} figures`)
    closeEvidenceMenu(true)
  }, [
    closeEvidenceMenu,
    dispatchAdjudicationCommand,
    evidenceMenu,
    rowTextById,
    sourceWitnessId,
  ])

  const issueEvidenceVerdictFromMenu = useCallback((op: 'admit' | 'relegate' | 'remove') => {
    if (!evidenceMenu || evidenceMenu.target.sourceKind === 'draft') {
      return
    }
    dispatchAdjudicationCommand({
      op,
      sourceKind: evidenceMenu.target.sourceKind,
      evidenceId: evidenceMenu.target.evidenceId,
    })
    if (op === 'remove' && onArchiveAnnotationResearchNotesForTarget) {
      const researchTarget = buildResearchNoteTargetForEvidence(evidenceMenu.target)
      const hasSurvivingSpeakerFragment = researchTarget?.kind === 'speaker' && researchTarget.logicalId
        ? Array.from(sourceLayerAnnotationsByRowId.values()).flat().some(annotation => (
            annotation.kind === 'speaker' &&
            annotation.speechTurnId === researchTarget.logicalId &&
            annotation.id !== evidenceMenu.target.evidenceId
          ))
        : false
      if (researchTarget && !hasSurvivingSpeakerFragment) {
        const archiveResult = onArchiveAnnotationResearchNotesForTarget(researchTarget)
        if (archiveResult && typeof (archiveResult as Promise<void>).catch === 'function') {
          void (archiveResult as Promise<void>).catch(error => {
            setAdjudicationNotice(
              error instanceof Error ? error.message : 'The attached research notes could not be archived',
            )
          })
        }
      }
    }
    setAdjudicationNotice(
      op === 'admit'
        ? 'Reading admitted to the edition'
        : op === 'relegate'
          ? 'Reading relegated non-destructively'
          : 'Reading removed; source text unchanged',
    )
    closeEvidenceMenu(op !== 'remove')
  }, [
    buildResearchNoteTargetForEvidence,
    closeEvidenceMenu,
    dispatchAdjudicationCommand,
    evidenceMenu,
    onArchiveAnnotationResearchNotesForTarget,
    sourceLayerAnnotationsByRowId,
  ])

  const resizeTweezedBoundary = useCallback((command: LectioAdjudicationResizeCommand, rowLabel: string) => {
    dispatchAdjudicationCommand(command)
    setAdjudicationNotice(
      `${rowLabel} · ${command.start}–${command.end} · admitted under the editor's hand`,
    )
  }, [dispatchAdjudicationCommand])

  const verdictAllSubJudice = useCallback((characterId: string, verdict: 'admit' | 'relegate') => {
    const targets = allLectioEvidence.filter(item => (
      lectioEvidenceRefersTo(item, characterId) &&
      item.witnessId === sourceWitnessId &&
      item.standing === 'subjudice'
    ))
    targets.forEach(item => dispatchAdjudicationCommand({
      op: verdict,
      sourceKind: item.sourceKind === 'occurrence' ? 'occurrence' : 'layer',
      evidenceId: item.id,
    }))
    setAdjudicationNotice(
      `${verdict === 'admit' ? 'Admitted' : 'Relegated'} ${targets.length} reading${targets.length === 1 ? '' : 's'} sub judice`,
    )
  }, [allLectioEvidence, dispatchAdjudicationCommand, sourceWitnessId])

  const restoreAdjudication = useCallback(() => {
    if (!adjudicationSession) {
      return
    }
    dispatchAdjudicationCommand({ op: 'restore', session: adjudicationSession })
    const openingCharacterId = adjudicationSession.openingCharacterId || adjudicationSession.characterId
    setAdjudicationSession(current => current ? {
      ...current,
      characterId: openingCharacterId,
      touched: [],
      mergedCharacterIds: [],
    } : current)
    setActiveMarginalCharacterId(openingCharacterId)
    onSelectCharacter?.(openingCharacterId)
    setFocusedCharacterOccurrenceId(null)
    setFocusedLayerNoteId(null)
    setAdjudicationNotice('Session restored to its opening state')
  }, [adjudicationSession, dispatchAdjudicationCommand, onSelectCharacter])

  const stepBackCharacterIndex = useCallback((): boolean => {
    if (evidenceMenu) {
      closeEvidenceMenu(true)
      return true
    }
    if (renamingCharacterId) {
      setRenamingCharacterId('')
      setCharacterRenameName('')
      setCharacterRenameError('')
      return true
    }
    if (deletingCharacterId) {
      setDeletingCharacterId('')
      setCharacterDeleteError('')
      return true
    }
    if (characterCreationOpen) {
      setCharacterCreationOpen(false)
      setCharacterCreationName('')
      setCharacterCreationError('')
      return true
    }
    if (inlineCandidate) {
      restoreReaderSurfaceFocusRef.current = true
      setInlineCandidate(null)
      return true
    }
    if (showContextualAssistance) {
      restoreReaderSurfaceFocusRef.current = true
      setShowContextualAssistance(false)
      return true
    }
    if (customSectionTypeOpen) {
      setCustomSectionTypeOpen(false)
      return true
    }
    if (rapidSectionStart || editingStructureSectionId) {
      setRapidSectionStart(null)
      setRapidSectionPhase('opening')
      setRapidSectionTypeId('')
      setEditingStructureSectionId('')
      setRapidSectionTitle('')
      rapidSectionTitleRef.current = ''
      setAdjudicationNotice('Section draft cancelled · section tool remains armed')
      return true
    }
    if (rapidSpanAnchor) {
      setRapidSpanAnchor(null)
      setAdjudicationNotice(`${rapidTool || 'analytic'} span cancelled · marking tool remains armed`)
      return true
    }
    if (rapidTool) {
      setRapidTool(null)
      setRapidSectionStart(null)
      setRapidSectionPhase('opening')
      setRapidSectionTypeId('')
      setEditingStructureSectionId('')
      setAdjudicationNotice('Rapid marking off · the contextual menu remains available')
      return true
    }
    if (adjudicationSession) {
      closeAdjudication()
      return true
    }
    if (showTextualBeings) {
      restoreReaderSurfaceFocusRef.current = true
      setShowTextualBeings(false)
      return true
    }
    if (evidenceProjection === 'analytic') {
      restoreReaderSurfaceFocusRef.current = true
      setEvidenceProjection('full-text')
      setExpandedLectioFoldIds(new Set())
      return true
    }
    if (openLineApparatusRowIds.size > 0) {
      setOpenLineApparatusRowIds(new Set())
      return true
    }
    if (isolatedEvidenceKind) {
      setIsolatedEvidenceKind(null)
      return true
    }
    if (activeCharacterId) {
      clearCharacterSelection()
      return true
    }
    return false
  }, [
    activeCharacterId,
    adjudicationSession,
    characterCreationOpen,
    customSectionTypeOpen,
    clearCharacterSelection,
    closeEvidenceMenu,
    closeAdjudication,
    evidenceProjection,
    evidenceMenu,
    deletingCharacterId,
    inlineCandidate,
    isolatedEvidenceKind,
    openLineApparatusRowIds.size,
    editingStructureSectionId,
    rapidSectionStart,
    rapidSpanAnchor,
    rapidTool,
    renamingCharacterId,
    showContextualAssistance,
    showTextualBeings,
  ])

  const handleReaderKeyDown = useCallback((event: React.KeyboardEvent<HTMLElement>) => {
    if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.altKey) {
      return
    }
    const target = event.target instanceof HTMLElement ? event.target : null
    if (
      target &&
      (
        target.isContentEditable ||
        target.closest('input, textarea, select, a[href], [contenteditable="true"], [role="dialog"]')
      )
    ) {
      return
    }
    /* The margin scopes its own keys; the stage grammar must keep working from its many
       buttons (chips, row labels, verbs), so only native activation keys pass through. */
    if (target?.closest('.lectio-character-margin')) {
      return
    }
    if (target?.closest('button') && (event.key === 'Enter' || event.key === ' ')) {
      return
    }

    const key = event.key.toLowerCase()
    const hasCharacterFocus = Boolean(selectedCharacterId || activeMarginalCharacterId || focusedCharacterOccurrenceId || focusedLayerNoteId)

    if (readerSection === 'character-index') {
      /* The cursor is pure: without an attended figure it walks source rows and never
         mutates attention; with one it walks only that figure's readings. */
      if (event.key === 'ArrowDown' || key === 'j') {
        event.preventDefault()
        if (activeCharacterId) {
          moveLectioEvidence(1)
        } else {
          moveUnit(1)
        }
      } else if (event.key === 'ArrowUp' || key === 'k') {
        event.preventDefault()
        if (activeCharacterId) {
          moveLectioEvidence(-1)
        } else {
          moveUnit(-1)
        }
      } else if (event.key === 'Enter') {
        const focusedId = focusedCharacterOccurrenceId || focusedLayerNoteId
        const focusedEvidence = lectioEvidenceItinerary.find(item => item.id === focusedId)
        if (focusedEvidence) {
          event.preventDefault()
          toggleLineApparatus(focusedEvidence.rowId)
        } else if (selectedUnit) {
          event.preventDefault()
          toggleLineApparatus(selectedUnit.rowId)
        }
      } else if (key === 'l' && activeCharacterId) {
        event.preventDefault()
        restoreReaderSurfaceFocusRef.current = true
        setEvidenceProjection('analytic')
        setIsolatedEvidenceKind(null)
      } else if (key === 'a' && activeCharacterId && onAdjudicateCharacterEvidence) {
        event.preventDefault()
        enterAdjudication()
      } else if (key === 'g') {
        event.preventDefault()
        restoreReaderSurfaceFocusRef.current = true
        setShowTextualBeings(true)
      } else if (event.key === 'Escape') {
        event.preventDefault()
        if (!stepBackCharacterIndex()) {
          onClose()
        }
      }
      return
    }

    if (focusLevel === 'reader') {
      if (event.key === 'ArrowDown' || key === 'j') {
        event.preventDefault()
        moveUnit(1)
      } else if (event.key === 'ArrowUp' || key === 'k') {
        event.preventDefault()
        moveUnit(-1)
      } else if (event.key === 'Enter' && readerSection === 'compare') {
        event.preventDefault()
        setFocusLevel('apparatus')
        setSelectedPeerIndex(0)
      } else if (event.key === 'Escape') {
        event.preventDefault()
        if (hasCharacterFocus) {
          clearCharacterSelection()
        } else {
          onClose()
        }
      }
      return
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setSelectedPeerIndex(current => (
        selectedPeers.length > 0 ? Math.min(selectedPeers.length - 1, current + 1) : 0
      ))
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setSelectedPeerIndex(current => Math.max(0, current - 1))
    } else if (key === 'j') {
      event.preventDefault()
      moveUnit(1)
    } else if (key === 'k') {
      event.preventDefault()
      moveUnit(-1)
    } else if (event.key === 'Escape') {
      event.preventDefault()
      if (hasCharacterFocus) {
        clearCharacterSelection()
      } else {
        setFocusLevel('reader')
      }
    }
  }, [
    activeMarginalCharacterId,
    activeCharacterId,
    clearCharacterSelection,
    enterAdjudication,
    focusedCharacterOccurrenceId,
    focusedLayerNoteId,
    focusLevel,
    lectioEvidenceItinerary,
    moveUnit,
    moveLectioEvidence,
    onClose,
    onAdjudicateCharacterEvidence,
    readerSection,
    selectedCharacterId,
    selectedPeers.length,
    selectedUnit,
    stepBackCharacterIndex,
    toggleLineApparatus,
  ])

  const apparatusViewLabel = readerSection === 'compare' ? 'Mapped rows' : 'Reader'

  const lectionBlocks = useMemo<LectioLectionBlock[]>(() => {
    if (!activeCharacterId || evidenceProjection === 'full-text') {
      return []
    }
    const kinds: LectioEvidenceKind[] = evidenceProjection === 'chain'
      ? ['name', 'coref']
      : evidenceProjection === 'speaker'
        ? ['speaker']
      : evidenceProjection === 'focalization'
        ? ['focalization']
      : evidenceProjection === 'characterization'
          ? ['characterization']
          : ['focalization', 'characterization']
    const kindSet = new Set(kinds)
    const rowOrder = new Map(lectioRowOrder.map((rowId, index) => [rowId, index]))
    const byRow = new Map<string, Array<{
      start: number
      end: number
      evidence: LectioEvidenceRecord[]
    }>>()
    lectioEvidence
      .filter(item => (
        lectioEvidenceRefersTo(item, activeCharacterId) &&
        item.witnessId === sourceWitnessId &&
        kindSet.has(item.kind) &&
        rowOrder.has(item.rowId)
      ))
      .slice()
      .sort((left, right) => (
        (rowOrder.get(left.rowId) ?? Number.MAX_SAFE_INTEGER) -
          (rowOrder.get(right.rowId) ?? Number.MAX_SAFE_INTEGER) ||
        left.start - right.start ||
        left.end - right.end ||
        left.id.localeCompare(right.id)
      ))
      .forEach(item => {
        const rowText = rowTextById.get(item.rowId) || ''
        const start = Math.max(0, Math.min(rowText.length, item.start))
        const end = Math.max(start, Math.min(rowText.length, item.end))
        if (end <= start) {
          return
        }
        const intervals = byRow.get(item.rowId) || []
        const previous = intervals[intervals.length - 1]
        if (previous && start <= previous.end + 1) {
          previous.end = Math.max(previous.end, end)
          previous.evidence.push(item)
        } else {
          intervals.push({ start, end, evidence: [item] })
        }
        byRow.set(item.rowId, intervals)
      })

    const blocks: LectioLectionBlock[] = []
    let silentRowIds: string[] = []
    const flushSilentRows = () => {
      if (silentRowIds.length === 0) {
        return
      }
      const rowIds = [...silentRowIds]
      blocks.push({
        type: 'fold',
        scope: 'inter',
        id: `fold|${rowIds[0]}|${rowIds[rowIds.length - 1]}`,
        rowIds,
        text: rowIds.map(rowId => rowTextById.get(rowId) || '').join(' '),
      })
      silentRowIds = []
    }

    lectioRowOrder.forEach(rowId => {
      const rowText = rowTextById.get(rowId) || ''
      const intervals = byRow.get(rowId)
      if (!intervals?.length) {
        silentRowIds.push(rowId)
        return
      }
      flushSilentRows()
      const items: Array<LectioLectionSpan | LectioIntraFold> = []
      let cursor = 0
      intervals.forEach(interval => {
        if (interval.start > cursor) {
          items.push({
            type: 'fold',
            scope: 'intra',
            id: `fold|${rowId}|${cursor}|${interval.start}`,
            rowIds: [rowId],
            start: cursor,
            end: interval.start,
            text: rowText.slice(cursor, interval.start),
          })
        }
        items.push({
          type: 'span',
          id: `lection|${rowId}|${interval.start}|${interval.end}`,
          rowId,
          start: interval.start,
          end: interval.end,
          text: rowText.slice(interval.start, interval.end),
          evidenceIds: interval.evidence.map(item => item.id),
          kinds: Array.from(new Set(interval.evidence.map(item => item.kind))),
        })
        cursor = interval.end
      })
      if (cursor < rowText.length) {
        items.push({
          type: 'fold',
          scope: 'intra',
          id: `fold|${rowId}|${cursor}|${rowText.length}`,
          rowIds: [rowId],
          start: cursor,
          end: rowText.length,
          text: rowText.slice(cursor),
        })
      }
      blocks.push({ type: 'row', rowId, items })
    })
    flushSilentRows()
    return blocks
  }, [
    activeCharacterId,
    evidenceProjection,
    lectioEvidence,
    lectioRowOrder,
    rowTextById,
    sourceWitnessId,
  ])
  const activeRosterEntry = activeCharacterId ? lectioRosterById.get(activeCharacterId) : null
  const activeCharacterLabel = activeCharacterId
    ? getCharacterDisplayLabel(effectiveCharacterIndexEntries.find(entry => entry.id === activeCharacterId) || ({
      id: activeCharacterId,
      lemma: activeCharacterId,
      names: { primary: activeCharacterId },
    } as CharacterIndexEntry))
    : ''
  const activeCharacterColor = activeCharacterId
    ? characterDecorationsById.get(activeCharacterId)?.color || getCharacterColor(activeCharacterId)
    : 'var(--cte-ink-2)'
  const activeAnnotationGrammar = LECTIO_ANNOTATION_GRAMMARS.find(grammar => grammar.id === annotationGrammar) ||
    LECTIO_ANNOTATION_GRAMMARS[0]
  const adjudicationReadings = adjudicationSession
    ? allLectioEvidence.filter(item => (
        lectioEvidenceRefersTo(item, adjudicationSession.characterId) && item.standing !== 'relegated'
      ))
    : []
  const adjudicationSubJudiceCount = adjudicationReadings.filter(item => item.standing === 'subjudice').length
  const adjudicationReceivedCount = adjudicationReadings.filter(item => item.standing === 'received').length

  const isolateCharacterEvidence = (kind: LectioIsolationKind) => {
    if (!activeCharacterId) {
      return
    }
    setEvidenceProjection('full-text')
    setExpandedLectioFoldIds(new Set())
    setIsolatedEvidenceKind(current => current === kind ? null : kind)
    setShowTextualBeings(false)
  }

  const toggleVisibleEvidenceKind = (kind: LectioIsolationKind) => {
    setVisibleEvidenceKinds(current => ({ ...current, [kind]: !current[kind] }))
  }

  const openCharacterLections = () => {
    if (!activeCharacterId) {
      return
    }
    restoreReaderSurfaceFocusRef.current = true
    setIsolatedEvidenceKind(null)
    setEvidenceProjection('analytic')
    setShowTextualBeings(false)
  }

  const showTextualBeingGallery = () => {
    restoreReaderSurfaceFocusRef.current = true
    setShowTextualBeings(true)
  }

  const returnToContinuousCharacterText = () => {
    restoreReaderSurfaceFocusRef.current = true
    setShowTextualBeings(false)
    setEvidenceProjection('full-text')
    setExpandedLectioFoldIds(new Set())
  }

  const lectioHeadnote = (
    <div className="lectio-reader-headnote" data-testid="lectio-reader-headnote" aria-live="polite">
      {adjudicationSession ? (
        <>
          Adjudicating the readings for{' '}
          <span className="lectio-headnote-name">{activeCharacterLabel || adjudicationSession.characterId}</span>
          {' '}— {adjudicationSubJudiceCount} sub judice, {adjudicationReceivedCount} received — admit or relegate each
          below its line; click a bare word to classify a new reading in place.{' '}
          <button type="button" aria-label="Restore the session" onClick={restoreAdjudication}>Restore the session</button>{' · '}
          <button type="button" aria-label="close adjudication" onClick={closeAdjudication}>close</button>.
        </>
      ) : showContextualAssistance ? (
        <>
          The second hand attends{' '}
          <span className="lectio-headnote-name">{activeCharacterLabel || inlineCandidate?.text || 'the selected reading'}</span>
          {' '}against immutable <b>{sourceLabel}</b>; suggestions remain sub judice until the editor accepts them.{' '}
          <button type="button" onClick={() => setShowContextualAssistance(false)}>Dismiss tools</button>.
        </>
      ) : inlineCandidate ? (
        <>
          Treating <span className="lectio-headnote-name">{inlineCandidate.text}</span> as unindexed source text in{' '}
          <b>{rowLabelById.get(inlineCandidate.rowId) || inlineCandidate.rowId}</b>; its bounded brief and returned
          readings remain inline and sub judice until adjudication.{' '}
          <button type="button" onClick={() => setInlineCandidate(null)}>Dismiss candidate</button>.
        </>
      ) : showTextualBeings ? (
        <>
          All indexed persons are gathered as textual beings in <b>{sourceLabel}</b>; only focalization and
          characterization construct each card.{' '}
          <button type="button" onClick={returnToContinuousCharacterText}>Return to continuous text</button>.
        </>
      ) : evidenceProjection === 'analytic' && activeCharacterId ? (
        <>
          Lections for <span className="lectio-headnote-name">{activeCharacterLabel}</span> gather focalization and
          characterization only; omitted source remains available beneath each fold.{' '}
          <button type="button" onClick={returnToContinuousCharacterText}>Return to continuous text</button>.
        </>
      ) : activeCharacterId ? (
        <>
          Attending <span className="lectio-headnote-name">{activeCharacterLabel}</span> in <b>{sourceLabel}</b>
          {isolatedEvidenceKind ? <>; isolating <b>{isolatedEvidenceKind}</b> across the continuous text</> : <> continuously</>}.{' '}
          <button
            type="button"
            className={isolatedEvidenceKind === 'chain' ? 'active' : ''}
            onClick={() => isolateCharacterEvidence('chain')}
          >Chain · {activeRosterEntry?.counts.chain || 0}</button>{' · '}
          <button
            type="button"
            className={isolatedEvidenceKind === 'speaker' ? 'active' : ''}
            onClick={() => isolateCharacterEvidence('speaker')}
          >Spk · {activeRosterEntry?.counts.speaker || 0}</button>{' · '}
          <button
            type="button"
            className={isolatedEvidenceKind === 'focalization' ? 'active' : ''}
            onClick={() => isolateCharacterEvidence('focalization')}
          >Foc · {activeRosterEntry?.counts.focalization || 0}</button>{' · '}
          <button
            type="button"
            className={isolatedEvidenceKind === 'characterization' ? 'active' : ''}
            onClick={() => isolateCharacterEvidence('characterization')}
          >Char · {activeRosterEntry?.counts.characterization || 0}</button>{' · '}
          <button type="button" onClick={openCharacterLections}>Lections</button>{' · '}
          <button type="button" onClick={clearCharacterSelection}>Release</button>.
        </>
      ) : (
        <>
          Reading <b>{sourceLabel}</b> continuously with{' '}
          <button
            type="button"
            aria-pressed={visibleEvidenceKinds.chain}
            onClick={() => toggleVisibleEvidenceKind('chain')}
          >Chain</button>,{' '}
          <button
            type="button"
            aria-pressed={visibleEvidenceKinds.speaker}
            onClick={() => toggleVisibleEvidenceKind('speaker')}
          >Spk</button>,{' '}
          <button
            type="button"
            aria-pressed={visibleEvidenceKinds.focalization}
            onClick={() => toggleVisibleEvidenceKind('focalization')}
          >Foc</button>, and{' '}
          <button
            type="button"
            aria-pressed={visibleEvidenceKinds.characterization}
            onClick={() => toggleVisibleEvidenceKind('characterization')}
          >Char</button> evidence in place. Attend a
          person from the Index nominum or open the{' '}
          <button type="button" onClick={showTextualBeingGallery}>gallery of textual beings</button>.
        </>
      )}
      {!showTextualBeings && evidenceProjection === 'full-text' && (
        <div className="lectio-grammar-control">
          <label htmlFor="lectio-annotation-grammar">Notation</label>
          <select
            id="lectio-annotation-grammar"
            aria-label="Annotation grammar"
            value={annotationGrammar}
            onChange={event => setAnnotationGrammar(event.target.value as LectioAnnotationGrammar)}
          >
            {LECTIO_ANNOTATION_GRAMMARS.map(grammar => (
              <option key={grammar.id} value={grammar.id}>{grammar.label}</option>
            ))}
          </select>
          <span>{activeAnnotationGrammar.note}</span>
        </div>
      )}
      <span className="lectio-headnote-rule" aria-hidden="true" />
    </div>
  )

  const lineApparatusFor = (unit: LedgerSafeTraditionUnit) => {
    const rowReadings = allLectioEvidence.filter(item => (
      item.rowId === unit.rowId &&
      item.witnessId === sourceWitnessId &&
      item.standing !== 'relegated'
    ))
    const readings = rowReadings.filter(item => item.start >= 0 && item.end > item.start)
    const unplacedCount = rowReadings.length - readings.length
    const isAdjudicating = Boolean(adjudicationSession && activeCharacterId)
    return (
      <div className="lectio-line-apparatus" role="region" aria-label={`${unit.rowLabel} character apparatus`}>
        <div className="lectio-line-apparatus-head">
          <span>Apparatus · {unit.rowLabel} · {readings.length} {readings.length === 1 ? 'reading' : 'readings'}</span>
          <button type="button" onClick={() => toggleLineApparatus(unit.rowId)}>close</button>
        </div>
        {readings.length === 0 ? (
          <p>No character readings are placed on this line.</p>
        ) : readings.map(reading => {
          const character = effectiveCharacterIndexEntries.find(entry => entry.id === reading.characterId)
          const label = character ? getCharacterDisplayLabel(character) : reading.characterId
          const decoration = characterDecorationsById.get(reading.characterId)
          const sourceKind = reading.sourceKind || (
            reading.kind === 'name' || reading.kind === 'coref' ? 'occurrence' : 'layer'
          )
          const standing = reading.standing || 'received'
          const isAttendedReading = reading.characterId === activeCharacterId
          const siglum = standing === 'admitted' ? 'E' : standing === 'subjudice' ? 'M' : 'R'
          const meta = sourceKind === 'occurrence'
            ? `chain · ${reading.kind}`
            : `${reading.kind} · ${reading.subtype || 'unclassified'} · ${reading.voice || 'figure'}`
          const issue = (command: LectioAdjudicationCommand, notice: string) => {
            dispatchAdjudicationCommand(command)
            setAdjudicationNotice(notice)
          }
          return (
            <div
              key={reading.id}
              className={[
                'lectio-line-apparatus-entry',
                isAdjudicating && isAttendedReading ? 'lit' : '',
                standing === 'subjudice' ? 'subjudice' : '',
                isAdjudicating && !isAttendedReading ? 'other-hand' : '',
              ].filter(Boolean).join(' ')}
              data-app-entry={reading.id}
            >
              <button
                type="button"
                className="lectio-apparatus-lemma"
                onClick={() => focusLectioEvidence(reading)}
                style={{ '--cte-character-color': decoration?.color || getCharacterColor(reading.characterId) } as React.CSSProperties}
              >
                <span className="lectio-apparatus-chip">{decoration?.chip || '?'}</span>
                {reading.text}
              </button>
              <span className="lectio-apparatus-meta">] {meta} · {unit.rowLabel} {reading.start}–{reading.end}</span>
              <span className={`lectio-apparatus-siglum ${siglum === 'E' ? 'editor' : siglum === 'M' ? 'model' : ''}`}>⟨{siglum}⟩</span>
              <b className={`lectio-apparatus-standing ${standing}`}>{standing === 'subjudice' ? 'sub judice' : standing}</b>
              {isAdjudicating && isAttendedReading && (
                <span className="lectio-apparatus-verbs">
                  {(standing === 'received' || standing === 'subjudice') && <>
                    <button type="button" className="admit" onClick={() => issue(
                      { op: 'admit', sourceKind, evidenceId: reading.id },
                      'Reading admitted to the edition',
                    )}>admit</button>
                    <span>·</span>
                    <button type="button" className="relegate" onClick={() => issue(
                      { op: 'relegate', sourceKind, evidenceId: reading.id },
                      'Reading relegated non-destructively',
                    )}>relegate</button>
                    <span>·</span>
                  </>}
                  <button type="button" className="remove" onClick={() => issue(
                    { op: 'remove', sourceKind, evidenceId: reading.id },
                    'Reading removed; source text unchanged',
                  )}>remove</button>
                </span>
              )}
              {isAdjudicating && !isAttendedReading && standing !== 'subjudice' && sourceKind === 'occurrence' && (
                <span className="lectio-apparatus-verbs">
                  <button type="button" onClick={() => issue({
                    op: 'claim',
                    sourceKind: 'occurrence',
                    evidenceId: reading.id,
                    characterId: activeCharacterId,
                  }, `This occurrence claimed for ${activeCharacterLabel}`)}>claim for {activeCharacterLabel}</button>
                </span>
              )}
              {!isAdjudicating && <span className="lectio-apparatus-owner">{label}</span>}
            </div>
          )
        })}
        {unplacedCount > 0 && (
          <p className="lectio-apparatus-note">
            {unplacedCount} record{unplacedCount === 1 ? '' : 's'} without usable offsets remain{unplacedCount === 1 ? 's' : ''} counted, not shown
          </p>
        )}
      </div>
    )
  }

  const handleInlineCandidateSelection = () => {
    if (readerSection !== 'character-index' || typeof window.getSelection !== 'function') {
      return
    }
    const selection = window.getSelection()
    if (!selection || selection.isCollapsed || selection.rangeCount === 0) {
      return
    }
    const anchorElement = selection.anchorNode instanceof Element
      ? selection.anchorNode
      : selection.anchorNode?.parentElement
    if (!anchorElement) {
      return
    }
    const rowText = anchorElement.closest<HTMLElement>('.lectio-row-text')
    const rowId = rowText?.dataset.rowId || ''
    const selectedText = selection.toString().replace(/\s+/g, ' ').trim()
    if (!rowId || selectedText.length < 2 || selectedText.length > 120) {
      return
    }
    if (rapidTool === 'create-character') {
      const sourceText = rowTextById.get(rowId) || ''
      let start = sourceText.indexOf(selectedText)
      let end = start >= 0 ? start + selectedText.length : -1
      try {
        const range = selection.getRangeAt?.(0)
        if (range && rowText?.contains(range.startContainer) && rowText.contains(range.endContainer)) {
          const prefix = document.createRange()
          prefix.selectNodeContents(rowText)
          prefix.setEnd(range.startContainer, range.startOffset)
          const rawText = selection.toString()
          const leadingWhitespace = rawText.length - rawText.trimStart().length
          start = prefix.toString().length + leadingWhitespace
          end = start + rawText.trim().length
        }
      } catch {
        /* The immutable row-text lookup above is the safe fallback for test DOMs
           and browsers that cannot project this selection back to a Range. */
      }
      if (start >= 0 && end > start && end <= sourceText.length) {
        selection.removeAllRanges?.()
        void createCharacterFromSourceSpan(rowId, start, end, selectedText)
      } else {
        setAdjudicationNotice('Select one complete named occurrence within a single unit')
      }
      return
    }
    if (anchorElement.closest('.cte-character-anchor, .cte-layer-anchor, .lectio-annotation-segment.has-annotation')) {
      return
    }
    setInlineCandidate({
      rowId,
      text: selectedText,
      pasteOpen: false,
      pastedResponse: '',
      status: 'idle',
      feedback: '',
    })
  }

  const buildInlineCandidateBrief = (candidate: LectioInlineCandidate): string => {
    const rowText = rowTextById.get(candidate.rowId) || ''
    return JSON.stringify({
      version: 'lectio-character-brief/1',
      operation: 'establish-character-and-readings',
      immutableSource: {
        witnessId: sourceWitnessId,
        siglum: sourceLabel,
        rowId: candidate.rowId,
        rowLabel: rowLabelById.get(candidate.rowId) || candidate.rowId,
      },
      candidate: {
        selectedText: candidate.text,
        boundedRowContext: rowText.slice(0, 2400),
      },
      request: [
        'canonical identity and name variants',
        'source-relative coreference chain',
        'focalization spans',
        'characterization spans',
      ],
      constraints: {
        returnJsonOnly: true,
        offsetsAreLocalToBoundedRowContext: true,
        doNotRewriteSource: true,
      },
    }, null, 2).slice(0, 6000)
  }

  const copyInlineCandidateBrief = async (candidate: LectioInlineCandidate) => {
    try {
      if (!navigator.clipboard?.writeText) {
        throw new Error('Clipboard writing is unavailable in this browser')
      }
      await navigator.clipboard.writeText(buildInlineCandidateBrief(candidate))
      setInlineCandidate(current => current && current.rowId === candidate.rowId
        ? { ...current, status: 'copied', feedback: 'Brief copied · awaiting an outside response' }
        : current)
    } catch (error) {
      setInlineCandidate(current => current && current.rowId === candidate.rowId
        ? { ...current, status: 'error', feedback: error instanceof Error ? error.message : 'Could not copy the brief' }
        : current)
    }
  }

  const stageInlineCandidateResponse = (response: string) => {
    try {
      const parsed = JSON.parse(response)
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        throw new Error('The response must be one JSON object')
      }
      setInlineCandidate(current => current ? {
        ...current,
        pastedResponse: response,
        pasteOpen: false,
        status: 'staged',
        feedback: 'Response staged sub judice · open adjudication to accept or reject it',
      } : current)
    } catch (error) {
      setInlineCandidate(current => current ? {
        ...current,
        status: 'error',
        feedback: error instanceof Error ? error.message : 'The response is not valid JSON',
      } : current)
    }
  }

  const unloadInlineCandidateClipboard = async () => {
    try {
      if (!navigator.clipboard?.readText) {
        throw new Error('Clipboard reading is unavailable in this browser')
      }
      const response = await navigator.clipboard.readText()
      setInlineCandidate(current => current ? { ...current, pastedResponse: response, pasteOpen: true } : current)
      stageInlineCandidateResponse(response)
    } catch (error) {
      setInlineCandidate(current => current ? {
        ...current,
        status: 'error',
        feedback: error instanceof Error ? error.message : 'Could not unload the clipboard',
      } : current)
    }
  }

  const inlineCandidateApparatus = inlineCandidate ? (
    <div
      className={`lectio-unindexed-candidate ${inlineCandidate.status}`}
      role="group"
      aria-label={`Unindexed text ${inlineCandidate.text}`}
    >
      <div className="lectio-unindexed-candidate-line">
        <span className="lectio-unindexed-marker" aria-hidden="true">+</span>
        <b>{inlineCandidate.text}</b>
        <span>name only · no character record</span>
      </div>
      <div className="lectio-unindexed-actions">
        <button
          type="button"
          disabled={!characterIndexControlPanel}
          onClick={openSecondHand}
        >Second hand</button>
        <span aria-hidden="true">·</span>
        <button type="button" onClick={() => void copyInlineCandidateBrief(inlineCandidate)}>copy brief</button>
        <span aria-hidden="true">·</span>
        <button type="button" onClick={() => setInlineCandidate(current => current ? { ...current, pasteOpen: true } : current)}>paste response</button>
        <span aria-hidden="true">·</span>
        <button type="button" onClick={() => void unloadInlineCandidateClipboard()}>unload clipboard</button>
        <span aria-hidden="true">·</span>
        <button type="button" onClick={() => setInlineCandidate(null)}>dismiss</button>
      </div>
      {inlineCandidate.pasteOpen && (
        <div className="lectio-unindexed-paste">
          <textarea
            aria-label={`Outside LLM response for ${inlineCandidate.text}`}
            value={inlineCandidate.pastedResponse}
            onChange={event => setInlineCandidate(current => current ? { ...current, pastedResponse: event.target.value } : current)}
            placeholder="Paste the JSON response here"
          />
          <button type="button" onClick={() => stageInlineCandidateResponse(inlineCandidate.pastedResponse)}>stage response</button>
        </div>
      )}
      {inlineCandidate.feedback && <p role="status">{inlineCandidate.feedback}</p>}
      {inlineCandidate.status === 'staged' && activeCharacterId && onAdjudicateCharacterEvidence && (
        <button type="button" className="lectio-unindexed-adjudicate" onClick={enterAdjudication}>
          open adjudication
        </button>
      )}
    </div>
  ) : null

  const rapidCharacterEntries = activeCharacterId
    ? [
        ...characterMarginEntries.filter(entry => entry.id === activeCharacterId),
        ...characterMarginEntries.filter(entry => entry.id !== activeCharacterId),
      ]
    : characterMarginEntries
  const rapidPalette = (
    <div
      className="lectio-rapid-palette"
      role="toolbar"
      aria-label="Rapid annotation palette"
      data-palette-layout="bifolio"
      data-testid="lectio-rapid-palette"
    >
      <div className="lectio-rapid-tool-group" role="group" aria-label="Marking tools">
        <button
          type="button"
          data-tool="coref"
          aria-label="Coreference marking tool"
          aria-pressed={rapidTool === 'coref'}
          disabled={!onAdjudicateCharacterEvidence}
          onClick={() => activateRapidTool('coref')}
        ><span aria-hidden="true">●</span><small>coref</small></button>
        <button
          type="button"
          data-tool="speaker"
          aria-label="Speaker marking tool"
          aria-pressed={rapidTool === 'speaker'}
          disabled={!onAdjudicateCharacterEvidence}
          onClick={() => activateRapidTool('speaker')}
        ><span aria-hidden="true">❯</span><small>speaker</small></button>
        <button
          type="button"
          data-tool="focalization"
          aria-label="Focalization marking tool"
          aria-pressed={rapidTool === 'focalization'}
          disabled={!onAdjudicateCharacterEvidence}
          onClick={() => activateRapidTool('focalization')}
        ><span aria-hidden="true">⊙</span><small>foc</small></button>
        <button
          type="button"
          data-tool="characterization"
          aria-label="Characterization marking tool"
          aria-pressed={rapidTool === 'characterization'}
          disabled={!onAdjudicateCharacterEvidence}
          onClick={() => activateRapidTool('characterization')}
        ><span aria-hidden="true">◆</span><small>char</small></button>
        <button
          type="button"
          data-tool="section"
          aria-label="Section marking tool"
          aria-pressed={rapidTool === 'section'}
          disabled={!textStructureLayer || !onEditTextStructure}
          onClick={activateSectionTool}
        ><span aria-hidden="true">§</span><small>section</small></button>
        {onCreateCharacter && (
          <button
            type="button"
            className="lectio-rapid-create-character"
            data-tool="create-character"
            aria-label="Create character from first occurrence"
            aria-pressed={rapidTool === 'create-character'}
            disabled={rapidCharacterCreationPending}
            onClick={beginRapidCharacterCreation}
          ><span aria-hidden="true">+</span><small>{rapidCharacterCreationPending ? 'saving' : 'new'}</small></button>
        )}
        {rapidTool && (
          <button
            type="button"
            className="lectio-rapid-neutral"
            data-tool="neutral"
            aria-label="Return to neutral cursor"
            onClick={() => {
              setRapidTool(null)
              setRapidSpanAnchor(null)
              setRapidSectionStart(null)
              setRapidSectionPhase('opening')
              setRapidSectionTypeId('')
              setRapidSectionTitle('')
              rapidSectionTitleRef.current = ''
              setEditingStructureSectionId('')
              setAdjudicationNotice('Rapid marking off · the contextual menu remains available')
            }}
          ><span aria-hidden="true">×</span><small>neutral</small></button>
        )}
      </div>
      {rapidTool === 'section' ? (
        <div
          className="lectio-rapid-structure-progress"
          role="status"
          aria-label="Section marking status"
          dir="ltr"
        >
          <div className="lectio-rapid-character-heading">Section</div>
          <span aria-hidden="true">§</span>
          <b>{rapidSectionPhase === 'opening'
            ? 'Choose opening'
            : rapidSectionPhase === 'classify'
              ? 'Classify in margin'
              : rapidSectionPhase === 'closing'
                ? 'Title, then close'
                : 'Edit in margin'}</b>
          <small>{rapidSectionPhase === 'opening'
            ? 'unit number or word'
            : rapidSectionPhase === 'classify'
              ? rowLabelById.get(rapidSectionStart?.rowId || '') || 'opening set'
              : rapidSectionPhase === 'closing'
                ? `${activeRapidSectionType?.label || 'section'} · choose final unit or word`
                : rowLabelById.get(rapidSectionStart?.rowId || '') || 'opening margin'}</small>
        </div>
      ) : (
        <div className="lectio-rapid-character-group" role="group" aria-label="Characters for rapid annotation" dir="rtl">
          <div className="lectio-rapid-character-heading" dir="ltr">Persons · first appearance</div>
          <div className="lectio-rapid-character-list">
            {rapidCharacterEntries.map(entry => {
              const decoration = characterDecorationsById.get(entry.id) || {
                chip: entry.id,
                color: getCharacterColor(entry.id),
              }
              const label = getCharacterDisplayLabel(entry)
              return (
                <button
                  key={entry.id}
                  type="button"
                  aria-label={`Use ${label} for rapid annotation`}
                  aria-current={activeCharacterId === entry.id ? 'true' : undefined}
                  onClick={() => selectRapidCharacter(entry.id)}
                  style={{ '--cte-character-color': decoration.color } as React.CSSProperties}
                >
                  <span className="lectio-rapid-character-chip" aria-hidden="true">{decoration.chip}</span>
                  <b className="lectio-rapid-character-name">{label}</b>
                  <small className="lectio-rapid-character-meta" dir="ltr">{entry.transliteration?.primary || decoration.chip}</small>
                </button>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )

  const registerReaderUnit = useCallback((rowId: string, element: HTMLSpanElement | null) => {
    if (element) {
      unitRefs.current.set(rowId, element)
    } else {
      unitRefs.current.delete(rowId)
    }
  }, [])
  const handleReaderBoundaryResize = useEventCallback((
    command: LectioAdjudicationResizeCommand,
    rowLabel: string,
  ) => {
    resizeTweezedBoundary(command, rowLabel)
  })
  const handleRapidMarkSectionPoint = useEventCallback((
    rowId: string,
    start: number,
    end: number,
    text: string,
  ) => {
    void markRapidSectionBoundary({ rowId, start, end, text, wholeUnit: false })
  })
  const handleRapidMarkUnitBoundary = useEventCallback((rowId: string) => {
    void markRapidSectionBoundary({
      rowId,
      start: 0,
      end: 0,
      text: '',
      wholeUnit: true,
    })
  })
  const handleDraftBareWord = useEventCallback((
    rowId: string,
    start: number,
    end: number,
    text: string,
    anchor: HTMLButtonElement,
  ) => {
    openEvidenceMenu({
      sourceKind: 'draft',
      evidenceId: `draft:${rowId}:${start}:${end}`,
      characterId: activeCharacterId,
      rowId,
      rowLabel: rowLabelById.get(rowId) || rowId,
      start,
      end,
      text,
    }, anchor)
    setAdjudicationNotice(`${rowLabelById.get(rowId) || rowId} · classify the new reading in place`)
  })
  const rapidMarkingContext = useMemo<LectioRapidMarkingContext | null>(() => (
    readerSection === 'character-index' && rapidTool && (
      rapidTool === 'create-character' || rapidTool === 'section' || activeCharacterId
    ) && (
      rapidTool !== 'section' || rapidSectionPhase === 'opening' || rapidSectionPhase === 'closing'
    )
      ? {
          tool: rapidTool,
          characterLabel: activeCharacterLabel || activeCharacterId || '',
          pendingSpan: rapidSpanAnchor,
          sectionTypeLabel: activeRapidSectionType?.label,
          pendingSectionStart: rapidSectionPhase === 'closing' ? rapidSectionStart : null,
          sectionBoundaryAction: rapidTool === 'section'
            ? rapidSectionPhase === 'closing' ? 'end' : 'start'
            : null,
        }
      : null
  ), [
    activeCharacterId,
    activeCharacterLabel,
    activeRapidSectionType?.label,
    rapidSectionPhase,
    rapidSectionStart,
    rapidSpanAnchor,
    rapidTool,
    readerSection,
  ])

  const readerAriaLabel = readerSection === 'character-index'
    ? 'Character Index source text'
    : readerSection === 'read'
      ? 'Plain source reading'
      : 'Variorum continuous witness text'
  const renderReaderText = (className = '') => (
    <section
      ref={readerSurfaceRef}
      className={['cte-reader', className].filter(Boolean).join(' ')}
      tabIndex={0}
      aria-label={readerAriaLabel}
      data-selected-character={activeCharacterId && readerSection === 'character-index' ? 'true' : undefined}
      data-annotation-grammar={readerSection === 'character-index' ? annotationGrammar : undefined}
      onMouseUp={handleInlineCandidateSelection}
    >
      {readerSection === 'character-index' && lectioHeadnote}
      {readerSection === 'character-index' && showContextualAssistance && characterIndexControlPanel && (
        <div className="lectio-second-hand-apparatus" data-testid="lectio-second-hand-apparatus">
          <div className="lectio-second-hand-apparatus-head">
            <span>
              Second hand · {activeCharacterLabel || inlineCandidate?.text || 'selected reading'}
            </span>
            <button type="button" onClick={() => setShowContextualAssistance(false)}>Dismiss</button>
          </div>
          {characterIndexControlPanel}
        </div>
      )}
      {model.units.length === 0 ? (
        <div className="cte-empty-reader">
          <div className="cte-empty-title">No collation units available</div>
          <p>Select a witness with mapped Variorum rows to enter The Tradition reader.</p>
        </div>
      ) : (
        <div className={[
          'cte-text-flow',
          'lectio-text-flow',
          readerSection === 'character-index' ? 'has-rapid-palette' : '',
        ].filter(Boolean).join(' ')}>
          <div
            ref={speakerRailHostRef}
            className="lectio-text-units"
            data-has-speaker-turn-rails={readerSection === 'character-index' && speakerTurnRails.length > 0
              ? 'true'
              : undefined}
          >
            {readerSection === 'read' && (
              <div className="lectio-plain-note">plain reading · character and comparison layers off</div>
            )}
            {model.units.map(unit => (
            <ReaderUnit
              key={unit.rowId}
              unit={unit}
              annotationGrammar={annotationGrammar}
              focused={unit.rowId === selectedUnit?.rowId}
              characterAnchors={readerSection === 'character-index'
                ? visibleSourceCharacterAnchorsByRowId.get(unit.rowId) || EMPTY_READER_CHARACTER_ANCHORS
                : EMPTY_READER_CHARACTER_ANCHORS}
              layerAnnotations={readerSection === 'character-index'
                ? visibleSourceLayerAnnotationsByRowId.get(unit.rowId) || EMPTY_READER_LAYER_ANNOTATIONS
                : EMPTY_READER_LAYER_ANNOTATIONS}
              unitLayerSummaries={readerSection === 'character-index'
                ? unitLayerSummariesByRowId.get(unit.rowId) || EMPTY_READER_UNIT_LAYER_SUMMARIES
                : undefined}
              structureSummary={readerSection === 'character-index' && textStructureLayer
                ? unitStructureSummariesByRowId.get(unit.rowId) || EMPTY_READER_UNIT_STRUCTURE_SUMMARY
                : undefined}
              structureDraftEditor={readerSection === 'character-index' &&
                rapidTool === 'section' &&
                rapidSectionStart?.rowId === unit.rowId &&
                rapidSectionPhase !== 'opening' ? (
                  <ReaderSectionMarginEditor
                    rowLabel={unit.rowLabel}
                    phase={rapidSectionPhase}
                    sectionTypes={textStructureLayer?.types || []}
                    selectedType={activeRapidSectionType}
                    title={rapidSectionTitle}
                    editing={Boolean(editingStructureSectionId)}
                    customTypeOpen={customSectionTypeOpen}
                    customTypeLabel={customSectionTypeLabel}
                    customTypeAbbreviation={customSectionTypeAbbreviation}
                    saving={structureSaving}
                    onSelectType={selectRapidSectionType}
                    onChangeType={reopenRapidSectionClassification}
                    onChangeTitle={title => {
                      rapidSectionTitleRef.current = title
                    }}
                    onToggleCustomType={() => setCustomSectionTypeOpen(current => !current)}
                    onChangeCustomTypeLabel={setCustomSectionTypeLabel}
                    onChangeCustomTypeAbbreviation={setCustomSectionTypeAbbreviation}
                    onAddCustomType={() => void addCustomSectionType()}
                    onSave={() => void saveEditedStructureSection()}
                    onDelete={() => void deleteEditedStructureSection()}
                    onRemark={remarkEditedStructureSection}
                    noteCount={editingStructureSectionNoteCount}
                    onOpenNotes={anchor => {
                      if (editingStructureSection) {
                        openResearchNotesForSection(editingStructureSection, anchor)
                      }
                    }}
                    onCancel={cancelRapidSectionDraft}
                  />
                ) : undefined}
              structureDraftOffset={readerSection === 'character-index' && rapidTool === 'section' &&
                rapidSectionStart?.rowId === unit.rowId
                ? rapidSectionStart.offset
                : undefined}
              focusedLayerNoteId={focusedLayerNoteId}
              focusedOccurrenceId={focusedCharacterOccurrenceId}
              selectedCharacterId={activeCharacterId}
              showMappedRowHighlights={showMappedRowHighlights}
              showRowLabel={readerSection !== 'compare'}
              inlineCandidate={readerSection === 'character-index' && inlineCandidate?.rowId === unit.rowId
                ? inlineCandidateApparatus
                : undefined}
              lineApparatus={readerSection === 'character-index' && openLineApparatusRowIds.has(unit.rowId)
                ? lineApparatusFor(unit)
                : undefined}
              lineApparatusOpen={readerSection === 'character-index' && openLineApparatusRowIds.has(unit.rowId)}
              subJudiceCount={readerSection === 'character-index' ? subJudiceCountByRowId.get(unit.rowId) || 0 : 0}
              tweezedReadingId={readerSection === 'character-index' && tweezedReading?.rowId === unit.rowId
                ? tweezedReading.id
                : null}
              onBoundaryResize={handleReaderBoundaryResize}
              variationTier={unitVariationTierByRowId.get(unit.rowId)}
              selectFocusLevel={readerSection === 'compare' ? 'apparatus' : 'reader'}
              onFocus={selectUnit}
              onSelect={selectUnit}
              onSelectCharacter={selectCharacterInReader}
              onSelectCharacterOccurrence={selectCharacterInReader}
              onSelectLayerAnnotation={selectLayerAnnotationInReader}
              onSelectUnitLayerSummary={readerSection === 'character-index'
                ? selectUnitLayerSummary
                : undefined}
              onSelectStructureSection={readerSection === 'character-index'
                ? selectStructureSection
                : undefined}
              getResearchNoteCount={readerSection === 'character-index' ? getResearchNoteCount : undefined}
              onOpenEvidenceMenu={readerSection === 'character-index' && adjudicationSession
                ? openEvidenceMenu
                : undefined}
              rapidMarking={rapidMarkingContext}
              onRapidMarkWord={readerSection === 'character-index' ? handleRapidMarkWord : undefined}
              onRapidMarkSectionPoint={readerSection === 'character-index' && rapidTool === 'section'
                ? handleRapidMarkSectionPoint
                : undefined}
              onRapidMarkUnitBoundary={readerSection === 'character-index' && rapidTool === 'section' && (
                rapidSectionPhase === 'opening' || rapidSectionPhase === 'closing'
              )
                ? handleRapidMarkUnitBoundary
                : undefined}
              openEvidenceMenuId={evidenceMenu?.target.evidenceId || null}
              onToggleLineApparatus={readerSection === 'character-index'
                ? toggleLineApparatus
                : undefined}
              onDraftBareWord={readerSection === 'character-index' && adjudicationSession && !rapidTool
                ? handleDraftBareWord
                : undefined}
              register={registerReaderUnit}
            />
            ))}
            {readerSection === 'character-index' && speakerRailLayouts.length > 0 && (
              <div
                className="lectio-speaker-turn-rails"
                role="group"
                aria-label="Speaker turn rails"
              >
                {speakerRailLayouts.map(turn => {
                  const visibleLevel = turn.level + 1
                  const singleUnit = turn.startRowId === turn.endRowId
                  const locus = singleUnit
                    ? `at ${turn.startRowLabel}`
                    : `from ${turn.startRowLabel} to ${turn.endRowLabel}`
                  const levelPhrase = turn.labelPlacement === 'entry'
                    ? ` quotation level ${visibleLevel}`
                    : ''
                  return (
                    <span
                      key={turn.turnId}
                      className="lectio-speaker-turn-rail"
                      data-standing={turn.standing}
                      data-overlay-visible={String(turn.overlayVisible)}
                      data-speech-level={visibleLevel}
                      style={{
                        top: `${turn.top}px`,
                        height: `${turn.height}px`,
                        '--lectio-speaker-indent': `${turn.level * 14}px`,
                        '--lectio-speaker-label-offset': `${turn.labelOffset}px`,
                        '--lectio-speaker-color': turn.color,
                      } as React.CSSProperties}
                    >
                      <span className="lectio-speaker-turn-rule" aria-hidden="true" />
                      <button
                        type="button"
                        className="lectio-speaker-turn-label"
                        data-label-placement={turn.labelPlacement}
                        data-speech-level={visibleLevel}
                        data-source-turn-count={turn.sourceTurnIds.length}
                        data-selected={activeCharacterId === turn.characterId ? 'true' : undefined}
                        aria-label={`${turn.label}${levelPhrase} speaker turn ${locus}`}
                        onClick={event => {
                          event.stopPropagation()
                          selectLayerAnnotationInReader(turn.characterId, turn.startRowId, turn.firstNoteId)
                        }}
                      >
                        {turn.labelPlacement === 'entry' && (
                          <sup aria-hidden="true">{toSuperscriptNumber(visibleLevel)}</sup>
                        )}
                        <b dir="auto">{turn.label}</b>
                      </button>
                    </span>
                  )
                })}
              </div>
            )}
          </div>
          {readerSection === 'character-index' && rapidPalette}
        </div>
      )}
    </section>
  )

  const toggleLectioFold = (foldId: string) => {
    setExpandedLectioFoldIds(current => {
      const updated = new Set(current)
      if (updated.has(foldId)) {
        updated.delete(foldId)
      } else {
        updated.add(foldId)
      }
      return updated
    })
  }

  const renderIntraFold = (fold: LectioIntraFold, rowLabel: string): React.ReactNode => {
    const isExpanded = expandedLectioFoldIds.has(fold.id)
    return (
      <span key={fold.id} className={['lectio-fold', 'lectio-fold-intra', isExpanded ? 'expanded' : ''].filter(Boolean).join(' ')}>
        {isExpanded && <span className="lectio-fold-open">{fold.text}</span>}
        <button
          type="button"
          aria-label={`${isExpanded ? 'Fold' : 'Unfold'} omitted text in ${rowLabel} (${fold.start}–${fold.end})`}
          title={`${fold.text.length} characters omitted · ${isExpanded ? 'fold again' : 'unfold in place'}`}
          onClick={() => toggleLectioFold(fold.id)}
        >…</button>
      </span>
    )
  }

  const renderLectionText = (item: LectioLectionSpan): React.ReactNode => {
    const hasFocalization = item.kinds.includes('focalization')
    const hasCharacterization = item.kinds.includes('characterization')
    const blackLetterIncipit = annotationGrammar === 'black-letter'
      ? item.text.match(/^(\s*)(\S+)([\s\S]*)$/u)
      : null
    const text = blackLetterIncipit ? (
      <>
        {blackLetterIncipit[1]}
        <span
          className={[
            'lectio-annotation-incipit',
            hasFocalization ? 'focalization' : '',
            hasCharacterization ? 'characterization' : '',
          ].filter(Boolean).join(' ')}
        >{blackLetterIncipit[2]}</span>
        {blackLetterIncipit[3]}
      </>
    ) : item.text
    return (
      <>
        {hasFocalization && <span className="lectio-layer-bracket focalization open" aria-hidden="true" />}
        {hasCharacterization && <span className="lectio-layer-bracket characterization open" aria-hidden="true" />}
        {text}
        {hasCharacterization && <span className="lectio-layer-bracket characterization close" aria-hidden="true" />}
        {hasFocalization && <span className="lectio-layer-bracket focalization close" aria-hidden="true" />}
      </>
    )
  }

  const renderLectionBlock = (block: LectioLectionBlock): React.ReactNode => {
    if (block.type === 'row') {
      const rowLabel = rowLabelById.get(block.rowId) || block.rowId
      return (
        <div key={`lection-row:${block.rowId}`} className="lectio-lection-row" data-row-block={block.rowId}>
          <span className="lectio-row-label">{rowLabel}</span>
          <span className="lectio-lection-row-text">
            {block.items.map(item => item.type === 'fold' ? renderIntraFold(item, rowLabel) : (
              <span
                key={item.id}
                className={[
                  'lectio-lection-span',
                  item.kinds.includes('focalization') ? 'cte-layer-focalization' : '',
                  item.kinds.includes('characterization') ? 'cte-layer-characterization' : '',
                ].filter(Boolean).join(' ')}
                data-evidence-id={item.evidenceIds.join(' ')}
                data-evidence-kind={item.kinds.join(' ')}
                data-row-id={item.rowId}
                data-native-source-text={item.text}
              >
                <small>{item.kinds.join(' + ')} · {rowLabel}</small>
                {renderLectionText(item)}
              </span>
            ))}
          </span>
        </div>
      )
    }

    const firstLabel = rowLabelById.get(block.rowIds[0]) || block.rowIds[0]
    const lastRowId = block.rowIds[block.rowIds.length - 1]
    const lastLabel = rowLabelById.get(lastRowId) || lastRowId
    const foldLabel = firstLabel === lastLabel ? firstLabel : `${firstLabel}–${lastLabel}`
    const isExpanded = expandedLectioFoldIds.has(block.id)
    return (
      <div key={block.id} className={['lectio-fold-strip', isExpanded ? 'expanded' : ''].filter(Boolean).join(' ')}>
        <div className="lectio-fold-strip-head">
          <span>{foldLabel} · {block.text.length} characters {isExpanded ? 'unfolded' : 'folded'}</span>
          <button
            type="button"
            aria-label={`${isExpanded ? 'Fold' : 'Unfold'} omitted text in ${foldLabel}`}
            onClick={() => toggleLectioFold(block.id)}
          >{isExpanded ? 'fold again' : '… unfold in place'}</button>
        </div>
        {isExpanded && block.rowIds.map(rowId => (
          <div key={`${block.id}:${rowId}`} className="lectio-lection-row lectio-folded-row" data-row-block={rowId}>
            <span className="lectio-row-label">{rowLabelById.get(rowId) || rowId}</span>
            <span className="lectio-lection-row-text lectio-fold-open">{rowTextById.get(rowId) || ''}</span>
          </div>
        ))}
      </div>
    )
  }

  const evidenceOnlyReader = (
    <section
      ref={readerSurfaceRef}
      className="cte-reader lectio-character-reader lectio-evidence-only-reader"
      tabIndex={0}
      aria-label="Character Index source text"
      data-selected-character="true"
      style={{ '--lectio-character-color': activeCharacterColor } as React.CSSProperties}
    >
      {lectioHeadnote}
      <div className="lectio-evidence-projection" data-testid="lectio-evidence-projection">
        <header>
          <div>
            <span className="cte-overline">Text preserved under folds</span>
            <h2>Lections · {activeCharacterLabel}</h2>
          </div>
          <button type="button" onClick={returnToContinuousCharacterText}>Full text</button>
        </header>
        <div className="lectio-evidence-projection-flow">
          {lectionBlocks.length === 0 ? (
            <p>No placed evidence for this view.</p>
          ) : lectionBlocks.map(renderLectionBlock)}
        </div>
      </div>
    </section>
  )

  const characterMargin = (
    <nav
      ref={characterMarginRef}
      className="lectio-character-margin"
      aria-label="Marginal index nominum in order of first appearance"
      onKeyDown={event => {
        const key = event.key.toLowerCase()
        if (event.key === 'Escape') {
          event.preventDefault()
          event.stopPropagation()
          stepBackCharacterIndex()
          readerSurfaceRef.current?.focus()
          return
        }
        if (key === 'l' && activeCharacterId) {
          event.preventDefault()
          event.stopPropagation()
          openCharacterLections()
          return
        }
        if (key === 'a' && activeCharacterId && onAdjudicateCharacterEvidence) {
          event.preventDefault()
          event.stopPropagation()
          enterAdjudication()
          return
        }
        if (key === 'g') {
          event.preventDefault()
          event.stopPropagation()
          showTextualBeingGallery()
          return
        }
        if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') {
          return
        }
        const target = event.target instanceof HTMLElement ? event.target : null
        const currentButton = target?.closest<HTMLButtonElement>('.lectio-index-name') || null
        if (!currentButton) {
          return
        }
        const buttons = Array.from(event.currentTarget.querySelectorAll<HTMLButtonElement>('.lectio-index-name'))
        const currentIndex = buttons.indexOf(currentButton)
        if (currentIndex < 0) {
          return
        }
        event.preventDefault()
        event.stopPropagation()
        const delta = event.key === 'ArrowDown' ? 1 : -1
        const nextIndex = Math.max(0, Math.min(buttons.length - 1, currentIndex + delta))
        buttons[nextIndex]?.focus()
      }}
    >
      <div className="lectio-character-margin-head">
        <div className="lectio-index-kicker">Index nominum · {sourceLabel} · first appearance</div>
        <h2>
          <span>Persons</span>
          <small>{characterMarginEntries.length} canonical</small>
        </h2>
        {onCreateCharacter && (
          <button
            type="button"
            aria-expanded={characterCreationOpen}
            onClick={() => {
              setCharacterCreationOpen(current => !current)
              setCharacterCreationError('')
            }}
          >Create character</button>
        )}
        <button type="button" aria-label="Textual beings" onClick={showTextualBeingGallery}>All as textual beings</button>
      </div>
      {characterCreationOpen && onCreateCharacter && (
        <form
          className="lectio-character-create"
          aria-label="Create character"
          onSubmit={event => {
            event.preventDefault()
            void createCharacterInMargin()
          }}
        >
          <label htmlFor="lectio-new-character-name">New character</label>
          <input
            id="lectio-new-character-name"
            type="text"
            aria-label="New character name"
            value={characterCreationName}
            onChange={event => setCharacterCreationName(event.target.value)}
            placeholder="Name"
            autoFocus
            disabled={characterCreationPending}
          />
          <label htmlFor="lectio-new-character-kind">Figure type</label>
          <select
            id="lectio-new-character-kind"
            aria-label="New character figure type"
            value={characterCreationKind}
            onChange={event => setCharacterCreationKind(event.target.value as CharacterIndexFigureKind)}
            disabled={characterCreationPending}
          >
            {CHARACTER_FIGURE_KIND_OPTIONS.map(option => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
          <span className="lectio-character-create-actions">
            <button type="submit" disabled={!characterCreationName.trim() || characterCreationPending}>
              {characterCreationPending ? 'Creating…' : 'Create and adjudicate'}
            </button>
            <span aria-hidden="true">·</span>
            <button
              type="button"
              disabled={characterCreationPending}
              onClick={() => {
                setCharacterCreationOpen(false)
                setCharacterCreationName('')
                setCharacterCreationKind('person')
                setCharacterCreationError('')
              }}
            >cancel</button>
          </span>
          {characterCreationError && <span role="status">{characterCreationError}</span>}
        </form>
      )}
      {characterMarginEntries.length === 0 ? (
        <p className="lectio-character-margin-empty">No canonical persons in this source scope</p>
      ) : <div className="lectio-character-roster" role="list">{characterMarginEntries.map(entry => {
        const decoration = characterDecorationsById.get(entry.id) || {
          chip: entry.id,
          color: getCharacterColor(entry.id),
        }
        const counts = lectioRosterById.get(entry.id)?.counts || {
          chain: 0,
          speaker: 0,
          focalization: 0,
          characterization: 0,
        }
        const isActive = activeCharacterId === entry.id
        const label = getCharacterDisplayLabel(entry)
        const figureKind = entry.kind || 'person'
        const visibleKindSuffix = figureKind === 'person' ? '' : ` · ${figureKind}`
        const characterEvidence = lectioEvidence.filter(item => (
          lectioEvidenceRefersTo(item, entry.id) && item.witnessId === sourceWitnessId
        )).slice().sort((left, right) => (
          (rowOrderById.get(left.rowId) ?? Number.MAX_SAFE_INTEGER) -
            (rowOrderById.get(right.rowId) ?? Number.MAX_SAFE_INTEGER) ||
          left.start - right.start ||
          left.end - right.end
        ))
        const loci = Array.from(new Set(characterEvidence.map(item => item.rowId)))
        const characterStandings = allLectioEvidence.filter(item => (
          lectioEvidenceRefersTo(item, entry.id) && item.witnessId === sourceWitnessId && item.standing !== 'relegated'
        ))
        const admittedCount = characterStandings.filter(item => item.standing === 'admitted').length
        const receivedCount = characterStandings.filter(item => item.standing === 'received').length
        const entrySubJudiceCount = characterStandings.filter(item => item.standing === 'subjudice').length
        const unplacedEntryCount = characterStandings.filter(item => !(item.start >= 0 && item.end > item.start)).length
        const lociLabels = loci.map(rowId => rowLabelById.get(rowId) || rowId)
        const lociPrefixes = new Set(lociLabels.map(rowLabel => (
          rowLabel.includes('-') ? rowLabel.slice(0, rowLabel.indexOf('-')) : ''
        )))
        const sharedLocusPrefix = lociPrefixes.size === 1 ? Array.from(lociPrefixes)[0] : ''
        return (
          <div
            key={entry.id}
            className={['lectio-index-entry', isActive ? 'active' : ''].filter(Boolean).join(' ')}
            data-character-index-entry={entry.id}
            data-character-kind={figureKind}
            role="listitem"
            style={{ '--cte-character-color': decoration.color } as React.CSSProperties}
          >
            <button
              type="button"
              className="lectio-index-name"
              aria-label={`${label}${figureKind === 'person' ? '' : ` ${figureKind}`} character`}
              aria-current={isActive ? 'true' : undefined}
              aria-expanded={isActive}
              onClick={event => selectMarginalCharacter(entry.id, event.currentTarget)}
            >
              <span className="lectio-index-marker" aria-hidden="true">{decoration.chip}</span>
              <span className="lectio-index-main">
                <b data-lectio-character-name>{label}</b>
                <small>{entry.transliteration.primary || entry.names.primary || entry.id}{visibleKindSuffix}</small>
              </span>
              <em>
                {counts.chain}c {counts.speaker}sp {counts.focalization}f {counts.characterization}ch
                <br />
                {characterStandings.length === 0 ? 'no evidence' : [
                  admittedCount > 0 ? <span key="adm" className="adm">{admittedCount} adm</span> : null,
                  receivedCount > 0 ? <span key="rec">{receivedCount} rec</span> : null,
                  entrySubJudiceCount > 0 ? <span key="sj" className="sj">† {entrySubJudiceCount}</span> : null,
                  unplacedEntryCount > 0
                    ? <span key="unpl" title="records without usable offsets remain counted">{unplacedEntryCount} unpl</span>
                    : null,
                ].filter(Boolean).flatMap((node, index) => index === 0 ? [node] : [' · ', node])}
              </em>
            </button>
            <div className="lectio-index-loci">
              {loci.length === 0 ? 'no placed evidence' : <>
                {sharedLocusPrefix ? `${sharedLocusPrefix} · ` : ''}{loci.map(rowId => {
                  const rowLabel = rowLabelById.get(rowId) || rowId
                  const target = characterEvidence.find(item => item.rowId === rowId)
                  const locusLabel = sharedLocusPrefix
                    ? rowLabel.replace(/^.*?-/, '').replace(/^0+(?=\d)/, '')
                    : rowLabel
                  return (
                    <button
                      key={rowId}
                      type="button"
                      aria-label={`${label} locus ${rowLabel}`}
                      onClick={() => selectMarginalCharacter(entry.id, null, target?.id || '')}
                    >{locusLabel}</button>
                  )
                })}
              </>}
            </div>
            {isActive && (
              <div className="lectio-index-actions" role="group" aria-label={`${label} actions`}>
                <button type="button" aria-pressed={isolatedEvidenceKind === 'chain'} onClick={() => isolateCharacterEvidence('chain')}>chain · {counts.chain}</button>
                <span aria-hidden="true">·</span>
                <button type="button" aria-pressed={isolatedEvidenceKind === 'speaker'} onClick={() => isolateCharacterEvidence('speaker')}>spk · {counts.speaker}</button>
                <span aria-hidden="true">·</span>
                <button type="button" aria-pressed={isolatedEvidenceKind === 'focalization'} onClick={() => isolateCharacterEvidence('focalization')}>foc · {counts.focalization}</button>
                <span aria-hidden="true">·</span>
                <button type="button" aria-pressed={isolatedEvidenceKind === 'characterization'} onClick={() => isolateCharacterEvidence('characterization')}>char · {counts.characterization}</button>
                <span aria-hidden="true">·</span>
                <button type="button" aria-pressed={evidenceProjection === 'analytic'} onClick={openCharacterLections}>Lections</button>
                <span aria-hidden="true">·</span>
                <button type="button" disabled={!onAdjudicateCharacterEvidence} onClick={enterAdjudication}>Adjudicate</button>
                {onRenameCharacter && <>
                  <span aria-hidden="true">·</span>
                  <button
                    type="button"
                    aria-label={`Edit canonical name for ${label}`}
                    aria-expanded={renamingCharacterId === entry.id}
                    onClick={() => {
                      setDeletingCharacterId('')
                      setCharacterDeleteError('')
                      setRenamingCharacterId(entry.id)
                      setCharacterRenameName(label)
                      setCharacterRenameKind(figureKind)
                      setCharacterRenameError('')
                    }}
                  >edit name</button>
                </>}
                {onDeleteCharacter && <>
                  <span aria-hidden="true">·</span>
                  <button
                    type="button"
                    className="remove"
                    aria-label={`Delete ${label}`}
                    aria-expanded={deletingCharacterId === entry.id}
                    onClick={() => {
                      setRenamingCharacterId('')
                      setCharacterRenameName('')
                      setCharacterRenameError('')
                      setDeletingCharacterId(entry.id)
                      setCharacterDeleteError('')
                    }}
                  >delete</button>
                </>}
                <span aria-hidden="true">·</span>
                <button
                  type="button"
                  disabled={!characterIndexControlPanel}
                  onClick={() => {
                    if (showContextualAssistance) {
                      setShowContextualAssistance(false)
                    } else {
                      openSecondHand()
                    }
                  }}
                >Second hand</button>
              </div>
            )}
            {isActive && analyticalTaskSet && selectedUnit &&
              onCreateAnalyticalCheckpoint &&
              onSetAnalyticalWorkstreamState &&
              onSetAnalyticalCheckpointState && (
                <ReaderAnalyticalWorkPackage
                  taskSet={analyticalTaskSet}
                  characterId={entry.id}
                  witnessId={sourceWitnessId}
                  currentPosition={{ rowId: selectedUnit.rowId, unitLabel: selectedUnit.rowLabel }}
                  onCreateCheckpoint={onCreateAnalyticalCheckpoint}
                  onSetWorkstreamState={onSetAnalyticalWorkstreamState}
                  onSetCheckpointState={onSetAnalyticalCheckpointState}
                  onResumePosition={position => {
                    if (!model.units.some(unit => unit.rowId === position.rowId)) return
                    setSelectedRowId(position.rowId)
                    setSelectedPeerIndex(0)
                    setFocusLevel('reader')
                  }}
                />
              )}
            {isActive && deletingCharacterId === entry.id && onDeleteCharacter && (
              <div
                className="lectio-character-create lectio-character-delete"
                role="group"
                aria-label={`Delete ${label} character`}
              >
                <span>Delete {label}? The character record and its readings will leave the working index.</span>
                <span className="lectio-character-create-actions">
                  <button
                    type="button"
                    className="remove"
                    disabled={characterDeletePending}
                    onClick={() => void deleteCharacterFromMargin(entry.id, label)}
                  >{characterDeletePending ? 'Deleting…' : 'Delete character'}</button>
                  <span aria-hidden="true">·</span>
                  <button
                    type="button"
                    disabled={characterDeletePending}
                    onClick={() => {
                      setDeletingCharacterId('')
                      setCharacterDeleteError('')
                    }}
                  >Cancel deletion</button>
                </span>
                {characterDeleteError && <span role="status">{characterDeleteError}</span>}
              </div>
            )}
            {isActive && renamingCharacterId === entry.id && onRenameCharacter && (
              <form
                className="lectio-character-create lectio-character-rename"
                aria-label={`Edit canonical name for ${label}`}
                onSubmit={event => {
                  event.preventDefault()
                  void saveCanonicalName()
                }}
              >
                <label htmlFor={`lectio-character-name-${entry.id}`}>Canonical name</label>
                <input
                  id={`lectio-character-name-${entry.id}`}
                  type="text"
                  aria-label="Canonical name"
                  value={characterRenameName}
                  onChange={event => setCharacterRenameName(event.target.value)}
                  autoFocus
                  disabled={characterRenamePending}
                />
                <label htmlFor={`lectio-character-kind-${entry.id}`}>Figure type</label>
                <select
                  id={`lectio-character-kind-${entry.id}`}
                  aria-label="Figure type"
                  value={characterRenameKind}
                  onChange={event => setCharacterRenameKind(event.target.value as CharacterIndexFigureKind)}
                  disabled={characterRenamePending}
                >
                  {CHARACTER_FIGURE_KIND_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
                <span className="lectio-character-create-actions">
                  <button type="submit" disabled={!characterRenameName.trim() || characterRenamePending}>
                    {characterRenamePending ? 'Saving…' : 'Save canonical name'}
                  </button>
                  <span aria-hidden="true">·</span>
                  <button
                    type="button"
                    disabled={characterRenamePending}
                    onClick={() => {
                      setRenamingCharacterId('')
                      setCharacterRenameName('')
                      setCharacterRenameKind('person')
                      setCharacterRenameError('')
                    }}
                  >cancel</button>
                </span>
                {characterRenameError && <span role="status">{characterRenameError}</span>}
              </form>
            )}
            {isActive && entrySubJudiceCount > 0 && onAdjudicateCharacterEvidence && (
              <div className="lectio-index-state" role="group" aria-label={`${label} sub judice readings`}>
                <b>{entrySubJudiceCount} reading{entrySubJudiceCount === 1 ? '' : 's'} sub judice</b>
                <button type="button" className="admit" onClick={() => verdictAllSubJudice(entry.id, 'admit')}>admit all</button>
                <button type="button" className="relegate" onClick={() => verdictAllSubJudice(entry.id, 'relegate')}>relegate all</button>
              </div>
            )}
          </div>
        )
      })}</div>}
      <p className="lectio-character-margin-foot">
        One entry per canonical identity; evidence-free persons remain visible for editorial completion.
      </p>
    </nav>
  )

  const textualBeings = (
    <section
      ref={readerSurfaceRef}
      className="lectio-textual-beings"
      data-testid="lectio-textual-beings"
      aria-label="Textual beings"
      tabIndex={0}
    >
      {lectioHeadnote}
      <div className="lectio-textual-beings-head">
        <div>
          <span className="cte-overline">All persons · one witness</span>
          <h2>Textual beings</h2>
          <p>
            Each card is only the focalization and characterization prose that constructs the figure in {sourceLabel}.
            Coreference holds the being together without becoming card prose.
          </p>
        </div>
        <button type="button" onClick={returnToContinuousCharacterText}>Return to text</button>
      </div>
      <div className="lectio-textual-being-grid">
        {characterMarginEntries.map(entry => {
          const pieces = textualBeingCardsById.get(entry.id)?.pieces || []
          const spanCount = pieces.filter(piece => piece.type === 'span').length
          const label = getCharacterDisplayLabel(entry)
          const decoration = characterDecorationsById.get(entry.id) || {
            chip: entry.id,
            color: getCharacterColor(entry.id),
          }
          return (
            <article
              key={entry.id}
              className="lectio-textual-being-card"
              style={{ '--cte-character-color': decoration.color } as React.CSSProperties}
            >
              <header>
                <div>
                  <span className="lectio-index-kicker">textual being · {spanCount} lection{spanCount === 1 ? '' : 's'}</span>
                  <h3>{label}</h3>
                  <small>{entry.transliteration.primary || entry.names.primary || entry.id}</small>
                </div>
                <button type="button" onClick={() => selectMarginalCharacter(entry.id)}>
                  Open {label} in text
                </button>
              </header>
              <div className="lectio-textual-being-evidence">
                {pieces.length === 0 ? (
                  <p>
                    No focalization or characterization prose constructs this being yet.{' '}
                    <button
                      type="button"
                      disabled={!characterIndexControlPanel}
                      onClick={() => {
                        setActiveMarginalCharacterId(entry.id)
                        onSelectCharacter?.(entry.id)
                        setShowTextualBeings(false)
                        openSecondHand()
                      }}
                    >summon the second hand</button>
                  </p>
                ) : pieces.map((piece, index) => piece.type === 'ellipsis' ? (
                  <span key={`ellipsis:${index}`} className="lectio-evidence-ellipsis" aria-hidden="true">…</span>
                ) : (
                  <blockquote key={piece.evidenceId}>
                    <small>
                      {(piece.kinds || (piece.kind ? [piece.kind] : [])).join(' + ')} ·{' '}
                      {rowLabelById.get(piece.rowId || '') || piece.rowId}
                    </small>
                    {piece.text}
                  </blockquote>
                ))}
              </div>
            </article>
          )
        })}
      </div>
    </section>
  )

  const handleEvidenceMenuKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const target = event.target instanceof HTMLElement ? event.target : null
    const characterizationBranch = target?.dataset.characterizationBranch as (
      LectioCharacterizationBranch | undefined
    )
    const directSource = target?.dataset.characterizationDirectSource as (
      LectioManualCharacterizationDirectSource | undefined
    )
    const indirectChannel = target?.dataset.characterizationIndirectChannel as (
      LectioManualCharacterizationIndirectChannel | undefined
    )
    if (event.key === 'ArrowRight' && characterizationBranch) {
      event.preventDefault()
      event.stopPropagation()
      openCharacterizationBranch(characterizationBranch)
      return
    }
    if (event.key === 'ArrowRight' && directSource) {
      event.preventDefault()
      event.stopPropagation()
      selectDirectCharacterizationSource(directSource)
      return
    }
    if (event.key === 'ArrowRight' && indirectChannel) {
      event.preventDefault()
      event.stopPropagation()
      selectIndirectCharacterizationChannel(indirectChannel)
      return
    }
    if (
      event.key === 'ArrowLeft'
      && evidenceMenu?.characterizationDetail
      && target?.closest('.lectio-characterization-detail-submenu')
    ) {
      event.preventDefault()
      event.stopPropagation()
      setEvidenceMenu(current => current ? {
        ...current,
        characterizationDetail: current.characterizationDetail === 'other-character-picker'
          ? 'other'
          : current.characterizationDetail === 'reaction-character-picker'
            ? 'reaction'
            : null,
      } : current)
      return
    }
    if (
      event.key === 'ArrowLeft'
      && evidenceMenu?.characterizationBranch
      && target?.closest('.lectio-characterization-branch-submenu')
    ) {
      event.preventDefault()
      event.stopPropagation()
      setEvidenceMenu(current => current ? { ...current, characterizationBranch: null } : current)
      return
    }
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') {
      return
    }
    const focusScope = target?.closest<HTMLElement>('[role="menu"]') || evidenceMenuRef.current
    const items = Array.from(
      focusScope?.querySelectorAll<HTMLElement>(
        '[role="menuitem"], [role="menuitemradio"], [role="menuitemcheckbox"]',
      ) || [],
    ).filter(item => !item.hasAttribute('disabled'))
    if (items.length === 0) {
      return
    }
    event.preventDefault()
    event.stopPropagation()
    const currentIndex = items.indexOf(document.activeElement as HTMLElement)
    const direction = event.key === 'ArrowDown' ? 1 : -1
    const nextIndex = currentIndex < 0
      ? 0
      : (currentIndex + direction + items.length) % items.length
    items[nextIndex]?.focus()
  }

  const evidenceMenuAriaLabel = evidenceMenu
    ? `Classify ${evidenceMenu.target.text} ${
        evidenceMenu.target.sourceKind === 'draft'
          ? 'new reading'
          : evidenceMenu.target.sourceKind === 'occurrence'
            ? 'occurrence'
            : 'layer'
      }`
    : ''
  const claimReceiverEntries = evidenceMenu && evidenceMenu.target.sourceKind !== 'draft'
    ? effectiveCharacterIndexEntries.filter(entry => entry.id !== evidenceMenu.target.characterId)
    : []
  const sharedReferentEntries = evidenceMenu && evidenceMenu.target.sourceKind !== 'layer'
    ? effectiveCharacterIndexEntries.filter(entry => entry.id !== evidenceMenu.target.characterId)
    : []
  const characterizationCharacterEntries = evidenceMenu?.submenu === 'characterization'
    ? effectiveCharacterIndexEntries.filter(entry => entry.id !== evidenceMenu.target.characterId)
    : []
  const manualCharacterizationDraft = evidenceMenu?.manualCharacterization
  const manualCharacterizationError = manualCharacterizationDraft
    ? getManualCharacterizationValidationError(manualCharacterizationDraft)
    : 'Choose direct or indirect characterization'
  const selectedSourceCharacter = manualCharacterizationDraft?.sourceCharacterId
    ? effectiveCharacterIndexEntries.find(entry => entry.id === manualCharacterizationDraft.sourceCharacterId)
    : undefined
  const selectedReactingCharacter = manualCharacterizationDraft?.relatedCharacterId
    ? effectiveCharacterIndexEntries.find(entry => entry.id === manualCharacterizationDraft.relatedCharacterId)
    : undefined
  const activeDirectForms = manualCharacterizationDraft?.directSource === 'narratorial'
    ? LECTIO_MANUAL_CHARACTERIZATION_NARRATORIAL_FORMS
    : LECTIO_MANUAL_CHARACTERIZATION_DIRECT_FORMS
  const speakerAnalysis = evidenceMenu?.speakerAnalysis
  const speakerAnalysisError = speakerAnalysis
    ? getLectioSpeakerAnalysisValidationError(speakerAnalysis)
    : ''
  const speakerAddressLabel = speakerAnalysis
    ? getSpeakerChoiceLabel(LECTIO_SPEAKER_ADDRESS_MODES, speakerAnalysis.addressMode)
    : 'Addressee indeterminate'
  const speakerAddresseeEntries = evidenceMenu?.submenu === 'speaker'
    ? effectiveCharacterIndexEntries.filter(entry => entry.id !== evidenceMenu.target.characterId)
    : []
  const speakerAddresseeLabels = speakerAnalysis?.addresseeCharacterIds.map(characterId => {
    const entry = effectiveCharacterIndexEntries.find(candidate => candidate.id === characterId)
    return entry ? getCharacterDisplayLabel(entry) : characterId
  }) || []
  const speakerRetrospectiveCandidates = evidenceMenu?.submenu === 'speaker'
    ? speakerTurnRails.filter(turn => {
        if (turn.sourceTurnIds.includes(evidenceMenu.target.evidenceId)) {
          return false
        }
        return compareReaderPositions(
          { rowId: turn.endRowId, offset: turn.endOffset },
          { rowId: evidenceMenu.target.rowId, offset: evidenceMenu.target.start },
          rowOrderById,
        ) < 0
      })
    : []
  const evidenceResearchNoteTarget = evidenceMenu
    ? buildResearchNoteTargetForEvidence(evidenceMenu.target)
    : null
  const evidenceResearchNoteCount = evidenceResearchNoteTarget
    ? getResearchNoteCount(evidenceResearchNoteTarget)
    : 0

  const evidenceContextMenu = evidenceMenu ? (
    <div
      ref={evidenceMenuRef}
      className="lectio-evidence-context-menu"
      style={{ left: evidenceMenu.anchorLeft, top: evidenceMenu.anchorBottom + 6 }}
      onKeyDown={handleEvidenceMenuKeyDown}
      onPointerDown={event => event.stopPropagation()}
    >
      <div
        className="lectio-evidence-context-menu-panel"
        role="menu"
        aria-label={evidenceMenuAriaLabel}
      >
        <div className="lectio-evidence-context-menu-head">
          <b>{evidenceMenu.target.text}</b>
          <span>{evidenceMenu.target.rowLabel} · {evidenceMenu.target.start}–{evidenceMenu.target.end}</span>
        </div>
        {evidenceMenu.target.sourceKind !== 'layer' && (
          <div className="lectio-evidence-context-menu-group" role="group" aria-label="Occurrence reading">
            <button
              type="button"
              role={evidenceMenu.target.sourceKind === 'draft' ? 'menuitem' : 'menuitemradio'}
              aria-checked={evidenceMenu.target.sourceKind === 'occurrence'
                ? evidenceMenu.target.mentionType === 'name'
                : undefined}
              onClick={() => classifyOccurrenceFromMenu('name')}
            >Read as name</button>
            <button
              type="button"
              role={evidenceMenu.target.sourceKind === 'draft' ? 'menuitem' : 'menuitemradio'}
              aria-checked={evidenceMenu.target.sourceKind === 'occurrence'
                ? evidenceMenu.target.mentionType === 'coref'
                : undefined}
              onClick={() => classifyOccurrenceFromMenu('coref')}
            >Read as coreference</button>
            {evidenceMenu.target.sourceKind === 'draft' && (
              <button
                type="button"
                role="menuitem"
                aria-haspopup="menu"
                aria-expanded={evidenceMenu.submenu === 'share'}
                onClick={openShareSubmenu}
              >Read as shared coreference…</button>
            )}
          </div>
        )}
        <div className="lectio-evidence-context-menu-group" role="group" aria-label="Analytic readings">
          <button
            type="button"
            role="menuitem"
            aria-haspopup="menu"
            aria-expanded={evidenceMenu.submenu === 'speaker'}
            data-selected={(evidenceMenu.target.sourceKind === 'layer' && evidenceMenu.target.layerKind === 'speaker') || undefined}
            onClick={() => openLayerSubmenu('speaker')}
          >{evidenceMenu.target.sourceKind === 'layer' && evidenceMenu.target.layerKind === 'speaker'
              ? 'Edit speaker analysis'
              : 'Add speaker span'}</button>
          <button
            type="button"
            role="menuitem"
            aria-haspopup="menu"
            aria-expanded={evidenceMenu.submenu === 'focalization'}
            onClick={() => openLayerSubmenu('focalization')}
          >{evidenceMenu.target.sourceKind === 'layer' ? 'Classify as focalization' : 'Add focalization'}</button>
          <button
            type="button"
            role="menuitem"
            aria-haspopup="menu"
            aria-expanded={evidenceMenu.submenu === 'characterization'}
            onClick={() => openLayerSubmenu('characterization')}
          >{evidenceMenu.target.sourceKind === 'layer' ? 'Classify as characterization' : 'Add characterization'}</button>
        </div>
        {evidenceResearchNoteTarget && (
          <div className="lectio-evidence-context-menu-group" role="group" aria-label="Research annotation">
            <button type="button" role="menuitem" onClick={openResearchNotesForEvidence}>
              {evidenceResearchNoteCount > 0
                ? `Research notes (${evidenceResearchNoteCount})…`
                : 'Research notes…'}
            </button>
          </div>
        )}
        {evidenceMenu.target.sourceKind !== 'draft' && (
        <div className="lectio-evidence-context-menu-group" role="group" aria-label="Editorial standing">
          {evidenceMenu.target.sourceKind === 'occurrence' && (
            <button
              type="button"
              role="menuitem"
              aria-haspopup="menu"
              aria-expanded={evidenceMenu.submenu === 'share'}
              onClick={openShareSubmenu}
            >Share reference with…</button>
          )}
          <button
            type="button"
            role="menuitem"
            aria-haspopup="menu"
            aria-expanded={evidenceMenu.submenu === 'claim'}
            onClick={openClaimSubmenu}
          >Claim for…</button>
          <button type="button" role="menuitem" onClick={() => issueEvidenceVerdictFromMenu('admit')}>Admit reading</button>
          <button type="button" role="menuitem" onClick={() => issueEvidenceVerdictFromMenu('relegate')}>Relegate reading</button>
          <button type="button" role="menuitem" onClick={() => closeEvidenceMenu(true)}>Adjust span boundaries</button>
          <button type="button" role="menuitem" className="remove" onClick={() => issueEvidenceVerdictFromMenu('remove')}>Remove reading</button>
        </div>
        )}
      </div>
      {(evidenceMenu.submenu === 'speaker' || evidenceMenu.submenu === 'focalization' || evidenceMenu.submenu === 'characterization') && (
        <div
          className="lectio-evidence-context-menu-panel lectio-evidence-context-submenu"
          role="menu"
          aria-label={evidenceMenu.submenu === 'speaker'
            ? 'Speaker analysis options'
            : `${formatLectioMenuChoice(evidenceMenu.submenu)} options`}
        >
          <div className="lectio-evidence-context-menu-head">
            <b>{evidenceMenu.submenu === 'speaker' ? 'Speaker analysis' : formatLectioMenuChoice(evidenceMenu.submenu)}</b>
            <span>
              {evidenceMenu.submenu === 'speaker'
                ? 'speech situation · discourse · orientation · stance'
                : evidenceMenu.submenu === 'characterization'
                ? 'interpretive relation · progressive disclosure'
                : 'classification · explicit judgment'}
            </span>
          </div>
          {evidenceMenu.submenu === 'speaker' ? (
            <div className="lectio-evidence-context-menu-group" role="group" aria-label="Speaker analysis dimensions">
              <button
                type="button"
                role="menuitem"
                aria-haspopup="menu"
                aria-expanded={evidenceMenu.speakerBranch === 'address'}
                onClick={() => openSpeakerBranch('address')}
              >Address: {speakerAddressLabel}</button>
              {(speakerAnalysis?.addressMode === 'individual' || speakerAnalysis?.addressMode === 'collective') && (
                <button
                  type="button"
                  role="menuitem"
                  aria-haspopup="menu"
                  aria-expanded={evidenceMenu.speakerBranch === 'addressees'}
                  onClick={() => openSpeakerBranch('addressees')}
                >Addressees: {speakerAddresseeLabels.join(', ') || 'none selected'}</button>
              )}
              <button
                type="button"
                role="menuitem"
                aria-haspopup="menu"
                aria-expanded={evidenceMenu.speakerBranch === 'functions'}
                onClick={() => openSpeakerBranch('functions')}
              >Functions: {summarizeSpeakerChoices(LECTIO_SPEAKER_DISCOURSE_FUNCTIONS, speakerAnalysis?.functions || [])}</button>
              <button
                type="button"
                role="menuitem"
                aria-haspopup="menu"
                aria-expanded={evidenceMenu.speakerBranch === 'forms'}
                onClick={() => openSpeakerBranch('forms')}
              >Forms: {summarizeSpeakerChoices(LECTIO_SPEAKER_DISCOURSE_FORMS, speakerAnalysis?.forms || [])}</button>
              <button
                type="button"
                role="menuitem"
                aria-haspopup="menu"
                aria-expanded={evidenceMenu.speakerBranch === 'orientations'}
                onClick={() => openSpeakerBranch('orientations')}
              >Orientations: {summarizeSpeakerChoices(LECTIO_SPEAKER_ORIENTATIONS, speakerAnalysis?.orientations || [])}</button>
              <button
                type="button"
                role="menuitem"
                aria-haspopup="menu"
                aria-expanded={evidenceMenu.speakerBranch === 'stances'}
                onClick={() => openSpeakerBranch('stances')}
              >Stances: {summarizeSpeakerChoices(LECTIO_SPEAKER_STANCES, speakerAnalysis?.stances || [])}</button>
              <button
                type="button"
                role="menuitem"
                aria-haspopup="menu"
                aria-expanded={evidenceMenu.speakerBranch === 'retrospective'}
                onClick={() => openSpeakerBranch('retrospective')}
              >Retrospective target: {speakerAnalysis?.retrospectiveStatus || 'none'}</button>
            </div>
          ) : evidenceMenu.submenu === 'focalization' ? (
            <div className="lectio-evidence-context-menu-group" role="group" aria-label="Classification">
              {LECTIO_FOCALIZATION_CLASSES.map(classification => (
                <button
                  key={classification}
                  type="button"
                  role="menuitemradio"
                  aria-checked={evidenceMenu.classification === classification}
                  onClick={() => setEvidenceMenu(current => current ? { ...current, classification } : current)}
                >{formatLectioMenuChoice(classification)}</button>
              ))}
            </div>
          ) : (
            <div className="lectio-evidence-context-menu-group" role="group" aria-label="Characterization relation">
              <button
                type="button"
                role="menuitem"
                aria-haspopup="menu"
                aria-expanded={evidenceMenu.characterizationBranch === 'direct'}
                data-characterization-branch="direct"
                onClick={() => openCharacterizationBranch('direct')}
              >Direct</button>
              <button
                type="button"
                role="menuitem"
                aria-haspopup="menu"
                aria-expanded={evidenceMenu.characterizationBranch === 'indirect'}
                data-characterization-branch="indirect"
                onClick={() => openCharacterizationBranch('indirect')}
              >Indirect</button>
              <button
                type="button"
                role="menuitem"
                aria-haspopup="menu"
                aria-expanded={evidenceMenu.characterizationBranch === 'modifiers'}
                data-characterization-branch="modifiers"
                onClick={() => openCharacterizationBranch('modifiers')}
              >Add modifier</button>
            </div>
          )}
          {evidenceMenu.submenu === 'characterization' && (
            <div className="lectio-characterization-summary" role="presentation">
              <span>{evidenceMenu.manualCharacterization.mode || 'relation unset'}</span>
              <b>{manualCharacterizationError || 'ready to admit'}</b>
            </div>
          )}
          {evidenceMenu.submenu === 'speaker' && (
            <div className="lectio-characterization-summary" role="presentation">
              <span>{speakerAnalysis?.functions.length || 0} functions · {speakerAnalysis?.forms.length || 0} forms</span>
              <b>{speakerAnalysisError || 'ready to admit'}</b>
            </div>
          )}
          {evidenceMenu.submenu === 'focalization' && (
            <div className="lectio-evidence-context-menu-group" role="group" aria-label="Voice">
              <span className="lectio-evidence-context-menu-label">Voice</span>
              {LECTIO_VOICES.map(voice => (
                <button
                  key={voice}
                  type="button"
                  role="menuitemradio"
                  aria-checked={evidenceMenu.voice === voice}
                  onClick={() => setEvidenceMenu(current => current ? { ...current, voice } : current)}
                >{formatLectioMenuChoice(voice)}</button>
              ))}
            </div>
          )}
          <div className="lectio-evidence-context-menu-group lectio-evidence-context-menu-commit">
            <button
              type="button"
              role="menuitem"
              disabled={(evidenceMenu.submenu === 'characterization' && Boolean(manualCharacterizationError))
                || (evidenceMenu.submenu === 'speaker' && Boolean(speakerAnalysisError))}
              onClick={evidenceMenu.submenu === 'speaker'
                ? commitSpeakerFromMenu
                : commitLayerClassificationFromMenu}
            >
              {evidenceMenu.target.sourceKind === 'layer' ? 'Apply' : 'Admit'} {evidenceMenu.submenu === 'speaker'
                ? 'speaker analysis'
                : evidenceMenu.submenu}
            </button>
          </div>
        </div>
      )}
      {evidenceMenu.submenu === 'speaker' && evidenceMenu.speakerBranch === 'address' && (
        <div
          className="lectio-evidence-context-menu-panel lectio-evidence-context-submenu lectio-speaker-branch-submenu"
          role="menu"
          aria-label="Speaker address"
        >
          <div className="lectio-evidence-context-menu-head">
            <b>Address</b>
            <span>the speech situation, not the speaker's identity</span>
          </div>
          <div className="lectio-evidence-context-menu-group" role="group" aria-label="Address mode">
            {LECTIO_SPEAKER_ADDRESS_MODES.map(option => (
              <button
                key={option.id}
                type="button"
                role="menuitemradio"
                aria-checked={speakerAnalysis?.addressMode === option.id}
                onClick={() => selectSpeakerAddressMode(option.id)}
              >{option.label}</button>
            ))}
          </div>
        </div>
      )}
      {evidenceMenu.submenu === 'speaker' && evidenceMenu.speakerBranch === 'addressees' && (
        <div
          className="lectio-evidence-context-menu-panel lectio-evidence-context-submenu lectio-speaker-branch-submenu"
          role="menu"
          aria-label="Speaker addressees"
        >
          <div className="lectio-evidence-context-menu-head">
            <b>{speakerAnalysis?.addressMode === 'individual' ? 'Addressee' : 'Addressed group'}</b>
            <span>{speakerAnalysis?.addressMode === 'individual' ? 'choose exactly one figure' : 'choose every addressed figure'}</span>
          </div>
          <div className="lectio-evidence-context-menu-group" role="group" aria-label="Available addressees">
            {speakerAddresseeEntries.length === 0 ? (
              <span className="lectio-evidence-context-menu-empty">No other character is available</span>
            ) : speakerAddresseeEntries.map(entry => (
              <button
                key={entry.id}
                type="button"
                role={speakerAnalysis?.addressMode === 'individual' ? 'menuitemradio' : 'menuitemcheckbox'}
                aria-checked={speakerAnalysis?.addresseeCharacterIds.includes(entry.id) || false}
                onClick={() => toggleSpeakerAddressee(entry.id)}
              >{getCharacterDisplayLabel(entry)}</button>
            ))}
          </div>
        </div>
      )}
      {evidenceMenu.submenu === 'speaker' && evidenceMenu.speakerBranch === 'functions' && (
        <div
          className="lectio-evidence-context-menu-panel lectio-evidence-context-submenu lectio-speaker-branch-submenu"
          role="menu"
          aria-label="Speaker functions"
        >
          <div className="lectio-evidence-context-menu-head">
            <b>Discourse functions</b>
            <span>additive · choose every operative function</span>
          </div>
          <div className="lectio-evidence-context-menu-group" role="group" aria-label="Discourse functions">
            {LECTIO_SPEAKER_DISCOURSE_FUNCTIONS.map(option => (
              <button
                key={option.id}
                type="button"
                role="menuitemcheckbox"
                aria-checked={speakerAnalysis?.functions.includes(option.id) || false}
                onClick={() => toggleSpeakerClassification('functions', option.id)}
              >{option.label}</button>
            ))}
          </div>
        </div>
      )}
      {evidenceMenu.submenu === 'speaker' && evidenceMenu.speakerBranch === 'forms' && (
        <div
          className="lectio-evidence-context-menu-panel lectio-evidence-context-submenu lectio-speaker-branch-submenu"
          role="menu"
          aria-label="Speaker forms"
        >
          <div className="lectio-evidence-context-menu-head">
            <b>Discourse forms</b>
            <span>rhetorical form remains distinct from function</span>
          </div>
          <div className="lectio-evidence-context-menu-group" role="group" aria-label="Discourse forms">
            {LECTIO_SPEAKER_DISCOURSE_FORMS.map(option => (
              <button
                key={option.id}
                type="button"
                role="menuitemcheckbox"
                aria-checked={speakerAnalysis?.forms.includes(option.id) || false}
                onClick={() => toggleSpeakerClassification('forms', option.id)}
              >{option.label}</button>
            ))}
          </div>
        </div>
      )}
      {evidenceMenu.submenu === 'speaker' && evidenceMenu.speakerBranch === 'orientations' && (
        <div
          className="lectio-evidence-context-menu-panel lectio-evidence-context-submenu lectio-speaker-branch-submenu"
          role="menu"
          aria-label="Speaker orientations"
        >
          <div className="lectio-evidence-context-menu-head">
            <b>Orientation</b>
            <span>what the utterance is directed toward</span>
          </div>
          <div className="lectio-evidence-context-menu-group" role="group" aria-label="Discourse orientations">
            {LECTIO_SPEAKER_ORIENTATIONS.map(option => (
              <button
                key={option.id}
                type="button"
                role="menuitemcheckbox"
                aria-checked={speakerAnalysis?.orientations.includes(option.id) || false}
                onClick={() => toggleSpeakerClassification('orientations', option.id)}
              >{option.label}</button>
            ))}
          </div>
        </div>
      )}
      {evidenceMenu.submenu === 'speaker' && evidenceMenu.speakerBranch === 'stances' && (
        <div
          className="lectio-evidence-context-menu-panel lectio-evidence-context-submenu lectio-speaker-branch-submenu"
          role="menu"
          aria-label="Speaker stances"
        >
          <div className="lectio-evidence-context-menu-head">
            <b>Temporal / epistemic stance</b>
            <span>how the speaker knows and locates the utterance</span>
          </div>
          <div className="lectio-evidence-context-menu-group" role="group" aria-label="Temporal and epistemic stances">
            {LECTIO_SPEAKER_STANCES.map(option => (
              <button
                key={option.id}
                type="button"
                role="menuitemcheckbox"
                aria-checked={speakerAnalysis?.stances.includes(option.id) || false}
                onClick={() => toggleSpeakerClassification('stances', option.id)}
              >{option.label}</button>
            ))}
          </div>
        </div>
      )}
      {evidenceMenu.submenu === 'speaker' && evidenceMenu.speakerBranch === 'retrospective' && (
        <div
          className="lectio-evidence-context-menu-panel lectio-evidence-context-submenu lectio-speaker-branch-submenu"
          role="menu"
          aria-label="Speaker retrospective target"
        >
          <div className="lectio-evidence-context-menu-head">
            <b>Retrospective target</b>
            <span>link memory to prior discourse when the text permits</span>
          </div>
          <div className="lectio-evidence-context-menu-group" role="group" aria-label="Target status">
            <button
              type="button"
              role="menuitemradio"
              aria-checked={speakerAnalysis?.retrospectiveStatus === 'none'}
              onClick={() => setSpeakerRetrospectiveStatus('none')}
            >No retrospective target</button>
            <button
              type="button"
              role="menuitemradio"
              aria-checked={speakerAnalysis?.retrospectiveStatus === 'unresolved'}
              onClick={() => setSpeakerRetrospectiveStatus('unresolved')}
            >Target not yet identified</button>
          </div>
          <div className="lectio-evidence-context-menu-group" role="group" aria-label="Earlier speech turns">
            <span className="lectio-evidence-context-menu-label">Earlier speech turns</span>
            {speakerRetrospectiveCandidates.length === 0 ? (
              <span className="lectio-evidence-context-menu-empty">No earlier speech turn is available in this text</span>
            ) : speakerRetrospectiveCandidates.map(turn => {
              const target: LectioSpeakerRetrospectiveTarget = {
                kind: 'speech-turn',
                targetId: turn.turnId,
                witnessId: sourceWitnessId,
                rowId: turn.startRowId,
                endRowId: turn.endRowId,
                start: turn.startOffset,
                end: turn.endOffset,
                label: `${turn.label} · ${turn.startRowLabel}${turn.startRowId === turn.endRowId ? '' : `–${turn.endRowLabel}`}`,
              }
              const selected = speakerAnalysis?.retrospectiveTargets.some(candidate => (
                candidate.kind === target.kind && candidate.targetId === target.targetId
              )) || false
              return (
                <button
                  key={turn.turnId}
                  type="button"
                  role="menuitemcheckbox"
                  aria-checked={selected}
                  onClick={() => toggleSpeakerRetrospectiveTarget(target)}
                >{target.label}</button>
              )
            })}
          </div>
        </div>
      )}
      {evidenceMenu.submenu === 'characterization' && evidenceMenu.characterizationBranch === 'direct' && (
        <div
          className="lectio-evidence-context-menu-panel lectio-evidence-context-submenu lectio-characterization-branch-submenu"
          role="menu"
          aria-label="Direct characterization sources"
        >
          <div className="lectio-evidence-context-menu-head">
            <b>Direct characterization</b>
            <span>who supplies the characterization?</span>
          </div>
          <div className="lectio-evidence-context-menu-group" role="group" aria-label="Direct source">
            {([
              ['self', 'Self-characterization'],
              ['other', 'By another character'],
              ['narratorial', 'Narratorial'],
            ] as const).map(([source, label]) => (
              <button
                key={source}
                type="button"
                role="menuitem"
                aria-haspopup="menu"
                aria-expanded={evidenceMenu.characterizationDetail === source}
                data-characterization-direct-source={source}
                onClick={() => selectDirectCharacterizationSource(source)}
              >{label}</button>
            ))}
          </div>
        </div>
      )}
      {evidenceMenu.submenu === 'characterization' && evidenceMenu.characterizationBranch === 'indirect' && (
        <div
          className="lectio-evidence-context-menu-panel lectio-evidence-context-submenu lectio-characterization-branch-submenu"
          role="menu"
          aria-label="Indirect characterization channels"
        >
          <div className="lectio-evidence-context-menu-head">
            <b>Indirect characterization</b>
            <span>how does the passage construct the figure?</span>
          </div>
          <div className="lectio-evidence-context-menu-group" role="group" aria-label="Indirect channel">
            {LECTIO_MANUAL_CHARACTERIZATION_INDIRECT_CHANNELS.map(channel => {
              const opensDetail = channel.id === 'action_conduct' || channel.id === 'others_reaction'
              return (
                <button
                  key={channel.id}
                  type="button"
                  role="menuitem"
                  aria-haspopup={opensDetail ? 'menu' : undefined}
                  aria-expanded={channel.id === 'action_conduct'
                    ? evidenceMenu.characterizationDetail === 'action'
                    : channel.id === 'others_reaction'
                      ? evidenceMenu.characterizationDetail === 'reaction'
                      : undefined}
                  data-characterization-indirect-channel={channel.id}
                  data-selected={evidenceMenu.manualCharacterization.indirectChannel === channel.id || undefined}
                  onClick={() => selectIndirectCharacterizationChannel(channel.id)}
                >{channel.label}</button>
              )
            })}
          </div>
        </div>
      )}
      {evidenceMenu.submenu === 'characterization' && evidenceMenu.characterizationBranch === 'modifiers' && (
        <div
          className="lectio-evidence-context-menu-panel lectio-evidence-context-submenu lectio-characterization-branch-submenu"
          role="menu"
          aria-label="Characterization modifiers"
        >
          <div className="lectio-evidence-context-menu-head">
            <b>Optional modifiers</b>
            <span>additive · choose any that matter</span>
          </div>
          <div className="lectio-evidence-context-menu-group" role="group" aria-label="Modifiers">
            {LECTIO_MANUAL_CHARACTERIZATION_MODIFIERS.map(modifier => (
              <button
                key={modifier.id}
                type="button"
                role="menuitemcheckbox"
                aria-checked={evidenceMenu.manualCharacterization.modifiers.includes(modifier.id)}
                onClick={() => toggleManualCharacterizationModifier(modifier.id)}
              >{modifier.label}</button>
            ))}
          </div>
        </div>
      )}
      {evidenceMenu.submenu === 'characterization' && (
        evidenceMenu.characterizationDetail === 'self'
        || evidenceMenu.characterizationDetail === 'other'
        || evidenceMenu.characterizationDetail === 'narratorial'
      ) && (
        <div
          className="lectio-evidence-context-menu-panel lectio-evidence-context-submenu lectio-characterization-detail-submenu"
          role="menu"
          aria-label={evidenceMenu.characterizationDetail === 'self'
            ? 'Self-characterization details'
            : evidenceMenu.characterizationDetail === 'other'
              ? 'By another character details'
              : 'Narratorial characterization details'}
        >
          <div className="lectio-evidence-context-menu-head">
            <b>{evidenceMenu.characterizationDetail === 'self'
              ? 'Self-characterization'
              : evidenceMenu.characterizationDetail === 'other'
                ? 'By another character'
                : 'Narratorial'}</b>
            <span>channel and form remain distinct</span>
          </div>
          {evidenceMenu.characterizationDetail === 'other' && (
            <div className="lectio-evidence-context-menu-group" role="group" aria-label="Source character">
              <span className="lectio-evidence-context-menu-label">Source character</span>
              <button
                type="button"
                role="menuitem"
                aria-haspopup="menu"
                onClick={() => openManualCharacterizationCharacterPicker('other')}
              >Choose source character…</button>
              {selectedSourceCharacter && (
                <span className="lectio-evidence-context-menu-selection">
                  {getCharacterDisplayLabel(selectedSourceCharacter)}
                </span>
              )}
            </div>
          )}
          {evidenceMenu.characterizationDetail !== 'narratorial' && (
            <div className="lectio-evidence-context-menu-group" role="group" aria-label="Channel">
              <span className="lectio-evidence-context-menu-label">Channel</span>
              {([
                ['spoken', 'Spoken'],
                ['interior', 'Interior'],
              ] as const).map(([channel, label]) => (
                <button
                  key={channel}
                  type="button"
                  role="menuitemradio"
                  aria-checked={evidenceMenu.manualCharacterization.channel === channel}
                  onClick={() => selectManualCharacterizationChannel(channel)}
                >{label}</button>
              ))}
            </div>
          )}
          <div className="lectio-evidence-context-menu-group" role="group" aria-label="Form">
            <span className="lectio-evidence-context-menu-label">Form · choose any</span>
            {activeDirectForms.map(form => (
              <button
                key={form.id}
                type="button"
                role="menuitemcheckbox"
                aria-checked={evidenceMenu.manualCharacterization.forms.includes(form.id)}
                onClick={() => toggleManualCharacterizationForm(form.id)}
              >{form.label}</button>
            ))}
          </div>
        </div>
      )}
      {evidenceMenu.submenu === 'characterization' && evidenceMenu.characterizationDetail === 'action' && (
        <div
          className="lectio-evidence-context-menu-panel lectio-evidence-context-submenu lectio-characterization-detail-submenu"
          role="menu"
          aria-label="Action and conduct details"
        >
          <div className="lectio-evidence-context-menu-head">
            <b>Action / conduct</b>
            <span>choose the strongest applicable reading</span>
          </div>
          <div className="lectio-evidence-context-menu-group" role="group" aria-label="Action or conduct">
            {LECTIO_MANUAL_CHARACTERIZATION_ACTION_DETAILS.map(detail => (
              <button
                key={detail.id}
                type="button"
                role="menuitemradio"
                aria-checked={evidenceMenu.manualCharacterization.indirectDetail === detail.id}
                onClick={() => selectManualCharacterizationIndirectDetail(detail.id)}
              >{detail.label}</button>
            ))}
          </div>
        </div>
      )}
      {evidenceMenu.submenu === 'characterization' && evidenceMenu.characterizationDetail === 'reaction' && (
        <div
          className="lectio-evidence-context-menu-panel lectio-evidence-context-submenu lectio-characterization-detail-submenu"
          role="menu"
          aria-label="Another's reaction details"
        >
          <div className="lectio-evidence-context-menu-head">
            <b>Another's reaction</b>
            <span>identify the reacting figure and reaction</span>
          </div>
          <div className="lectio-evidence-context-menu-group" role="group" aria-label="Reacting character">
            <button
              type="button"
              role="menuitem"
              aria-haspopup="menu"
              onClick={() => openManualCharacterizationCharacterPicker('reaction')}
            >Choose reacting character…</button>
            {selectedReactingCharacter && (
              <span className="lectio-evidence-context-menu-selection">
                {getCharacterDisplayLabel(selectedReactingCharacter)}
              </span>
            )}
          </div>
          <div className="lectio-evidence-context-menu-group" role="group" aria-label="Reaction">
            {LECTIO_MANUAL_CHARACTERIZATION_REACTION_DETAILS.map(detail => (
              <button
                key={detail.id}
                type="button"
                role="menuitemradio"
                aria-checked={evidenceMenu.manualCharacterization.indirectDetail === detail.id}
                onClick={() => selectManualCharacterizationIndirectDetail(detail.id)}
              >{detail.label}</button>
            ))}
          </div>
        </div>
      )}
      {evidenceMenu.submenu === 'characterization' && (
        evidenceMenu.characterizationDetail === 'other-character-picker'
        || evidenceMenu.characterizationDetail === 'reaction-character-picker'
      ) && (
        <div
          className="lectio-evidence-context-menu-panel lectio-evidence-context-submenu lectio-characterization-detail-submenu"
          role="menu"
          aria-label={evidenceMenu.characterizationDetail === 'other-character-picker'
            ? 'Choose source character'
            : 'Choose reacting character'}
        >
          <div className="lectio-evidence-context-menu-head">
            <b>{evidenceMenu.characterizationDetail === 'other-character-picker'
              ? 'Source character'
              : 'Reacting character'}</b>
            <span>the target character is already fixed</span>
          </div>
          <div className="lectio-evidence-context-menu-group" role="group" aria-label="Available characters">
            {characterizationCharacterEntries.length === 0 ? (
              <span className="lectio-evidence-context-menu-empty">No other character is available</span>
            ) : characterizationCharacterEntries.map(entry => {
              const isOtherPicker = evidenceMenu.characterizationDetail === 'other-character-picker'
              const selectedId = isOtherPicker
                ? evidenceMenu.manualCharacterization.sourceCharacterId
                : evidenceMenu.manualCharacterization.relatedCharacterId
              return (
                <button
                  key={entry.id}
                  type="button"
                  role="menuitemradio"
                  aria-checked={selectedId === entry.id}
                  onClick={() => selectManualCharacterizationRelatedCharacter(
                    isOtherPicker ? 'other' : 'reaction',
                    entry.id,
                  )}
                >{getCharacterDisplayLabel(entry)}</button>
              )
            })}
          </div>
        </div>
      )}
      {evidenceMenu.submenu === 'claim' && (
        <div
          className="lectio-evidence-context-menu-panel lectio-evidence-context-submenu"
          role="menu"
          aria-label={`Claim ${evidenceMenu.target.text} for character`}
        >
          <div className="lectio-evidence-context-menu-head">
            <b>Claim for</b>
            <span>
              this {evidenceMenu.target.sourceKind === 'layer'
                ? `${evidenceMenu.target.layerKind || 'analytic'} span`
                : 'occurrence'} only · other readings stay put
            </span>
          </div>
          <div className="lectio-evidence-context-menu-group" role="group" aria-label="Receiving character">
            {claimReceiverEntries.length === 0 ? (
              <span className="lectio-evidence-context-menu-empty">No other character is available</span>
            ) : claimReceiverEntries.map(entry => {
              const label = getCharacterDisplayLabel(entry)
              return (
                <button
                  key={entry.id}
                  type="button"
                  role="menuitem"
                  onClick={() => claimReadingFromMenu(entry.id, label)}
                >{label}</button>
              )
            })}
          </div>
        </div>
      )}
      {evidenceMenu.submenu === 'share' && (
        <div
          className="lectio-evidence-context-menu-panel lectio-evidence-context-submenu"
          role="menu"
          aria-label={`Share ${evidenceMenu.target.text} with character`}
        >
          <div className="lectio-evidence-context-menu-head">
            <b>Share reference with</b>
            <span>{evidenceMenu.target.sourceKind === 'draft'
              ? 'choose every referent · then admit one occurrence'
              : 'one occurrence · additive referents · toggle any figure'}</span>
          </div>
          <div className="lectio-evidence-context-menu-group" role="group" aria-label="Joint referents">
            {sharedReferentEntries.length === 0 ? (
              <span className="lectio-evidence-context-menu-empty">No other character is available</span>
            ) : sharedReferentEntries.map(entry => {
              const label = getCharacterDisplayLabel(entry)
              const referentIds = evidenceMenu.target.referentCharacterIds || [evidenceMenu.target.characterId]
              return (
                <button
                  key={entry.id}
                  type="button"
                  role="menuitemcheckbox"
                  aria-checked={referentIds.includes(entry.id)}
                  onClick={() => toggleSharedReferentFromMenu(entry.id, label)}
                >{label}</button>
              )
            })}
          </div>
          <div className="lectio-evidence-context-menu-group lectio-evidence-context-menu-commit">
            {evidenceMenu.target.sourceKind === 'draft' ? (
              <button
                type="button"
                role="menuitem"
                disabled={(evidenceMenu.target.referentCharacterIds || [])
                  .every(characterId => characterId === evidenceMenu.target.characterId)}
                onClick={commitSharedReferenceFromMenu}
              >Admit shared coreference</button>
            ) : (
              <button type="button" role="menuitem" onClick={() => closeEvidenceMenu(true)}>Done</button>
            )}
          </div>
        </div>
      )}
    </div>
  ) : null

  return (
    <section
      data-testid="ledger-safe-tradition-reader"
      data-native-source-witness-id={sourceWitnessId}
      data-native-work-id={rows.find(row => row.chapterId)?.chapterId || 'reader-document'}
      data-native-reader-section={readerSection}
      data-selected-row-id={selectedRowId}
      data-native-annotation-grammar={annotationGrammar}
      className="cross-text-edition ledger-tradition-edition"
      onKeyDown={handleReaderKeyDown}
    >
      <header className="cte-masthead">
        <div className="cte-masthead-left">
          Work · <b>{contextLabel || 'Variorum reader'}</b>
          <br />
          Source · <b>{sourceLabel}</b> · {model.units.length} rows
        </div>
        <div className="cte-masthead-title">
          <h1>THE READER</h1>
          <small>
            character index · lectio — the interface is a critical apparatus
          </small>
        </div>
        <div className="cte-masthead-right">
          <span>{characterMarginEntries.length} persons · {subJudiceCharacterCount} sub judice</span>
          <br />
          VARIORUM
        </div>
      </header>

      <nav className="lectio-reader-sections" aria-label="Reader modes and source scope">
        <div className="lectio-reader-section-tabs" role="tablist" aria-label="Reader sections">
          <button
            type="button"
            role="tab"
            aria-selected={readerSection === 'read'}
            className={readerSection === 'read' ? 'active' : ''}
            onClick={() => changeReaderSection('read')}
          >
            <span className="lectio-section-key">1</span>Read
          </button>
          {canShowCharacterIndex && (
            <button
              type="button"
              role="tab"
              aria-selected={readerSection === 'character-index'}
              className={readerSection === 'character-index' ? 'active' : ''}
              onClick={() => changeReaderSection('character-index')}
            >
              <span className="lectio-section-key">2</span>Character index
            </button>
          )}
          <button
            type="button"
            role="tab"
            aria-selected={readerSection === 'compare'}
            className={readerSection === 'compare' ? 'active' : ''}
            onClick={() => changeReaderSection('compare')}
          >
            <span className="lectio-section-key">3</span>Compare witnesses
          </button>
        </div>
        <div className="lectio-reader-source-scope">
          <span>Immutable source</span>
          <b>{sourceLabel}{contextLabel ? ` · ${contextLabel}` : ''}</b>
        </div>
      </nav>

      {readerSection === 'compare' && <div className="cte-strip" data-testid="ledger-tradition-context-strip">
        <div className="cte-strip-left">
          <span className="cte-pill">Source · {sourceLabel}</span>
          <span className="cte-strip-against">against</span>
          {targets.slice(0, 5).map(target => {
            const attestsCurrentUnit = selectedPeerWitnessIds.has(target.id)
            return (
              <span
                key={target.id}
                className={`cte-pill target ${attestsCurrentUnit ? 'attests' : 'silent-here'}`}
                title={`${target.sigla} ${attestsCurrentUnit ? 'attests this unit' : 'is silent for this unit'}`}
              >
              {target.sigla}
              </span>
            )
          })}
          {targets.length > 5 && <span className="cte-muted">+{targets.length - 5}</span>}
        </div>
        <div className="cte-progress">
          <span><b>{selectedUnit ? selectedUnit.index + 1 : 0}</b> / <b>{model.units.length}</b> units</span>
          <span className="cte-progress-bar">
            <i style={{ width: `${model.units.length && selectedUnit ? ((selectedUnit.index + 1) / model.units.length) * 100 : 0}%` }} />
          </span>
          <span><b>{selectedUnit?.peerCount || 0}</b> related units</span>
          <button type="button" className="secondary" onClick={onClose}>Ledger table</button>
        </div>
      </div>}

      {readerSection === 'compare' && (
      <main className="cte-frame ledger-tradition-frame">
        <aside data-testid="ledger-tradition-apparatus" className={`cte-marginalia ${focusLevel === 'apparatus' ? 'l2' : 'l1'} ledger-tradition-apparatus`}>
          <div className="cte-mghead">
            Apparatus · {selectedUnit?.rowLabel || 'no unit'} · vs {sourceLabel}
          </div>
          <div className="cte-rail-tabs" role="tablist" aria-label="Reader apparatus view">
            <button
              type="button"
              role="tab"
              aria-selected="true"
              className="active"
            >
              Mapped rows
            </button>
          </div>

          <>
              {selectedUnit && (
                <article className="cte-card ledger-tradition-source-card">
                  <div className="cte-card-head">
                    <span>
                      {sourceLabel}
                      <small>
                        {selectedUnit.unitLabel ? `Witness unit ${selectedUnit.unitLabel}` : 'Witness unit unsequenced'}
                      </small>
                    </span>
                    <span className="cte-tier">source</span>
                  </div>
                  <div className="cte-card-score">
                    <span>Ledger projection</span>
                    <span className="cte-score-bar"><i style={{ width: '100%' }} /></span>
                    <span>{selectedUnit.rowLabel}</span>
                  </div>
                  <div className="cte-card-snippet" dir={hasArabicText(selectedUnit.text) ? 'rtl' : 'auto'}>
                    {selectedUnit.text}
                  </div>
                </article>
              )}
              {selectedPeers.length === 0 ? (
                <article className="cte-card silent">
                  <div className="cte-card-head">
                    <span>No related unit<small>for this collation row</small></span>
                    <span className="cte-tier">Silent</span>
                  </div>
                </article>
              ) : selectedPeers.map((peer, index) => (
                <PeerCard
                  key={`${peer.rowId}:${peer.witnessId}:${peer.unitLabel || index}`}
                  peer={peer}
                  active={focusLevel === 'apparatus' && selectedPeer?.witnessId === peer.witnessId}
                  cardRef={element => {
                    if (element) {
                      peerRefs.current.set(index, element)
                    } else {
                      peerRefs.current.delete(index)
                    }
                  }}
                  onClick={() => {
                    setSelectedPeerIndex(index)
                    setFocusLevel('apparatus')
                  }}
                />
              ))}
          </>
        </aside>

        <section
          className="cte-reader"
          tabIndex={0}
          aria-label="Variorum continuous witness text"
          data-selected-character={selectedCharacterId ? 'true' : undefined}
        >
          {model.units.length === 0 ? (
            <div className="cte-empty-reader">
              <div className="cte-empty-title">No collation units available</div>
              <p>Select a witness with mapped Variorum rows to enter The Tradition reader.</p>
            </div>
          ) : (
            <div className="cte-text-flow">
              {model.units.map(unit => (
                <ReaderUnit
                  key={unit.rowId}
                  unit={unit}
                  focused={unit.rowId === selectedUnit?.rowId}
                  characterAnchors={EMPTY_READER_CHARACTER_ANCHORS}
                  layerAnnotations={EMPTY_READER_LAYER_ANNOTATIONS}
                  focusedLayerNoteId={focusedLayerNoteId}
                  focusedOccurrenceId={focusedCharacterOccurrenceId}
                  selectedCharacterId={selectedCharacterId}
                  showMappedRowHighlights={showMappedRowHighlights}
                  variationTier={unitVariationTierByRowId.get(unit.rowId)}
                  selectFocusLevel="apparatus"
                  onFocus={selectUnit}
                  onSelect={selectUnit}
                  onSelectCharacter={selectCharacterInReader}
                  onSelectCharacterOccurrence={selectCharacterInReader}
                  onSelectLayerAnnotation={selectLayerAnnotationInReader}
                  register={registerReaderUnit}
                />
              ))}
            </div>
          )}
        </section>

        <aside className="cte-pulse-rail" aria-label="Collation unit pulse rail">
          <div className="cte-pulse-legend">Units</div>
          {model.units.map(unit => {
            const unitVariationTier = unitVariationTierByRowId.get(unit.rowId) || 'silent-run'
            return (
              <button
                key={unit.rowId}
                type="button"
                className={[
                  'cte-prow',
                  unit.rowId === selectedUnit?.rowId ? 'here' : '',
                ].filter(Boolean).join(' ')}
                title={`${unit.rowLabel} · ${getUnitVariationLabel(unitVariationTier)}`}
                onClick={() => selectUnit(unit)}
              >
                <span className={`cte-cell ${unitVariationTier}`} />
              </button>
            )
          })}
        </aside>
      </main>
      )}

      {readerSection === 'read' && (
        <main className="cte-frame ledger-tradition-frame lectio-reading-frame">
          {renderReaderText('lectio-plain-reader')}
        </main>
      )}

      {readerSection === 'character-index' && (
        <main data-testid="ledger-reader-character-index" className="lectio-character-layout">
          {showTextualBeings
            ? textualBeings
            : evidenceProjection === 'full-text'
              ? renderReaderText('lectio-character-reader')
              : evidenceOnlyReader}
          {characterMargin}
        </main>
      )}

      {evidenceContextMenu}
      {researchNoteEditor && (
        <ReaderResearchNoteEditor
          state={researchNoteEditor}
          notes={getAnnotationResearchNotesForTarget(annotationResearchNotes, researchNoteEditor.target)}
          workLabel={contextLabel}
          witnessLabel={sourceLabel}
          savingDisabled={!onSaveAnnotationResearchNote}
          onSave={onSaveAnnotationResearchNote}
          onArchive={onArchiveAnnotationResearchNote}
          onClose={() => setResearchNoteEditor(null)}
        />
      )}

      <footer className="lectio-reader-console" data-testid="lectio-reader-console">
        <div className="lectio-console-hints">
          <span><kbd>↑↓</kbd>{focusLevel === 'apparatus' ? 'Cards' : readerSection === 'character-index' ? 'evidence' : 'Walk'}</span>
          {readerSection !== 'read' && (
            <span><kbd>⏎</kbd>{readerSection === 'character-index' ? 'apparatus' : apparatusViewLabel}</span>
          )}
          {readerSection === 'character-index' && <span><kbd>L</kbd>lections</span>}
        </div>
        <div className="lectio-console-live" aria-live="polite">
          {readerSection === 'character-index' && adjudicationNotice
            ? <span className="lectio-adjudication-notice"><b>{adjudicationNotice}</b></span>
            : readerSection === 'character-index'
            ? <>Character index · <b>{sourceLabel}</b>{activeCharacterLabel ? ` · ${activeCharacterLabel}` : ''}</>
            : readerSection === 'read'
              ? <>Plain reading · <b>{sourceLabel}</b></>
              : selectedUnit
                ? `${selectedUnit.rowLabel} · ${selectedUnit.peerCount} related unit${selectedUnit.peerCount === 1 ? '' : 's'}`
                : 'No unit selected'}
        </div>
        <div className="lectio-console-hints">
          {readerSection === 'character-index' && <span><kbd>A</kbd>adjudicate</span>}
          {readerSection === 'character-index' && <span><kbd>G</kbd>gallery</span>}
          <span><kbd>ESC</kbd>step back</span>
        </div>
        <div className="lectio-console-legend" aria-label="Reader legend">
          {readerSection === 'compare' ? (
            <>
              <span className="cte-legend-item"><i className="cte-legend-line t2" />Divergent</span>
              <span className="cte-legend-item"><i className="cte-legend-line t1" />Near</span>
              <span className="cte-legend-item"><i className="cte-legend-line t3" />Identical</span>
            </>
          ) : readerSection === 'character-index' ? (
            !showTextualBeings && evidenceProjection === 'full-text' ? (
              <>
                <span className="cte-legend-item"><b>{activeAnnotationGrammar.label}</b> · {activeAnnotationGrammar.note}</span>
                <span className="cte-legend-item">
                  Name = character ink + overline · Coref = character ink alone · Speaker = character guillemets ·
                  Focalization = vermilion · Characterization = sienna
                </span>
              </>
            ) : (
              <span className="cte-legend-item">Analytic projection · continuous-text notation withdrawn</span>
            )
          ) : (
            <span className="cte-legend-item">Immutable source · annotation and comparison marks withdrawn</span>
          )}
        </div>
      </footer>
    </section>
  )
}
